import ResponsiveContainer from "../components/DensityResponsiveContainer";
import { DashboardContent } from "../components/DashboardLoader";
import DashboardLoader from "../components/DashboardLoader";
import useMarketViewLayout from "../hooks/useMarketViewLayout";
import CsvExportButton from "../components/CsvExportButton";
import { layoutRect, layoutCoordinate, viewportClientWidth, viewportHeight as layoutViewportHeight, viewportScrollY, viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
/* eslint-disable */
import { memo, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { cachedHistoryGames, hydrateSnapshot, loadSnapshot, loadHistory, loadGameHistories, loadFavoriteHistory, snapshotLatestPeriod, usesPerGameHistory, needsMindshareHistory, snapshotMetadata, snapshotFilterOptions, snapshotReleaseDateRange } from "../services/mindshareData";
import { recordMindshareTiming, timeMindshare } from "../services/mindsharePerformance";
import { MARKET_PALETTE, MARKET_FAVORITES, matchesMarketFavorite, marketGrowthColor, marketTotalColor, marketTotalRankColor, marketLabelColor, marketLabelStroke } from "../services/mindshareMarket";
import "../components/marketMind.css";
import "../components/gameArtCards.css";
import { fitMarketLabel, settleMarketSquares, squareMarketLabel } from "../services/marketSquareLayout";
import { mindshareGameLabelStyle, selectMindshareTooltipPoint } from "../services/mindshareChart";
import { isMindshareEditorial, MGFilterControl, MGHeadingToggle, MGIconButton, MGRankBadge, MGSectionHeader, MGTechFrame } from "../components/editorial/MGEditorial";
import {
  Area,
  Line,
  ComposedChart,
  CartesianGrid,
  ReferenceDot,

  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as d3 from "d3";

// UI pieces reused from TikTok.jsx
import DateSlider from "../components/DateSlider";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import TextInputField from "../components/Fields/TextInputField";
import SavedViewsManager from "../components/SavedViewsManager";
import MindshareHeaderControls from "../components/MindshareHeaderControls";
import { HISTORY_URL as GAME_STATS_URL, MOMENTUM_URL as MINDSHARE_MOMENTUM_URL, BUSINESS_ROLLUPS_BASE_URL } from "../config/mindshareSources";
import PublisherAtlas from "../components/PublisherAtlas";
import MindshareTopGrowth from "../components/MindshareTopGrowth";
import { SkeletonMarketViewBubbleChart } from "../components/skeletons/SkeletonMarketViewBubbleChart";
import { ArrowLeft, ArrowRight, Download, Grip, Lock, Maximize2, RotateCcw, X as CloseIcon, Search as SearchIcon, CalendarDays, Ruler, Image as ImageIcon, GripHorizontal } from "lucide-react";


// TikTok metadata context (ImageURL, Publisher, Genre, Release_Date, etc.)
import { useTikTokData } from "../contexts/TikTokDataContext";

/* ---------------- Data ---------------- */
const BUSINESS_DIMENSIONS = [
  { value: "publishers", label: "Publishers" },
  { value: "developers", label: "Developers" },
  { value: "genres", label: "Genres" },
];

const BUSINESS_WINDOWS = [
  { value: "52w", label: "52 Weeks" },
];

const MARKET_PERIOD_OPTIONS = [
  { value: "1w", label: "Past Week", days: 7 },
  { value: "1m", label: "Past 1 Month", days: 30 },
  { value: "3m", label: "Past 3 Months", days: 90 },
  { value: "6m", label: "Past 6 Months", days: 180 },
  { value: "1y", label: "Past Year", days: 365 },
  { value: "all", label: "All Periods", days: null },
];

const GOOGLE_ICON = `${process.env.PUBLIC_URL}/Google Icon.png`;
const YT_ICON = `${process.env.PUBLIC_URL}/YouTube Icon.png`;
const TWITCH_ICON = `${process.env.PUBLIC_URL}/Twitch Icon.png`;
const TIKTOK_ICON = `${process.env.PUBLIC_URL}/TikTok Logo.png`;
const MINDGAME_TREE_ICON = `${process.env.PUBLIC_URL}/redesign/logos/icon/mindGAME Tree Logo.png`;
const FILTER_BUTTON_ICON = `${process.env.PUBLIC_URL}/Filter Button.png`;

async function fetchGzipText(url) {
  const res = await fetch(url, { mode: "cors", cache: "no-store" });
  if (!res.ok) throw new Error(`${url} HTTP ${res.status}`);
  const buf = await res.arrayBuffer();
  const u8 = new Uint8Array(buf);
  const looksGzip = u8.length >= 2 && u8[0] === 0x1f && u8[1] === 0x8b;
  if (looksGzip && "DecompressionStream" in window) {
    const ds = new DecompressionStream("gzip");
    const stream = new Response(u8).body.pipeThrough(ds);
    return (await new Response(stream).text()).trim();
  }
  return new TextDecoder("utf-8").decode(u8).trim();
}

async function fetchGzipJson(url) {
  const txt = await fetchGzipText(url);
  return JSON.parse(txt);
}

const businessSlugify = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’'`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "series";

const getBusinessSeriesName = (entry) =>
  typeof entry === "string" ? entry : entry?.name || entry?.label || entry?.series || "";

const getBusinessSeriesSlug = (entry) => {
  if (!entry) return "";
  if (typeof entry === "string") return businessSlugify(entry);
  return entry.slug || businessSlugify(entry.name || entry.label || entry.series || "");
};

const pickBusinessValue = (row, name) => {
  if (!row) return null;
  const candidates = [
    row[name],
    row.share,
    row.mindshare,
    row.mindSHARE,
    row.value,
    row.level,
    row.latest_share,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/* -------------- helpers -------------- */
const norm = (s = "") =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[’'`]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const pct3 = (v) => `${(Number(v) * 100).toFixed(3)}%`;
// Use 4 decimals for very small mindSHARE values (under 0.100%) so they don't all round to the same 3-decimal figure.
const pctMindshare = (v) => {
  const n = Number(v) * 100;
  const decimals = n > 0 && n < 0.1 ? 4 : 3;
  return `${n.toFixed(decimals)}%`;
};const fmtMDY = (v) => {
  const d = new Date(v);
  if (isNaN(d)) return String(v);
  const m = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${m}/${day}/${yy}`;
};

// ORIGINAL Trended line-chart palette (Steam-style)
const chartSeriesColors = [
  "#20a5bb", // mindGAME teal
  "#F5E6C8", // cream
  "#ff9b70", // warm peach
  "#33c3eb", // bright cyan-blue
  "#f1726a", // coral
  "#66CCFF", // soft blue
  "#A855F7", // purple
  "#FF5342", // red-orange
];

const chartSeriesColor = (i) =>
  chartSeriesColors[i % chartSeriesColors.length];

const toDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v) ? null : v;
  const d = new Date(v);
  return isNaN(d) ? null : d;
};
const coerceRangeToDates = (range) => {
  if (!Array.isArray(range) || range.length !== 2) return [null, null];
  return [toDate(range[0]), toDate(range[1])];
};

const filterMindshareGames = (games, filters, isAllReleaseDateRange) => {
  const {
    selectedTags, selectedPublishers, selectedGenres, selectedDevelopers,
    selectedLiveService, selectedPricing, selectedUGC, selectedFocus,
    selectedPlatforms, selectedDateRange, onlyUnreleased,
  } = filters;
  const [r0, r1] = coerceRangeToDates(selectedDateRange);
  const hasRange = Boolean(r0 && r1 && !isAllReleaseDateRange);
  let result = games
    .filter(g => !selectedTags.length || selectedTags.some(t => g.tags.includes(t.value)))
    .filter(g => !selectedPublishers.length || selectedPublishers.some(p => g.publisher === p.value))
    .filter(g => !selectedGenres.length || selectedGenres.some(genre => g.genres?.includes(genre.value)))
    .filter(g => !selectedDevelopers.length || selectedDevelopers.some(developer => g.developer === developer.value))
    .filter(g => !selectedLiveService || g.live_service_static === selectedLiveService.value)
    .filter(g => !selectedPricing || g.pricing === selectedPricing.value)
    .filter(g => !selectedUGC || g.user_generated_content === selectedUGC.value)
    .filter(g => {
      if (!selectedFocus.length) return true;
      const focusValues = String(g.single_multiplayer_focus || "").split(",").map(value => value.trim()).filter(Boolean);
      return selectedFocus.some(focus => focusValues.includes(focus.value));
    })
    .filter(g => {
      if (!selectedPlatforms.length) return true;
      const platforms = Array.isArray(g.platforms) ? g.platforms : [];
      return selectedPlatforms.some(platform => platforms.includes(platform.value));
    })
    .filter(g => {
      if (!hasRange) return true;
      if (!g.release_date) return onlyUnreleased;
      const releaseDate = toDate(g.release_date);
      if (!releaseDate) return onlyUnreleased;
      const timestamp = releaseDate.getTime();
      return timestamp >= r0.getTime() && timestamp <= r1.getTime();
    });
  if (onlyUnreleased) result = result.filter(g => g.isUnreleased);
  return result.sort((a, b) => (b.views || 0) - (a.views || 0));
};

const launchScoreForGame = game => {
  const precomputed = Number(game?.launch_score);
  if (Number.isFinite(precomputed) && precomputed !== 0) return precomputed;
  let sum = 0;
  for (const key of game?._w2lKeys || []) {
    const w2l = Number(key);
    if (!Number.isFinite(w2l) || w2l < -30 || w2l > 0) continue;
    const value = Number(game._byW2L?.get(key));
    if (Number.isFinite(value) && value > 0) sum += value;
  }
  return sum || Number(game?.views || 0);
};

const compareLaunchGames = (a, b) =>
  launchScoreForGame(b) - launchScoreForGame(a) ||
  String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });

/** ========= Shared bubble helpers (inlined from hooks/components) ========= **/

// 1) useBubblePack – packs nodes for Market Mind
function useBubblePack({ filtered, dims, colorScale, colorMode = "growth" }) {
  return useMemo(() => {
    if (!filtered?.length || dims.w <= 0 || dims.h <= 0) return [];

    const children = filtered
      .map((g) => {
        const ms = Number.isFinite(g.mindshare)
          ? g.mindshare
          : Number(g.views ?? 0);

        if (!Number.isFinite(ms) || ms <= 0) return null;

        const latestKey = g._dateKeys?.[g._dateKeys.length - 1];
        const latestMeta = g._metaByDate?.get?.(latestKey) || {};

        const growth = Number.isFinite(g.growthPT)
          ? g.growthPT
          : Number.isFinite(g.percentGrowth)
          ? g.percentGrowth / 100
          : 0;

                return {
          ...g,
          id: g.id,
          name: g.name,
          value: ms,
          ms,
          delta: Number.isFinite(g.delta) ? g.delta : null,
          growth,
          color: colorScale(colorMode === "total" ? ms : growth),
          rank_mindshare: g.rank_mindshare ?? null,
          rank_search: g.rank_search ?? g.search_rank ?? latestMeta.searchRank ?? null,
          rank_video: g.rank_video ?? g.video_rank ?? latestMeta.videoRank ?? null,
          rank_twitch: g.rank_twitch ?? g.twitch_rank ?? latestMeta.twitchRank ?? null,
          search_volume: g.search_volume ?? latestMeta.search ?? null,
          publisher: g.publisher,
          developer: g.developer,
          tags: g.tags,
          genres: g.genres,
          live_service_static: g.live_service_static,
          pricing: g.pricing,
          user_generated_content: g.user_generated_content,
          single_multiplayer_focus: g.single_multiplayer_focus,
          platforms: g.platforms,
          description: g.description,
          release_date: g.release_date,
          image: g.imageUrl || g.image,
          video: g.steamVideo || g.igdbVideo || "",
          liveCcu: g.liveCcu ?? null,
          steamAppId: g.steamAppId ?? null,
          latestDate: g.latestDate ?? latestKey ?? null,
          prevDate: g.prevDate ?? null,
        };
      })
      .filter(Boolean);

    const root = d3.hierarchy({ children }).sum((d) => d.value);
    d3.pack().size([dims.w, dims.h - 60]).padding(1.5)(root);
    return root.leaves();
  }, [filtered, dims.w, dims.h, colorScale, colorMode]);
}

// 2) BubbleSkeleton – loading placeholder
const BubbleSkeleton = ({ width = 1200, height = 720, count = 28 }) => {
  const nodes = [];
  const cols = Math.max(4, Math.floor(width / 220));
  const rows = Math.ceil(count / cols);
  const gx = width / (cols + 1);
  const gy = height / (rows + 1);
  let k = 0;
  for (let r = 1; r <= rows; r++) {
    for (let c = 1; c <= cols; c++) {
      if (k++ >= count) break;
      const x = gx * c + (Math.random() - 0.5) * gx * 0.3;
      const y = gy * r + (Math.random() - 0.5) * gy * 0.3;
      const rad = Math.max(
        14,
        Math.min(72, (gx + gy) * 0.18 * (0.6 + Math.random() * 0.8))
      );
      nodes.push({ x, y, r: rad, a: 0.35 + Math.random() * 0.25 });
    }
  }
  return (
    <svg className="ms-bubble-skeleton" width={width} height={height}>
      {nodes.map((n, i) => (
        <circle
          key={i}
          cx={n.x}
          cy={n.y}
          r={n.r}
          fill="#1d2730"
          opacity={n.a}
        />
      ))}
    </svg>
  );
};

function GameDetailsTooltip({ box, showImage = true, cardHeight }) {
  const descriptionRef = useRef(null);
  const descriptionDrag = useRef(null);
  const [descriptionLines, setDescriptionLines] = useState(3);
  const [fullDescriptionLines, setFullDescriptionLines] = useState(3);
  useLayoutEffect(() => {
    setDescriptionLines(3);
    descriptionDrag.current = null;
    const element = descriptionRef.current;
    if (!element) {
      setFullDescriptionLines(3);
      return;
    }
    const measure = () => {
      const clone = element.cloneNode(true);
      Object.assign(clone.style, { position: "fixed", left: "-10000px", top: "0", width: `${layoutRect(element).width}px`, display: "block", webkitLineClamp: "unset", visibility: "hidden", height: "auto", maxHeight: "none" });
      document.body.appendChild(clone);
      const lines = Math.ceil(layoutRect(clone).height / 16);
      clone.remove();
      setFullDescriptionLines(Math.max(3, lines));
    };
    measure();
    let frame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });
    observer.observe(element);
    return () => { observer.disconnect(); cancelAnimationFrame(frame); };
  }, [box?.name, box?.description]);
  if (!box) return null;
  const image = box.imageUrl || box.image;
  const numberOrNull = (value) =>
    value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);
  const rankCards = [
    { label: "mindSHARE", rank: box.rank_mindshare, icon: MINDGAME_TREE_ICON, alt: "mindGAME" },
    { label: "Search", rank: box.search_rank ?? box.rank_search, icon: GOOGLE_ICON, alt: "Google Search" },
    { label: "Video", rank: box.video_rank ?? box.rank_video, icon: YT_ICON, alt: "YouTube" },
    { label: "Streaming", rank: box.twitch_rank ?? box.rank_twitch, icon: TWITCH_ICON, alt: "Twitch" },
  ];
  const formatRank = (row) => {
    const rank = numberOrNull(row.rank);
    const maxStreamingRank = numberOrNull(box.maxStreamingRank);
    return rank == null || rank <= 0 ||
      (row.label === "Streaming" && maxStreamingRank != null && rank >= maxStreamingRank)
      ? "—" : `#${rank}`;
  };
  const value = numberOrNull(box.value ?? box.mindshare ?? box.ms ?? box.views);
  const delta = numberOrNull(box.delta);
  const searchVolume = numberOrNull(box.search_volume);
  const listText = (value) => Array.isArray(value) ? value.join(", ") : value;
  const metadata = [
    ["Release", box.release_date ? fmtMDY(box.release_date) : null],
    ["Publisher", box.publisher],
    ["Developer", box.developer],
    ["Genres", listText(box.genres)],
    ["Tags", listText(box.tags)],
    ["Model", box.live_service_static],
    ["Pricing", box.pricing],
    ["UGC", box.user_generated_content],
    ["Play Type", box.single_multiplayer_focus],
    ["Platforms", listText(box.platforms)],
    ["Search Volume", searchVolume == null ? null : Math.round(searchVolume).toLocaleString()],
  ].filter(([, detail]) => detail != null && detail !== "");

  return (
    <div
      data-game-details-tooltip
      style={{
        position: "relative",
        background: "linear-gradient(180deg, rgba(18,18,18,0.98), rgba(8,8,8,0.98))",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "#fff",
        padding: 12,
        borderRadius: 2,
        width: cardHeight ? "100%" : "min(620px, calc(calc(var(--mg-vw) * 100) - 16px))",
        minHeight: cardHeight,
        maxWidth: "100%",
        boxSizing: "border-box",
        overflow: "visible",
        overflowWrap: "anywhere",
        pointerEvents: "auto",
        boxShadow: "0 22px 60px rgba(0,0,0,0.72), 0 0 0 1px rgba(32,165,187,0.08)",
      }}
    >
      <style>{`
        .mg-ms-tooltip-metadata { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 16px; }
        @container mg-viewport (max-width: 480px) {
          .mg-ms-tooltip-metadata { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
      {image && (
        <>
          <div aria-hidden style={{ position: "absolute", inset: 0, borderRadius: "inherit", backgroundImage: `url(${image})`, backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat", filter: "saturate(1.05) brightness(0.48)", zIndex: 0, pointerEvents: "none" }} />
          <div aria-hidden style={{ position: "absolute", inset: 0, borderRadius: "inherit", background: "linear-gradient(90deg, rgba(2,2,3,0.86) 0%, rgba(2,2,3,0.70) 46%, rgba(2,2,3,0.88) 100%), radial-gradient(680px 360px at 74% 18%, rgba(32,165,187,0.12), transparent 60%)", zIndex: 0, pointerEvents: "none" }} />
        </>
      )}
      <div style={{ position: "relative", zIndex: 1, margin: "-12px -12px 8px -12px", padding: "14px", borderBottom: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.14)" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          {showImage && (image ? (
            <img src={image} alt={box.name} style={{ width: 60, height: 82, objectFit: "cover", borderRadius: 2, flex: "0 0 auto", boxShadow: "0 14px 34px rgba(0,0,0,0.62)", border: "1px solid rgba(255,255,255,0.18)" }} />
          ) : (
            <div style={{ width: 60, height: 82, background: "linear-gradient(135deg, #202020, #090909)", borderRadius: 2, flex: "0 0 auto", border: "1px solid rgba(255,255,255,0.12)" }} />
          ))}
          <div style={{ minWidth: 0, flex: "1 1 0" }}>
            <div style={{ fontWeight: 950, fontSize: 18, textShadow: "0 2px 10px rgba(0,0,0,0.95)" }}>{box.name}</div>
            <div style={{ fontWeight: 950, marginTop: 3, fontSize: 17, textShadow: "0 2px 8px rgba(0,0,0,0.95)" }}>
              {value == null ? "—" : pct3(value)}{" "}
              {delta != null && <span style={{ fontSize: 12, color: "#9fb7c9" }}>· Δ {pct3(delta)}</span>}
            </div>
            <div style={{ fontSize: 12, color: "#cdd3db", marginTop: 2, textDecoration: "underline" }}>
              {box.xLabel ?? (box.latestDate ? fmtMDY(box.latestDate) : null)}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8, margin: "10px 0 0 0" }}>
              {rankCards.map((row) => (
                <div key={row.label} title={`${row.alt} rank`} style={{ minWidth: 0, border: "1px solid rgba(255,255,255,0.12)", borderRadius: 2, background: "rgba(0,0,0,0.42)", padding: "6px 3px", textAlign: "center", whiteSpace: "nowrap" }}>
                  <img src={row.icon} alt={row.alt} title={row.alt} style={{ width: 16, height: 16, objectFit: "contain" }} />
                  <div style={{ color: "#fff", fontSize: 14, lineHeight: "20px", fontWeight: 950 }}>{formatRank(row)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{ position: "relative", zIndex: 1, marginTop: 4 }}>
        {box.description && (
          <div style={{ marginBottom: 10 }}>
            <div ref={descriptionRef} style={{ fontSize: 12, color: "#d0d0d0", lineHeight: "16px", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: descriptionLines, overflow: "hidden" }}>{box.description}</div>
            {fullDescriptionLines > 3 && (
              <button
                type="button"
                aria-label={descriptionLines > 3 ? "Collapse description" : "Expand description"}
                aria-expanded={descriptionLines > 3}
                title={descriptionLines > 3 ? "Collapse description" : "Expand description"}
                onPointerDown={(event) => {
                  if (event.button !== 0) return;
                  descriptionDrag.current = { startY: layoutCoordinate(event.clientY), lines: descriptionLines, moved: false };
                  event.currentTarget.setPointerCapture(event.pointerId);
                }}
                onPointerMove={(event) => {
                  const drag = descriptionDrag.current;
                  if (!drag || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
                  const distance = layoutCoordinate(event.clientY) - drag.startY;
                  if (Math.abs(distance) > 4) drag.moved = true;
                  if (drag.moved) setDescriptionLines(Math.min(fullDescriptionLines, Math.max(3, drag.lines + Math.round(distance / 16))));
                }}
                onPointerUp={(event) => {
                  if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
                }}
                onPointerCancel={() => { descriptionDrag.current = null; }}
                onClick={(event) => {
                  if (event.detail === 0 || !descriptionDrag.current?.moved) setDescriptionLines(descriptionLines > 3 ? 3 : fullDescriptionLines);
                  descriptionDrag.current = null;
                }}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: 22, padding: 0, border: 0, background: "transparent", color: "#9ca8b5", cursor: "ns-resize", touchAction: "none" }}
              ><GripHorizontal size={18} /></button>
            )}
          </div>
        )}
        <div className="mg-ms-tooltip-metadata">
          {metadata.map(([label, detail]) => (
            <div key={label} style={{ gridColumn: cardHeight && label === "Tags" ? "1 / -1" : undefined, minWidth: 0, fontSize: 12, color: "#aaa", lineHeight: "16px" }}>{label}: <b style={{ color: "#ddd" }}>{detail}</b></div>
          ))}
        </div>
      </div>
    </div>
  );
}

// 3) BubbleMiniTooltip – hover card styled like the chart-point tooltip
// (background art, name, big stat number, icon rank cards).
function BubbleMiniTooltip({ node, dims, zoomT, maxRankByMetric, onClose }) {
  const tooltipRef = useRef(null);
  const [tooltipHeight, setTooltipHeight] = useState(0);
  const [viewport, setViewport] = useState(() => ({ width: layoutViewportWidth(), height: layoutViewportHeight() }));
  useLayoutEffect(() => {
    const element = tooltipRef.current;
    if (!element) return;
    const measure = () => setTooltipHeight(layoutRect(element).height);
    const resize = () => setViewport({ width: layoutViewportWidth(), height: layoutViewportHeight() });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    window.addEventListener("resize", resize);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [node]);
  if (!node) return null;
  const d = node.data;
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const viewportWidth = viewport.width;
  const viewportHeight = viewport.height;
  const container = dims?.bounds;
  const originX = container?.left ?? 0;
  const originY = container?.top ?? 0;
  const width = Math.max(0, Math.min(620, viewportWidth - 16));
  const x = (zoomT?.applyX?.(node.x) ?? node.x) + (node.r || 40) + 14;
  const y = (zoomT?.applyY?.(node.y) ?? node.y) - (node.r || 40);
  const box = {
    ...d,
    value: d.mindshare ?? d.ms ?? d.value ?? d.views,
    image: d.imageUrl || d.image,
    search_rank: d.search_rank ?? d.rank_search,
    video_rank: d.video_rank ?? d.rank_video,
    twitch_rank: d.twitch_rank ?? d.rank_twitch,
    maxStreamingRank: maxRankByMetric?.streaming,
  };
  return createPortal(
    <div
      ref={tooltipRef}
      className="mg-market-mini-hover"
      onMouseLeave={onClose}
      style={{
        position: "fixed",
        left: clamp(originX + x, 8, Math.max(8, viewportWidth - width - 8)),
        top: clamp(originY + y, 8, Math.max(8, viewportHeight - tooltipHeight - 8)),
        width,
        overflow: "visible",
        pointerEvents: "auto",
        zIndex: 5000,
        filter: "drop-shadow(0 16px 32px rgba(0,0,0,0.55))",
        borderRadius: 2,
      }}
    >
      <GameDetailsTooltip box={box} />
    </div>, document.body
  );
}

// 3.5) Local getVideoEmbed helper (was ../utils/getVideoEmbed)
function getVideoEmbed(raw) {
  if (!raw) return null;
  const url = String(raw).trim();

  // Direct video file
  if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(url)) {
    return { type: "file", src: url };
  }

  // YouTube
  const yt = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/
  );
  if (yt) {
    const id = yt[1];
    return {
      type: "youtube",
      src: `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&showinfo=0&loop=1&playlist=${id}`,
    };
  }

  return null;
}

// 4) MarketTooltip – full pop-out tooltip
function MarketTooltip({ node, dims, zoomT, onClose, periodLabel }) {
  if (!node) return null;
  const d = node.data;
  const tooltipRef = useRef(null);
  const [tooltipHeight, setTooltipHeight] = useState(300);

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  const rankCards = [
    { label: "mindSHARE", rank: d.rank_mindshare, icon: MINDGAME_TREE_ICON, alt: "mindGAME" },
    { label: "Search", rank: d.rank_search, icon: GOOGLE_ICON, alt: "Google Search" },
    { label: "Video", rank: d.rank_video, icon: YT_ICON, alt: "YouTube" },
    { label: "Streaming", rank: d.rank_twitch, icon: TWITCH_ICON, alt: "Twitch" },
  ];

  const formatFullRank = (rank) => {
    const n = Number(rank);
    return Number.isFinite(n) ? `#${n}` : "—";
  };

  useEffect(() => {
    if (tooltipRef.current) {
      setTooltipHeight(tooltipRef.current.offsetHeight);
    }
  }, [node]);

  const W = 520;
  const MARGIN = 20;
  const BUBBLE_OFFSET = 15;
  const xRaw =
    (zoomT?.applyX?.(node.x) ?? node.x) + (node.r || 40) + BUBBLE_OFFSET;
  const yRaw = (zoomT?.applyY?.(node.y) ?? node.y) - (node.r || 40);

  let left = xRaw;
  let top = yRaw;

  if (xRaw + W > dims.w - MARGIN) {
    left =
      (zoomT?.applyX?.(node.x) ?? node.x) -
      W -
      (node.r || 40) -
      BUBBLE_OFFSET;
  }

  left = clamp(left, MARGIN, dims.w - W - MARGIN);

  const availableBelow = dims.h - top - MARGIN;
  const availableAbove = top - MARGIN;

  if (tooltipHeight > availableBelow) {
    if (availableAbove >= tooltipHeight) {
      top =
        (zoomT?.applyY?.(node.y) ?? node.y) -
        tooltipHeight -
        (node.r || 20);
    } else {
      top = (dims.h - tooltipHeight) / 2;
    }
  } else {
    top = yRaw;
  }

  top = clamp(top, MARGIN, dims.h - tooltipHeight - MARGIN);

  return (
    <div
      ref={tooltipRef}
      className="ms-tooltip"
      style={{
        left,
        top,
        pointerEvents: "auto",
        zIndex: 5000,
        position: "absolute",
        background: "linear-gradient(180deg, rgba(18,18,18,0.98), rgba(8,8,8,0.98))",
        border: "1px solid rgba(255,255,255,0.14)",
        color: "#fff",
        padding: 12,
        borderRadius: 2,
        width: W,
        maxWidth: W,
        overflow: "hidden",
        boxShadow:
          "0 22px 60px rgba(0,0,0,0.72), 0 0 0 1px rgba(32,165,187,0.08)",
      }}
    >
      {onClose && (
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            background: "rgba(0,0,0,0.46)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#fff",
            cursor: "pointer",
            padding: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 5002,
            borderRadius: 2,
            boxShadow: "0 8px 20px rgba(0,0,0,0.50)",
          }}
        >
          <CloseIcon size={16} strokeWidth={2.2} />
        </button>
      )}

      {d.image && (
        <>
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `url(${d.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              filter: "blur(3px) saturate(1.05) brightness(0.48)",
              transform: "scale(1.05)",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(2,2,3,0.86) 0%, rgba(2,2,3,0.70) 46%, rgba(2,2,3,0.88) 100%), radial-gradient(680px 360px at 74% 18%, rgba(32,165,187,0.12), transparent 60%)",
              zIndex: 0,
              pointerEvents: "none",
            }}
          />
        </>
      )}

      {/* TOP SECTION: premium image-backed stat card */}
      <div
        style={{
          position: "relative",
          margin: "-12px -12px 8px -12px",
          padding: "16px 14px 14px 14px",
          minHeight: 142,
          overflow: "hidden",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.14)",
        }}
      >
         <div
          style={{
            position: "relative",
            display: "flex",
            gap: 14,
            alignItems: "center",
            zIndex: 1,
          }}
        >
          {d.image ? (
            <img
              src={d.image}
              alt={d.name}
              style={{
                width: 86,
                height: 116,
                objectFit: "cover",
                borderRadius: 2,
                flex: "0 0 auto",
                boxShadow: "0 14px 34px rgba(0,0,0,0.62)",
                border: "1px solid rgba(255,255,255,0.18)",
              }}
            />
          ) : (
            <div
              style={{
                width: 86,
                height: 116,
                background: "linear-gradient(135deg, #202020, #090909)",
                borderRadius: 2,
                flex: "0 0 auto",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
            />
          )}

          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontWeight: 950,
                fontSize: 18,
                letterSpacing: "-0.02em",
                textShadow: "0 2px 10px rgba(0,0,0,0.95)",
              }}
            >
              {d.name}
            </div>

            <div
              style={{
                fontWeight: 950,
                marginTop: 3,
                fontSize: 17,
                textShadow: "0 2px 8px rgba(0,0,0,0.95)",
              }}
            >
              {(d.ms * 100).toFixed(3)}%
              {Number.isFinite(d.delta) && (
                <span style={{ fontSize: 12, color: "#9fb7c9", marginLeft: 6 }}>
                  · Δ {(d.delta * 100).toFixed(3)} pp
                </span>
              )}
              {Number.isFinite(d.growth) && (
                <span style={{ fontSize: 12, color: "#cdd3db", marginLeft: 6 }}>
                  · Growth <b>{(d.growth * 100).toFixed(3)}%</b>
                </span>
              )}
            </div>

            {(periodLabel || d.latestDate) && (
              <div
                style={{
                  fontSize: 12,
                  color: "#9fb7c9",
                  marginTop: 2,
                  fontWeight: 800,
                  letterSpacing: "0.02em",
                  textDecoration: "none",
                }}
              >
                {periodLabel || fmtMDY(String(d.latestDate))}
              </div>
            )}


            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 8,
                margin: "10px 0 0 0",
              }}
            >
              {rankCards.map((row) => (
                <div
                  key={row.label}
                  style={{
                    minWidth: 0,
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 2,
                    background: "rgba(0,0,0,0.42)",
                    padding: "7px 5px",
                    textAlign: "center",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.025)",
                    backdropFilter: "blur(4px)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                      color: "#cfd8e3",
                      fontSize: 10,
                      fontWeight: 900,
                      lineHeight: "13px",
                      marginBottom: 3,
                      whiteSpace: "nowrap",
                    }}
                  >
                    <img src={row.icon} alt={row.alt} style={{ width: 14, height: 14, objectFit: "contain" }} />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{row.label}</span>
                  </div>
                  <div style={{ color: "#fff", fontSize: 18, lineHeight: "21px", fontWeight: 950 }}>
                    {formatFullRank(row.rank)}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ height: 4 }} />
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION: full-width metadata */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          marginTop: 4,
          paddingTop: 6,
          fontSize: 11,
          background: "rgba(0,0,0,0.28)",
          borderRadius: 8,
          padding: "8px 9px",
        }}
      >

        {d.description && (
          <div style={{ fontSize: 12, color: "#d0d0d0", marginBottom: 5, lineHeight: "16px" }}>
            {d.description}
          </div>
        )}

        {d.release_date && (
          <div style={{ fontSize: 12, color: "#aaa", marginBottom: 8 }}>
            Release: <b>{fmtMDY(d.release_date)}</b>
          </div>
        )}

        {(d.publisher || d.developer) && (
          <div style={{ fontSize: 12, color: "#ccc", marginBottom: 4 }}>
            {d.publisher && <>Publisher: <b>{d.publisher}</b></>}
            {d.publisher && d.developer && " · "}
            {d.developer && <>Developer: <b>{d.developer}</b></>}
          </div>
        )}

        {(d.genres?.length || d.tags?.length) && (
          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>
            {d.genres?.length ? `Genres: ${d.genres.join(", ")}` : null}
            {d.genres?.length && d.tags?.length ? " · " : null}
            {d.tags?.length ? `Tags: ${d.tags.join(", ")}` : null}
          </div>
        )}

        {(d.live_service_static || d.pricing || d.user_generated_content) && (
          <div style={{ fontSize: 11, color: "#aaa", marginBottom: 4 }}>
            {d.live_service_static && <>Model: {d.live_service_static} · </>}
            {d.pricing && <>Pricing: {d.pricing} · </>}
            {d.user_generated_content && <>UGC: {d.user_generated_content}</>}
          </div>
        )}

        {d.platforms?.length > 0 && (
          <div style={{ fontSize: 11, color: "#aaa" }}>
            Platforms: {d.platforms.join(", ")}
          </div>
        )}
      </div>
    </div>
  );
}

/** ========= Market Mind – inline bubble view ========= **/

// dropdown → bubble match
function matchesMarketSearch(game, query) {
  const words = (value) => String(value || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().match(/[a-z0-9]+/g) || [];
  const terms = words(query);
  const fields = words([game.name, game.publisher, game.developer, ...(game.genres || []), ...(game.tags || []), ...(game.platforms || []), game.live_service_static, game.pricing, game.user_generated_content, game.single_multiplayer_focus].join(" "));
  return terms.every((term) => fields.some((word) => word.startsWith(term)));
}

function matchesPreviewBubble(d, preview) {
  if (Array.isArray(preview)) {
    return preview.length === 0 || preview.some((item) => matchesPreviewBubble(d, item));
  }
  if (!preview || !preview.type || !preview.value) return true;

  // preview.value may be an option object or a primitive
  const raw =
    typeof preview.value === "object" && preview.value !== null
      ? preview.value.value ?? preview.value.label ?? preview.value
      : preview.value;

  const norm = (s) =>
    String(s || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const v = norm(raw);

  switch (preview.type) {
    case "ids":
      return preview.value.has(d.id);
    case "favorite":
      return matchesMarketFavorite(d, raw);
    case "tag":
      return (d.tags || []).map(norm).includes(v);
    case "genre":
      return (d.genres || []).map(norm).includes(v);
    case "publisher":
      return norm(d.publisher) === v;
    case "developer":
      return norm(d.developer) === v;
    case "liveService":
      return norm(d.live_service_static) === v;
    case "pricing":
      return norm(d.pricing) === v;
    case "ugc":
      return norm(d.user_generated_content) === v;
    case "focus": {
      const focusVals = String(d.single_multiplayer_focus || "")
        .split(",")
        .map(norm)
        .filter(Boolean);
      return focusVals.includes(v);
    }
    case "platform":
      return (d.platforms || []).map(norm).includes(v);
    case "appliedFilters": {
      const f = preview.value || {};
      if (!matchesMarketSearch(d, f.searchTerm)) return false;
      const selectedTags = f.selectedTags || [];
      const selectedPublishers = f.selectedPublishers || [];
      const selectedGenres = f.selectedGenres || [];
      const selectedDevelopers = f.selectedDevelopers || [];
      const selectedFocus = f.selectedFocus || [];
      const selectedPlatforms = f.selectedPlatforms || [];
      const selectedLiveService = f.selectedLiveService || null;
      const selectedPricing = f.selectedPricing || null;
      const selectedUGC = f.selectedUGC || null;
      const selectedDateRange = f.selectedDateRange || [null, null];
      const onlyUnreleased = Boolean(f.onlyUnreleased);

      const val = (opt) => norm(opt?.value ?? opt?.label ?? opt ?? "");
      const includesAny = (arr, opts) => {
        if (!opts.length) return true;
        const set = new Set((arr || []).map(norm));
        return opts.some((o) => set.has(val(o)));
      };

      if (!includesAny(d.tags, selectedTags)) return false;
      if (!includesAny(d.genres, selectedGenres)) return false;
      if (selectedPublishers.length && !selectedPublishers.some((o) => norm(d.publisher) === val(o))) return false;
      if (selectedDevelopers.length && !selectedDevelopers.some((o) => norm(d.developer) === val(o))) return false;
      if (selectedLiveService && norm(d.live_service_static) !== val(selectedLiveService)) return false;
      if (selectedPricing && norm(d.pricing) !== val(selectedPricing)) return false;
      if (selectedUGC && norm(d.user_generated_content) !== val(selectedUGC)) return false;
      if (selectedFocus.length) {
        const focusVals = String(d.single_multiplayer_focus || "").split(",").map(norm).filter(Boolean);
        if (!selectedFocus.some((o) => focusVals.includes(val(o)))) return false;
      }
      if (!includesAny(d.platforms, selectedPlatforms)) return false;
      if (onlyUnreleased && !d.isUnreleased) return false;

      const [r0, r1] = selectedDateRange || [null, null];
      if (r0 && r1) {
        if (!d.release_date) return onlyUnreleased ? true : false;
        const rd = new Date(d.release_date);
        if (isNaN(rd)) return onlyUnreleased ? true : false;
        const t = rd.getTime();
        if (t < new Date(r0).getTime() || t > new Date(r1).getTime()) return false;
      }

      return true;
    }
    default:
      return true;
  }
}

// Bubble size uses mindSHARE; color uses growth.
const marketSurfaceStyle = {
  "--mm-text": MARKET_PALETTE.text, "--mm-canvas": MARKET_PALETTE.canvas,
  "--mm-panel": MARKET_PALETTE.panel, "--mm-raised": MARKET_PALETTE.raised,
};

function MarketGrowthKey({ metricLabel = "mindSHARE", colorMode = "growth" }) {
  if (colorMode === "total") return (
    <div className="mm-growth-key" aria-label="Total color key">
      <span><i style={{ "--key-color": MARKET_PALETTE.growthPeak }} />Highest total</span>
      <span><i style={{ "--key-color": MARKET_PALETTE.warmWhite }} />Mid-range</span>
      <span><i style={{ "--key-color": MARKET_PALETTE.declineWorst }} />Lowest total</span>
      <small>Size and color = {metricLabel}</small>
    </div>
  );
  return (
    <div className="mm-growth-key" aria-label="Growth color key">
      <span><i style={{ "--key-color": MARKET_PALETTE.growth }} />Growth</span>
      <span><i style={{ "--key-color": MARKET_PALETTE.stone }} />Near flat ±2%</span>
      <span><i style={{ "--key-color": MARKET_PALETTE.coral }} />Decline</span>
      <small>Size = {metricLabel} · color = growth</small>
    </div>
  );
}

export function MarketView({ filtered, preview, dimensions, loading, periodLabel, squareMode = false, packWidth = null, metricLabel = "mindSHARE", renderDetails, colorMode = "growth", colorMaximum, onSelect, activeId, asyncLayout = false, loaderLabel = "Market Mind" }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [zoomT, setZoomT] = useState(d3.zoomIdentity);
  const [hovered, setHovered] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [selected, setSelected] = useState(null);
  const colorScale = useMemo(() => {
    if (colorMode !== "total") return marketGrowthColor;
    // Rank-based so outliers don't compress the palette into the blue middle.
    const sorted = filtered
      .map(game => Number(game.mindshare) || 0)
      .filter(value => value > 0)
      .sort((a, b) => a - b);
    return value => marketTotalRankColor(value, sorted);
  }, [colorMode, filtered]);


  // pack layout (uses mindshare as value, colorScale for color)
  // packWidth lets a narrow panel keep the mark scale of a wider pack.
  const layoutW = packWidth || dimensions.w;
  const packDims = useMemo(() => ({ w: layoutW, h: dimensions.h }), [layoutW, dimensions.h]);
  const packRows = useMemo(() => asyncLayout ? [] : filtered, [asyncLayout, filtered]);
  const circleNodes = useBubblePack({
    filtered: packRows,
    dims: packDims,
    colorScale,
    colorMode,
  });
  const asyncResult = useMarketViewLayout({
    enabled: asyncLayout, filtered, width: dimensions.w, height: dimensions.h,
    packWidth: layoutW, squareMode, colorScale,
  });
  const syncNodes = useMemo(() => {
    if (asyncLayout) return [];
    if (squareMode) return settleMarketSquares(circleNodes, dimensions.w, dimensions.h, undefined, { packWidth: layoutW });
    if (layoutW <= dimensions.w) return circleNodes;
    try {
      return settleMarketSquares(circleNodes, dimensions.w, dimensions.h, undefined, { packWidth: layoutW, sideScale: 2 });
    } catch {
      return settleMarketSquares(circleNodes, dimensions.w, dimensions.h, undefined, { packWidth: layoutW });
    }
  }, [circleNodes, dimensions.w, dimensions.h, layoutW, squareMode, asyncLayout]);
  const nodes = asyncLayout ? asyncResult.nodes : syncNodes;
  const viewId = useId();
  const selectedNode = useMemo(() => selected
    ? nodes.find(node => node.data.id === selected.data.id) || null : null, [nodes, selected]);
  const hoveredNode = useMemo(() => hovered
    ? nodes.find(node => node.data.id === hovered.data.id) || null : null, [nodes, hovered]);

  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const animation = svg.animate([{ opacity: 0.35 }, { opacity: 1 }], {
      duration: 180,
      easing: "ease-out",
    });
    return () => animation.cancel();
  }, [nodes]);

  // zoom/pan
  const zoomSvgRef = useRef(null);
  useEffect(() => {
    if (!zoomSvgRef.current) return;
    const svg = d3.select(zoomSvgRef.current);

    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 10])
      .on("zoom", ({ transform }) => {
        requestAnimationFrame(() => setZoomT(transform));
        d3.select(zoomSvgRef.current).select(".mm-zoom-group").attr("transform", transform);
      });

    svg.call(zoom);
    return () => svg.on(".zoom", null);
  }, [nodes]);

  // close full tooltip when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (!selected) return;
      const el = document.querySelector(".ms-tooltip");
      if (el && el.contains(e.target)) return;
      setSelected(null);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [selected]);

  const isPreviewActive = Array.isArray(preview) ? preview.length > 0 : !!(preview && preview.type && preview.value);
  const highlightMatches = useMemo(
    () => new Set(nodes.filter(node => matchesPreviewBubble(node.data, preview))),
    [nodes, preview]
  );
  const selectedId = selectedNode?.data?.id ?? activeId ?? null;

  const maxRankByMetric = useMemo(() => {
    const max = { mindshare: null, search: null, video: null, streaming: null };
    for (const g of filtered || []) {
      const rows = [
        ["mindshare", g.rank_mindshare],
        ["search", g.rank_search],
        ["video", g.rank_video],
        ["streaming", g.rank_twitch],
      ];
      for (const [key, value] of rows) {
        const n = Number(value);
        if (!Number.isFinite(n)) continue;
        max[key] = max[key] == null ? n : Math.max(max[key], n);
      }
    }
    return max;
  }, [filtered]);

  const k = zoomT?.k || 1;

  if (asyncResult.pending) return <DashboardLoader label={loaderLabel} />;
  if (asyncResult.error) return <div role="alert" style={{ padding: 20, color: "#ff9b9b" }}>
    Market Mind could not load: {asyncResult.error}{" "}
    <button type="button" onClick={asyncResult.retry}>Retry Market Mind</button>
  </div>;
  if (!filtered.length) {
    return (
      <div ref={containerRef} style={{ height: dimensions.h, width: dimensions.w, position: "relative" }}>
        {loading ? <BubbleSkeleton width={dimensions.w} height={dimensions.h} />
          : <div style={{ padding: 32, color: MARKET_PALETTE.text }}>No games match your current filters.</div>}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="mm-surface"
      style={{ ...marketSurfaceStyle, flex: 1, position: "relative", height: "100%" }}
    >
      <MarketGrowthKey metricLabel={metricLabel} colorMode={colorMode} />
      {/* local bubble highlight CSS, like Steam.jsx */}
      <style>
        {`
          .mm-bubble-dim { opacity: 0.22; }
          .mm-bubble-hit { opacity: 1; }
        `}
      </style>

      <svg
        ref={(el) => {
          zoomSvgRef.current = el;
          svgRef.current = el;
        }}
        width={dimensions.w}
        height={dimensions.h}
      >
        <g id={`mm-zoom-${viewId}`} className="mm-zoom-group" transform={zoomT.toString()}>
          {nodes.map((d, i) => {
            const hit = highlightMatches.has(d);

            const isSelected = d.data.id != null && d.data.id === selectedId;
            const isHovered = hoveredNode?.data?.id === d.data.id;
            const Mark = squareMode ? "rect" : "circle";
            const markGeometry = inset => squareMode
              ? { x: -Math.max(0, d.side / 2 - inset), y: -Math.max(0, d.side / 2 - inset),
                width: Math.max(0, d.side - inset * 2), height: Math.max(0, d.side - inset * 2) }
              : { r: Math.max(0, d.r - inset) };

            return (
              <g
                key={d.data.id ?? d.data.name}
                className={`mm-node${isHovered ? " is-hovered" : ""} ${
                  isPreviewActive
                    ? hit
                      ? "mm-bubble-hit"
                      : "mm-bubble-dim"
                    : ""
                }`}
                data-game-name={d.data.name}
                data-market-value={d.data.value}
                transform={`translate(${d.x},${d.y})`}
                onMouseEnter={() => !selectedNode && setHovered(d)}
                onMouseMove={() => !selectedNode && setHovered(d)}
                onMouseLeave={(event) => {
                  if (!event.relatedTarget?.closest?.(".mg-market-mini-hover")) setHovered(null);
                }}
                onClick={(ev) => {
                  ev.stopPropagation();
                  ev.preventDefault();
                  if (onSelect) {
                    onSelect(d);
                    setHovered(null);
                    return;
                  }
                  if (isSelected) {
                    setSelected(null);
                    setHovered(null);
                  } else {
                    setSelected(d);
                    setHovered(null);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <Mark
                  className={squareMode ? "mm-bubble-core mm-square-core" : "mm-bubble-core"}
                  {...markGeometry(0)}
                  fill={d.data.color}
                  stroke={isHovered || isSelected ? MARKET_PALETTE.text : MARKET_PALETTE.canvas}
                  strokeOpacity={isHovered || isSelected ? .85 : .55}
                  strokeWidth={isHovered || isSelected ? 1.3 : .65}
                  vectorEffect="non-scaling-stroke"
                />
                {isHovered && !selectedNode && (
                  <Mark
                    {...markGeometry(2 / k)}
                    fill="none"
                    stroke={MARKET_PALETTE.text}
                    strokeOpacity={.25}
                    strokeWidth={.6}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                {isSelected && (
                  <Mark
                    {...markGeometry(3 / k)}
                    fill="none"
                    stroke={MARKET_PALETTE.text}
                    strokeOpacity={.7}
                    strokeWidth={1}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
              </g>
            );
          })}
          <g className="mm-label-layer" pointerEvents="none">
            {nodes.map((d) => {
              const label = squareMode
                ? squareMarketLabel(d.data.name, d.side, k)
                : d.r > 16 / Math.pow(k, 1.15)
                  ? fitMarketLabel(d.data.name, d.r * 2 - 6 / k, 13 / Math.pow(k, 0.8), {
                    minCharacters: d.r * k > 80 ? 34 : d.r * k > 44 ? 27 : 16,
                  })
                  : null;
              if (!label) return null;
              const textColor = label.overflows ? MARKET_PALETTE.text : marketLabelColor(d.data.color);
              // Overflowing labels cross onto the canvas or neighbouring marks, so they
              // use light text with a dark halo that reads on every background.
              const isGrowthFill = [MARKET_PALETTE.growthPeak, MARKET_PALETTE.growth, MARKET_PALETTE.growthModerate, MARKET_PALETTE.growthLow].includes(d.data.color);
              const halo = label.overflows
                ? { stroke: MARKET_PALETTE.canvas, strokeWidth: 2.4 / k, strokeOpacity: .82 }
                : { stroke: marketLabelStroke(d.data.color), strokeWidth: (isGrowthFill ? 1.6 : .75) / k, strokeOpacity: 1 };
              const dimmed = isPreviewActive && !highlightMatches.has(d);
              // Keep spreading labels inside the visible panel instead of clipping at its edge.
              const halfText = (label.text.length * label.fontSize * .54 * k) / 2 + 4;
              const offsetX = zoomT?.x || 0;
              const screenX = offsetX + d.x * k;
              const labelX = halfText * 2 < dimensions.w
                ? (Math.max(halfText, Math.min(dimensions.w - halfText, screenX)) - offsetX) / k
                : d.x;
              return (
                <text
                  key={d.data.id ?? d.data.name}
                  className={dimmed ? "mm-bubble-dim" : undefined}
                  x={labelX}
                  y={d.y}
                  textAnchor="middle"
                  dy="0.35em"
                  fontSize={label.fontSize}
                  fill={textColor}
                  stroke={halo.stroke}
                  strokeWidth={halo.strokeWidth}
                  strokeOpacity={halo.strokeOpacity}
                  strokeLinejoin="round"
                  style={{
                    fontWeight: 700,
                    letterSpacing: "0",
                    paintOrder: "stroke",
                    fontFamily: '"Jura", sans-serif',
                  }}
                >
                  {label.text}
                </text>
              );
            })}
          </g>
        </g>
      </svg>

      {/* mini badge tooltip */}
      {renderDetails && (selectedNode || hoveredNode) ? renderDetails({
        node: selectedNode || hoveredNode, dimensions, zoomT, pinned: !!selectedNode,
        onClose: () => { setSelected(null); setHovered(null); },
      }) : <>
      {!selectedNode && hoveredNode && (
        <BubbleMiniTooltip
          node={hoveredNode}
          dims={{ ...dimensions, bounds: containerRef.current?.getBoundingClientRect() }}
          zoomT={zoomT}
          maxRankByMetric={maxRankByMetric}
          onClose={() => setHovered(null)}
        />
      )}

      {/* full pop-out tooltip */}
      {selectedNode && (
        <MarketTooltip
          node={selectedNode}
          dims={dimensions}
          zoomT={zoomT}
          onClose={() => setSelected(null)}
          periodLabel={periodLabel}
        />
      )}
      </>}
    </div>
  );
}

function useMarketTreemapNodes({ filtered, dims }) {
  return useMemo(() => {
    if (!filtered?.length || dims.w <= 0 || dims.h <= 0) return [];

    const children = filtered
      .map((g, idx) => {
        const ms = Number.isFinite(g.mindshare) ? g.mindshare : Number(g.views ?? 0);
        if (!Number.isFinite(ms) || ms <= 0) return null;
        const latestKey = g._dateKeys?.[g._dateKeys.length - 1];
        const latestMeta = g._metaByDate?.get?.(latestKey) || {};
        const growth = Number.isFinite(g.growthPT)
          ? g.growthPT
          : Number.isFinite(g.percentGrowth)
          ? g.percentGrowth / 100
          : 0;

        return {
          id: g.id ?? idx,
          name: g.name,
          value: ms,
          ms,
          delta: Number.isFinite(g.delta) ? g.delta : null,
          growth,
          color: marketGrowthColor(growth),
          rank_mindshare: g.rank_mindshare ?? null,
          rank_search: g.rank_search ?? g.search_rank ?? latestMeta.searchRank ?? null,
          rank_video: g.rank_video ?? g.video_rank ?? latestMeta.videoRank ?? null,
          rank_twitch: g.rank_twitch ?? g.twitch_rank ?? latestMeta.twitchRank ?? null,
          search_volume: g.search_volume ?? latestMeta.search ?? null,
          publisher: g.publisher,
          developer: g.developer,
          tags: g.tags,
          genres: g.genres,
          live_service_static: g.live_service_static,
          pricing: g.pricing,
          user_generated_content: g.user_generated_content,
          single_multiplayer_focus: g.single_multiplayer_focus,
          platforms: g.platforms,
          description: g.description,
          release_date: g.release_date,
          image: g.imageUrl || g.image,
          video: g.steamVideo || g.igdbVideo || "",
          liveCcu: g.liveCcu ?? null,
          steamAppId: g.steamAppId ?? null,
          latestDate: g.latestDate ?? latestKey ?? null,
          prevDate: g.prevDate ?? null,
        };
      })
      .filter(Boolean);

    const root = d3
      .hierarchy({ children })
      .sum((d) => d.value)
      .sort((a, b) => (b.value || 0) - (a.value || 0));

    d3
      .treemap()
      .tile(d3.treemapSquarify.ratio(1.18))
      .size([dims.w, dims.h])
      .paddingOuter(0)
      .paddingInner(0.65)
      .round(true)(root);

    return root.leaves();
  }, [filtered, dims.w, dims.h]);
}

function MarketBoxesView({ filtered, preview, dimensions, loading }) {
  const containerRef = useRef(null);
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);

  const nodes = useMarketTreemapNodes({ filtered, dims: dimensions });

  useEffect(() => {
    const handler = (e) => {
      if (!selected) return;
      const el = document.querySelector(".ms-tooltip");
      if (el && el.contains(e.target)) return;
      setSelected(null);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [selected]);

  if (!filtered.length) {
    return (
      <div
        ref={containerRef}
        style={{
          height: "calc(calc(var(--mg-vh) * 100) - 40px)",
          width: "100%",
          position: "relative",
          opacity: loading ? 0 : 1,
          transition: "opacity 0.3s ease-in-out",
        }}
      >
        <BubbleSkeleton width={dimensions.w} height={dimensions.h} />
      </div>
    );
  }

  const isPreviewActive = Array.isArray(preview) ? preview.length > 0 : !!(preview && preview.type && preview.value);
  const tooltipNode = (leaf) => {
    if (!leaf) return null;
    const w = Math.max(1, leaf.x1 - leaf.x0);
    const h = Math.max(1, leaf.y1 - leaf.y0);
    return {
      ...leaf,
      x: leaf.x0 + w / 2,
      y: leaf.y0 + h / 2,
      r: Math.max(22, Math.min(110, Math.max(w, h) / 2)),
      data: leaf.data,
    };
  };

  const maxRankByMetric = (() => {
    const max = { mindshare: null, search: null, video: null, streaming: null };
    for (const g of filtered || []) {
      const rows = [
        ["mindshare", g.rank_mindshare],
        ["search", g.rank_search],
        ["video", g.rank_video],
        ["streaming", g.rank_twitch],
      ];
      for (const [key, value] of rows) {
        const n = Number(value);
        if (!Number.isFinite(n)) continue;
        max[key] = max[key] == null ? n : Math.max(max[key], n);
      }
    }
    return max;
  })();

  return (
    <div ref={containerRef} className="mm-surface" style={{ ...marketSurfaceStyle, flex: 1, position: "relative", width: dimensions.w, height: dimensions.h, overflow: "hidden" }}>
      <MarketGrowthKey />
      <style>{`
        .mm-box-tile { transition: opacity 160ms ease, filter 160ms ease; transform-box: fill-box; transform-origin: center; }
        .mm-box-tile:hover { filter: brightness(1.08) drop-shadow(0 0 8px rgba(255,255,255,0.18)); }
      `}</style>

      <svg width={dimensions.w} height={dimensions.h}>
        {nodes.map((leaf, i) => {
          const x = leaf.x0;
          const y = leaf.y0;
          const w = Math.max(0, leaf.x1 - leaf.x0);
          const h = Math.max(0, leaf.y1 - leaf.y0);
          const area = w * h;
          const hit = matchesPreviewBubble(leaf.data, preview);
          const dim = isPreviewActive && !hit;
          const isSelected = selected?.data?.id === leaf.data.id;
          const cx = x + w / 2;
          const cy = y + h / 2;
          const showLabel = area > 1250 && w > 34 && h > 18;
          const maxChars = w > 170 ? 28 : w > 100 ? 18 : 10;
          const label = leaf.data.name.length > maxChars ? `${leaf.data.name.slice(0, maxChars)}…` : leaf.data.name;

          return (
            <g
              key={leaf.data.id ?? i}
              className="mm-box-tile"
              transform="none"
              opacity={dim ? 0.24 : 1}
              onMouseEnter={() => !selected && setHovered(tooltipNode(leaf))}
              onMouseMove={() => !selected && setHovered(tooltipNode(leaf))}
              onMouseLeave={(event) => {
                if (!event.relatedTarget?.closest?.(".mg-market-mini-hover")) setHovered(null);
              }}
              onClick={(ev) => {
                ev.stopPropagation();
                ev.preventDefault();
                setSelected(isSelected ? null : tooltipNode(leaf));
                setHovered(null);
              }}
              style={{ cursor: "pointer" }}
            >
              <rect
                x={x}
                y={y}
                width={w}
                height={h}
                rx={1.5}
                ry={1.5}
                fill={leaf.data.color}
                fillOpacity={1}
                stroke={
                  isSelected
                    ? "rgba(255,255,255,0.95)"
                    : hovered?.data?.id === leaf.data.id && !selected
                    ? "rgba(255,255,255,0.82)"
                    : "rgba(255,255,255,0.62)"
                }
                strokeWidth={isSelected ? 1.6 : hovered?.data?.id === leaf.data.id && !selected ? 0.8 : 0.45}
                vectorEffect="non-scaling-stroke"
              />
              {showLabel && (
                <text
                  className="mm-box-label"
                  x={x + w / 2}
                  y={y + h / 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={marketLabelColor(leaf.data.color)}
                  stroke={marketLabelStroke(leaf.data.color)}
                  strokeWidth={.75}
                  fontSize={Math.max(12, Math.min(15, Math.sqrt(area) / 16))}
                  fontWeight={700}
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {!selected && hovered && (
        <BubbleMiniTooltip
          node={hovered}
          dims={{ ...dimensions, bounds: containerRef.current?.getBoundingClientRect() }}
          zoomT={d3.zoomIdentity}
          maxRankByMetric={maxRankByMetric}
          onClose={() => setHovered(null)}
        />
      )}

      {selected && (
        <MarketTooltip node={selected} dims={dimensions} zoomT={d3.zoomIdentity} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}



export function MarketViewContainer({
  filtered,
  loading,
  isEmpty,
  width,
  height,
  preview,
  displayMode = "bubbles",
  splitFiltered = null,
  splitEnabled = false,
  splitPreview = null,
  splitLabel = "",
  fitSplitToPanel = false,
  periodLabel = "",
  marketProps = {},
}) {
  const isBoxes = displayMode === "boxes";
  const sharedMarketProps = {
    ...marketProps,
    colorMaximum: marketProps.colorMaximum ?? Math.max(0, ...filtered.map(game => Number(game.mindshare) || 0)),
  };

  // The boxes view lives inside an inset frame so it never runs under the top
  // controls or off the bottom edge. The SVG dimensions are reduced to match
  // that frame, which prevents the last rows from being clipped.
  const boxRightBuffer = 56;
  const boxBottomBuffer = 84;
  const boxTopBuffer = 58;
  const boxInnerW = Math.max(320, width - boxRightBuffer);
  const boxInnerH = Math.max(320, height - boxTopBuffer - boxBottomBuffer);

  const splitGap = 14;
  const splitTitleH = 58;
  const splitViewH = Math.max(300, height - splitTitleH - 14);
  // The filtered panel is narrower than half the screen, but it packs at the
  // old half-width scale so its marks keep their size and only spacing tightens.
  const splitHalfW = Math.max(340, Math.floor((width - splitGap) / 2));
  const splitPackW = Math.max(300, splitHalfW - 18);
  const splitPackNodes = useBubblePack({
    filtered: splitEnabled && !isBoxes ? splitFiltered : [],
    dims: { w: splitPackW, h: splitViewH },
    colorScale: marketGrowthColor,
  });
  const largestSplitMark = splitPackNodes.reduce((largest, node) =>
    Math.max(largest, node.r * (displayMode === "squares" ? Math.sqrt(Math.PI) : 2)), 0);
  // A dominant game may need a wider panel, but must never be shrunk to fit it.
  const splitPanelW = splitEnabled
    ? Math.max(Math.min(splitHalfW, Math.max(340, Math.round(width * 0.38))), Math.ceil(largestSplitMark + 42))
    : width;
  const splitW = splitEnabled ? splitPanelW : 0;
  const mainW = splitEnabled ? Math.max(340, width - splitGap - splitPanelW) : width;
  const mainBoxInnerW = isBoxes ? Math.max(320, mainW - boxRightBuffer) : mainW;
  const splitBoxInnerW = isBoxes ? Math.max(300, splitW - 32) : Math.max(300, splitW - 18);
  const splitBoxInnerH = isBoxes ? Math.max(300, boxInnerH - splitTitleH) : Math.max(300, height - splitTitleH - 14);

  return (
    <div
      style={{
        position: "absolute",
        inset: isBoxes ? `${boxTopBuffer}px ${boxRightBuffer}px ${boxBottomBuffer}px 0` : 0,
        border: "none",
        borderRadius: 0,
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(40,144,195,0.08), transparent 55%)",
        display: splitEnabled ? "grid" : "block",
        gridTemplateColumns: splitEnabled ? `minmax(0, 1fr) ${splitPanelW}px` : undefined,
        gap: splitEnabled ? splitGap : 0,
        minWidth: 0,
      }}
    >
      {loading ? (
        <SkeletonMarketViewBubbleChart width={isBoxes ? boxInnerW : width} height={isBoxes ? boxInnerH : height} />
      ) : isEmpty ? (
        <div
          style={{
            color: "#aaa",
            textAlign: "center",
            padding: 40,
          }}
        >
          <h3>No games match your current filters</h3>
          <p>Try adjusting your search criteria or filters to see results.</p>
        </div>
      ) : splitEnabled ? (
        <>
          <div style={{ position: "relative", minWidth: 0, overflow: "hidden" }}>
            {isBoxes ? (
              <MarketBoxesView filtered={filtered} preview={preview} dimensions={{ w: mainBoxInnerW, h: boxInnerH }} loading={loading} />
            ) : (
              <MarketView {...sharedMarketProps} filtered={filtered} preview={preview} dimensions={{ w: mainW, h: height }} loading={loading} periodLabel={periodLabel} squareMode={displayMode === "squares"} />
            )}
          </div>
          <div
            style={{
              position: "relative",
              minWidth: 0,
              overflow: "hidden",
              borderLeft: "1px solid rgba(255,255,255,0.12)",
              paddingLeft: 12,
              background: "linear-gradient(180deg, rgba(255,255,255,0.025), rgba(255,255,255,0.005))",
            }}
          >
            <div
              style={{
                minHeight: splitTitleH,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 10,
                color: "#cfeeff",
                fontSize: 12,
                fontWeight: 950,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
                paddingTop: 2,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div>Filtered Zoom</div>
                {splitLabel && (
                  <div
                    title={splitLabel}
                    style={{
                      marginTop: 4,
                      maxWidth: "100%",
                      color: "rgba(207,216,227,0.78)",
                      fontSize: 11,
                      lineHeight: "14px",
                      fontWeight: 850,
                      letterSpacing: 0,
                      textTransform: "none",
                      whiteSpace: "normal",
                    }}
                  >
                    {splitLabel}
                  </div>
                )}
              </div>
              <span style={{ color: "rgba(207,216,227,0.64)", fontSize: 11, fontWeight: 850, textTransform: "none", flexShrink: 0 }}>
                {(splitFiltered || []).length.toLocaleString()} games
              </span>
            </div>
            {isBoxes ? (
              <MarketBoxesView
                filtered={splitFiltered || []}
                preview={splitPreview}
                dimensions={{ w: splitBoxInnerW, h: splitBoxInnerH }}
                loading={loading}
              />
            ) : (
              <MarketView
                {...sharedMarketProps}
                filtered={splitFiltered || []}
                preview={splitPreview}
                dimensions={{ w: Math.max(300, splitW - 18), h: splitViewH }}
                packWidth={fitSplitToPanel ? null : splitPackW}
                loading={loading}
                periodLabel={periodLabel}
                squareMode={displayMode === "squares"}
              />
            )}
          </div>
        </>
      ) : isBoxes ? (
        <MarketBoxesView
          filtered={filtered}
          preview={preview}
          dimensions={{ w: boxInnerW, h: boxInnerH }}
          loading={loading}
        />
      ) : (
        <MarketView
          {...sharedMarketProps}
          filtered={filtered}
          preview={preview}
          dimensions={{ w: width, h: height }}
          loading={loading}
          periodLabel={periodLabel}
          squareMode={displayMode === "squares"}
        />
      )}
    </div>
  );}





/* ---------- mindSHARE Cards ---------- */
const CARD_WIDTH = 200;
// Frame padding/borders (32), range row (32), and collapsed legend row (36).
const CHART_PANEL_NONPLOT_HEIGHT = 100;

function getMindshareDefaultChartHeight() {
  const viewportWidth = typeof document !== "undefined"
    ? viewportClientWidth()
    : layoutViewportWidth();
  const sectionWidth = Math.max(280, viewportWidth - 60);
  const columns = Math.max(1, Math.floor((sectionWidth + 16) / (CARD_WIDTH + 16)));
  const cardWidth = (sectionWidth - 16 * (columns - 1)) / columns;
  const cardHeight = (cardWidth - 2) * (380 / 285) + 2;
  const cardsHeight = 2 * cardHeight + 16;
  const stacked = layoutViewportWidth() <= 1500;
  const mobile = layoutViewportWidth() <= 600;
  const slotHeight = cardsHeight + (stacked ? (mobile ? 236 : 246) : 0);
  return Math.max(340, slotHeight - CHART_PANEL_NONPLOT_HEIGHT);
}

const MindshareCard = memo(function MindshareCard({ game, index, onRemove, inChart, onToggleChart, editorial, readOnly = false, covered = false }) {
  const [hovered, setHovered] = useState(false);
  const [metadataVisible, setMetadataVisible] = useState(false);
  const [metadataPosition, setMetadataPosition] = useState({ left: 0, width: 420 });
  const [metadataBridge, setMetadataBridge] = useState(null);
  const dateKeys = Array.isArray(game?._dateKeys) ? game._dateKeys : [];
  const latestKey = dateKeys[dateKeys.length - 1];
  const previousKey = dateKeys[dateKeys.length - 2];
  const latestValue = Number(game?._byDate?.get?.(latestKey));
  const previousValue = Number(game?._byDate?.get?.(previousKey));
  const growth = Number.isFinite(game?.growthPT)
    ? Number(game.growthPT)
    : previousValue > 0 && Number.isFinite(latestValue)
    ? (latestValue - previousValue) / previousValue
    : 0;
  const growthLabel = `${growth >= 0 ? "+" : ""}${Math.round(growth * 100)}%`;
  const showCardLabel = index === 0;
  const latestMeta = game._metaByDate?.get?.(latestKey) || {};
  const tooltipBox = {
    ...game,
    value: latestKey != null && Number.isFinite(latestValue) ? latestValue : game.views,
    delta: latestKey != null && previousKey != null && Number.isFinite(latestValue) && Number.isFinite(previousValue)
      ? latestValue - previousValue : game.delta ?? game.videos,
    latestDate: latestKey ?? game.latestDate,
    rank_mindshare: latestMeta.rank ?? game.rank_mindshare,
    rank_search: latestMeta.searchRank ?? game.search_rank ?? game.rank_search,
    rank_video: latestMeta.videoRank ?? game.video_rank ?? game.rank_video,
    rank_twitch: latestMeta.twitchRank ?? game.twitch_rank ?? game.rank_twitch,
    search_volume: latestMeta.search ?? game.search_volume,
  };

  const handleMouseEnter = (event) => {
    // Covered ghost cards sit behind the preview veil: the pure-CSS pop still
    // runs, but the card must never lift above the gray or show metadata.
    if (covered) return;
    setHovered(true);
    setMetadataVisible(true);
    const rect = layoutRect(event.currentTarget);
    const cardWidth = rect.width || CARD_WIDTH;
    const grid = event.currentTarget.parentElement;
    const gridRect = layoutRect(grid);
    const gridStyle = getComputedStyle(grid);
    const gap = parseFloat(gridStyle.columnGap) || 16;
    const rowGap = parseFloat(gridStyle.rowGap) || gap;
    const columns = Math.max(1, Math.round((gridRect.width + gap) / (cardWidth + gap)));
    const span = Math.min(2, columns);
    const panelWidth = span * cardWidth + (span - 1) * gap;
    const column = Math.round((rect.left - gridRect.left) / (cardWidth + gap));
    const startColumn = column + span < columns ? column + 1 : column >= span ? column - span : Math.max(0, columns - span);
    const viewportLeft = gridRect.left + startColumn * (cardWidth + gap);
    setMetadataBridge(viewportLeft >= rect.right
      ? { left: cardWidth, width: viewportLeft - rect.right }
      : viewportLeft + panelWidth <= rect.left
      ? { left: viewportLeft + panelWidth - rect.left, width: rect.left - viewportLeft - panelWidth }
      : null);
    setMetadataPosition({
      left: viewportLeft - rect.left,
      top: 0,
      width: panelWidth,
      minHeight: rect.height * 2 + rowGap,
    });
  };

  return (
    <div
      style={{ position: "relative", width: "100%", margin: 0, zIndex: hovered ? 40 : 0 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => {
        setHovered(false);
        setMetadataVisible(false);
      }}
    >
      <MGTechFrame
        data-ms-card
        className={`mg-game-art-card ${growth < 0 ? "is-negative-growth" : "is-positive-growth"}`}
        style={{
          transform: "translateZ(0)",
        }}
      >
        <div className="mg-game-art-media">
          {game.imageUrl ? (
            <img
              src={game.imageUrl}
              alt={game.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
              loading="lazy"
              decoding="async"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
          ) : (
            <div
              style={{
                width: "100%",
                height: "100%",
                background: "linear-gradient(135deg, #1a1a1a 0%, #222 50%, #1a1a1a 100%)",
              }}
            />
          )}

          {editorial ? <MGRankBadge className="mg-game-art-rank" rank={index + 1} /> : <div
            className="mg-game-art-rank"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "auto",
              minWidth: 24,
              boxSizing: "border-box",
              whiteSpace: "nowrap",
              height: 22,
              color: "#fff",
              fontSize: 12,
              padding: "0 5px",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              textShadow: "0 1px 4px rgba(0,0,0,0.95)",
              lineHeight: "22px",
              zIndex: 11,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            #{index + 1}
          </div>}

          {!readOnly && (
          <button
            className="mg-game-art-remove"
            type="button"
            onClick={(e) => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              e.stopPropagation();
              onRemove?.(game.name);
            }}
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 22,
              height: 22,
              color: "#fff",
              fontSize: 12,
              padding: 0,
              border: "none",
              lineHeight: "22px",
              fontWeight: 900,
              letterSpacing: "-0.02em",
              textShadow: "0 1px 4px rgba(0,0,0,0.95)",
              cursor: "pointer",
              zIndex: 11,
            }}
            aria-label="Remove game"
          >
            ×
          </button>
          )}

          <div
            className="mg-game-art-footer"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 22px",
              alignItems: "end",
              gap: 10,
              padding: "36px 10px 14px 10px",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.76) 44%, rgba(0,0,0,0.0) 100%)",
              pointerEvents: "auto",
            }}
          >
            <div
              style={{
                minWidth: 0,
                color: "#cfd8e3",
                textShadow: "0 1px 4px rgba(0,0,0,0.95)",
              }}
            >
              {showCardLabel && (
                <div
                  style={{
                    color: "rgba(207,216,227,0.86)",
                    fontSize: 10,
                    lineHeight: "11px",
                    fontWeight: 900,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    marginBottom: 1,
                  }}
                >
                  mindSHARE
                </div>
              )}
              <div elementtiming={index === 0 ? "mindshare-first-card-content" : undefined} style={{ color: "#fff", fontSize: 16, lineHeight: "18px", fontWeight: 950 }}>
                {pctMindshare(game.views || 0)}
              </div>
              <div style={{ color: growth >= 0 ? "#20a5bb" : "#ffb19f", fontSize: 11, lineHeight: "13px", fontWeight: 800 }}>
                {growthLabel}{showCardLabel ? " GROWTH" : ""}
              </div>
            </div>

            {!readOnly && (
            <button
              className="mg-game-art-add"
              type="button"
              onMouseEnter={() => setMetadataVisible(false)}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMetadataVisible(false);
                onToggleChart?.(game);
              }}
              style={{
                width: 22,
                minWidth: 22,
                height: 22,
                flex: "0 0 22px",
                justifySelf: "end",
                position: "absolute",
                right: 0,
                bottom: 0,
                border: "none",
                color: "#fff",
                fontSize: 15,
                lineHeight: "15px",
                fontWeight: 900,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "none",
              }}
              title={inChart ? "Remove from chart" : "Add to chart"}
            >
              {inChart ? "✓" : "+"}
            </button>
            )}
          </div>
        </div>
      </MGTechFrame>
      {metadataVisible && hovered && metadataBridge && (
        <div style={{ position: "absolute", top: metadataPosition.top, ...metadataBridge, height: "100%", pointerEvents: "auto" }} />
      )}
      {metadataVisible && hovered && (
        <div
          style={{
            position: "absolute",
            ...metadataPosition,
            zIndex: 30,
            boxSizing: "border-box",
            overflow: "visible",
            borderRadius: 2,
            boxShadow: "0 22px 48px rgba(0,0,0,0.78), 0 0 24px rgba(32,165,187,0.18)",
            backdropFilter: "blur(10px)",
            animation: "mgCardMetadataIn 180ms ease-out both",
            pointerEvents: "auto",
          }}
        >
          <GameDetailsTooltip box={tooltipBox} showImage={false} cardHeight={metadataPosition.minHeight} />
        </div>
      )}
    </div>
  );
});

const MindshareCardGrid = memo(function MindshareCardGrid({ games, loading, onRemoveGame, comparisonGames, onToggleChart, editorial, readOnly = false, fixedCount = 0, coveredFromIndex = Number.MAX_SAFE_INTEGER }) {
  const INITIAL = 60;
  const BATCH = 60;
  const [showCount, setShowCount] = useState(INITIAL);
  const capped = fixedCount > 0;

  const gamesSignature = useMemo(
    () => games.map((g) => g.name).join("\u0001"),
    [games]
  );
  useEffect(() => {
    if (!capped) setShowCount(INITIAL);
  }, [gamesSignature, capped]);
  useEffect(() => {
    if (capped) return undefined;
    const onScroll = () => {
      if (layoutViewportHeight() + viewportScrollY() + 600 >= document.documentElement.offsetHeight) {
        setShowCount((n) => Math.min(n + BATCH, games.length));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [games.length, capped]);

  const showSkeleton = Boolean(loading && games.length === 0);
  const slice = useMemo(() => games.slice(0, capped ? fixedCount : showCount), [games, showCount, capped, fixedCount]);
  const chartSet = useMemo(() => new Set(comparisonGames || []), [comparisonGames]);

  return (
    <div
      className="mg-mindshare-card-grid mg-game-art-grid"
    >
      {showSkeleton && (
        <style>{`
          @keyframes mgMindshareSkeletonFlip {
            0% { transform: rotateX(90deg); opacity: 0; }
            40% { transform: rotateX(-15deg); opacity: 1; }
            100% { transform: rotateX(0deg); opacity: 1; }
          }
          @keyframes mgCardMetadataIn {
            from { opacity: 0; transform: translateY(4px) scale(0.99); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .mg-mindshare-skel-card {
            position: relative;
            width: 100%;
            background: linear-gradient(135deg, #0d0d0d 0%, #141414 50%, #0d0d0d 100%);
            overflow: hidden;
            transform-origin: top center;
            will-change: transform, opacity;
          }
        `}</style>
      )}

      {showSkeleton
        ? Array.from({ length: 60 }).map((_, i) => (
            <div
              key={i}
              className="mg-mindshare-skel-card mg-game-art-skeleton"
              style={{
                animation: "mgMindshareSkeletonFlip 1600ms cubic-bezier(.2,.8,.2,1) infinite",
                animationDelay: `${i * 70}ms`,
              }}
            />
          ))
        : slice.map((g, i) => (
            <MindshareCard
              key={g.name}
              game={g}
              index={i}
              onRemove={onRemoveGame}
              inChart={chartSet.has(g.name)}
              onToggleChart={onToggleChart}
              editorial={editorial}
              readOnly={readOnly}
              covered={i >= coveredFromIndex}
            />
          ))}
    </div>
  );
});

// Public preview: 3 rows of read-only ranked cards, then a gray cloud veil
// starting in the middle of the gap below row 3 and covering 3 more rows of
// ghost cards, with a membership call-to-action centered in the veiled region.
const MindsharePublicPreview = memo(function MindsharePublicPreview({ games, loading, onToggleChart, onRemoveGame, editorial }) {
  const gridRef = useRef(null);
  const [metrics, setMetrics] = useState({ veilTop: 0, ctaTop: 0, minHeight: 0, visibleCount: 0, totalCount: 0 });

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const measure = () => {
      const cards = grid.querySelectorAll("[data-ms-card]");
      if (!cards.length) return;
      const first = cards[0].getBoundingClientRect();
      const gridBox = grid.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(grid).rowGap) || 16;
      const rowHeight = first.height + gap;
      // Count how many cards sit on the first row to derive rows shown.
      const firstTop = first.top;
      let perRow = 0;
      for (const card of cards) {
        if (Math.abs(card.getBoundingClientRect().top - firstTop) < 2) perRow += 1;
        else break;
      }
      perRow = Math.max(1, perRow);
      const rowsShown = Math.max(1, Math.ceil(cards.length / perRow));
      const visibleRows = Math.min(3, rowsShown);
      const visibleCount = Math.min(games.length, visibleRows * perRow);
      // Fill the veiled region with 3 full rows of ghost cards where possible.
      const totalCount = Math.min(games.length, visibleCount + 3 * perRow);
      // Veil starts in the middle of the gap below the last fully visible row.
      const veilTop = (first.top - gridBox.top) + visibleRows * rowHeight - gap / 2;
      // Extend roughly three more rows of teaser height below the veil.
      const minHeight = veilTop + 3 * rowHeight;
      const ctaTop = veilTop + (3 * rowHeight) / 2;
      setMetrics((previous) => (
        Math.abs(previous.veilTop - veilTop) > 1 || Math.abs(previous.minHeight - minHeight) > 1 ||
        previous.visibleCount !== visibleCount || previous.totalCount !== totalCount
          ? { veilTop, ctaTop, minHeight, visibleCount, totalCount }
          : previous
      ));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(grid);
    return () => observer.disconnect();
  }, [games, loading]);

  return (
    <div className="mg-mindshare-preview-wrap" style={{ minHeight: metrics.minHeight || undefined }}>
      <div ref={gridRef}>
        <MindshareCardGrid
          editorial={editorial}
          games={games}
          comparisonGames={[]}
          onToggleChart={onToggleChart}
          loading={loading}
          onRemoveGame={onRemoveGame}
          readOnly
          fixedCount={metrics.totalCount || 60}
          coveredFromIndex={metrics.visibleCount || Number.MAX_SAFE_INTEGER}
        />
      </div>
      {metrics.veilTop > 0 && (
        <>
          <div className="mg-mindshare-preview-veil" style={{ top: metrics.veilTop }} aria-hidden="true" />
          <div
            className="mg-mindshare-preview-cta"
            style={{ top: metrics.ctaTop }}
            role="note"
            aria-label="mindSHARE full access"
          >
            <Lock size={22} aria-hidden="true" />
            <h3>Full mindSHARE access for paying members</h3>
            <p>
              Rankings, historical trends, filters, and chart tools are available to members.
              Contact <a href="mailto:info@mindgamedata.com">info@mindgamedata.com</a> for details.
            </p>
          </div>
        </>
      )}
    </div>
  );
});


// Inline text-style dropdown used in the chart header (Date / Weekly / Line / Benchmarks)
function HeaderTitleSelect({
  value,
  options,
  onChange,
  ariaLabel,
  multiple = false,
  triggerLabel,
  triggerIcon = null,
  triggerPrefix = "",
  fixedLabel = "",
  align = "left",
  width = 168,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const selectedValues = multiple ? new Set(value || []) : new Set([value]);
  const selectedOption = multiple ? null : options.find((o) => o.value === value) || options[0];
  const label = multiple
    ? `${triggerLabel}${selectedValues.size ? ` (${selectedValues.size})` : ""}`
    : fixedLabel
    ? fixedLabel
    : triggerPrefix
    ? `${triggerPrefix}: ${selectedOption?.label ?? ""}`
    : selectedOption?.label;

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          width,
          height: 26,
          padding: "0 8px 0 9px",
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.16)",
          background: "rgba(255,255,255,0.03)",
          color: "rgba(238,243,247,0.88)",
          cursor: "pointer",
          outline: "none",
          fontFamily: "inherit",
          fontSize: 15,
          lineHeight: "20px",
          fontWeight: 450,
          whiteSpace: "nowrap",
          textShadow: "none",
        }}
      >
        {multiple
          ? triggerIcon && (
              <span aria-hidden style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, lineHeight: 1 }}>{triggerIcon}</span>
            )
          : selectedOption?.icon && (
              <span aria-hidden style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, fontSize: 13, lineHeight: 1 }}>{selectedOption.icon}</span>
            )}
        <span style={{ flex: "1 1 auto", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" }}>{label}</span>
        <span
          aria-hidden
          style={{
            color: "#fff",
            fontSize: 13,
            lineHeight: 1,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        >
          ▾
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 7px)",
            [align === "right" ? "right" : "left"]: 0,
            minWidth: "100%",
            zIndex: 1000,
            background: "#0b0b0b",
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: 12,
            padding: 6,
            boxShadow: "0 18px 45px rgba(0,0,0,0.85)",
          }}
        >
          {options.map((opt) => {
            const isSelected = selectedValues.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(opt.value);
                  if (!multiple) setOpen(false);
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "rgba(255,255,255,0.07)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "transparent";
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  width: "100%",
                  height: 32,
                  padding: "0 10px",
                  borderRadius: 8,
                  border: "none",
                  background: isSelected ? "rgba(255,255,255,0.14)" : "transparent",
                  color: isSelected ? "#ffffff" : "#cfd8e3",
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: "inherit",
                  fontSize: 14,
                  fontWeight: 450,
                  whiteSpace: "nowrap",
                }}
              >
                {opt.icon && (
                  <span aria-hidden style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, fontSize: 13, lineHeight: 1 }}>{opt.icon}</span>
                )}
                <span style={{ flex: "1 1 auto" }}>{opt.label}</span>
                {isSelected && <span aria-hidden style={{ fontSize: 12 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

export function HeaderMiniDropdown({ value, options, onChange, active = false, width = 160, prefix = "", multi = false, placeholder = "" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", handleMouseDown);
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        wrapRef.current?.querySelector("button")?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);
  const selected = options.find((o) => o.value === value) || options[0];
  return (
    <div ref={wrapRef} style={{ position: "relative", width, flexShrink: 0 }}>
      <button type="button" aria-expanded={open} aria-label={multi ? placeholder : undefined} onClick={() => setOpen((v) => !v)} style={{ width: "100%", height: 32, borderRadius: 7, border: active ? "1px solid #20a5bb" : "1px solid #444", background: active ? "rgba(32,165,187,0.10)" : "#111", color: active ? "#20a5bb" : "#cfd8e3", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 9px 0 10px", fontSize: 12, fontWeight: 800, boxShadow: active ? "0 0 0 1px rgba(32,165,187,0.12), 0 0 12px rgba(32,165,187,0.10)" : "none" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
          {prefix && <span style={{ color: "#cfd8e3", fontWeight: 700 }}>{prefix} </span>}
          {multi ? `${placeholder}${value.length ? ` (${value.length})` : ""}` : selected?.label}
        </span>
        <span style={{ color: active ? "#20a5bb" : "#aaa", fontSize: 12, lineHeight: 1, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}>▾</span>
      </button>
      {open && (
        <div role={multi ? "group" : undefined} aria-label={multi ? placeholder : undefined} style={{ position: "absolute", top: 36, left: 0, width: "100%", zIndex: 1000, background: "#151515", border: "1px solid #444", borderRadius: 7, overflow: "hidden", boxShadow: "0 14px 35px rgba(0,0,0,0.75)" }}>
          {options.map((opt) => {
            const selectedOpt = multi ? value.includes(opt.value) : opt.value === value;
            return (
              <button key={opt.value} type="button" role={multi ? "checkbox" : undefined} aria-checked={multi ? selectedOpt : undefined} onClick={() => {
                onChange(multi ? selectedOpt ? value.filter(item => item !== opt.value) : [...value, opt.value] : opt.value);
                if (!multi) setOpen(false);
              }} style={{ width: "100%", border: "none", background: selectedOpt ? "rgba(32,165,187,0.18)" : "#151515", color: selectedOpt ? "#20a5bb" : "#ddd", padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, fontWeight: 800, cursor: "pointer", textAlign: "left" }} onMouseEnter={(e) => { if (!selectedOpt) e.currentTarget.style.background = "#222"; }} onMouseLeave={(e) => { if (!selectedOpt) e.currentTarget.style.background = "#151515"; }}>
                <span>{opt.label}</span>{selectedOpt && <span style={{ fontWeight: 900 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SidebarDivider = ({ label = "FILTERS" }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8, margin: "8px 0 2px 0", color: "rgba(187,187,187,0.70)", fontSize: 11, fontWeight: 900, letterSpacing: "0.16em" }}>
    <span style={{ height: 1, background: "rgba(255,255,255,0.12)" }} /><span>-{label}-</span><span style={{ height: 1, background: "rgba(255,255,255,0.12)" }} />
  </div>
);


const MINDSHARE_CHART_MARGIN = { left: 8, right: 80, top: 64, bottom: 0 };
const MINDSHARE_SKELETON_DATA = Array.from({ length: 52 }, (_, index) => ({
  x: index,
  primary: 0.12 + index / 110 + Math.sin(index / 5) * 0.09,
  secondary: 0.08 + index / 190 + Math.sin(index / 7) * 0.04,
}));

export const MindshareChartSkeleton = ({ height = 520, axisWidth = 90, axisData = [], axisMax = 1, formatXAxis, message = "Loading mindSHARE history…" }) => {
  const skeletonData = (axisData.length > 1 ? axisData : MINDSHARE_SKELETON_DATA).map((entry, index, entries) => {
    const position = index * 51 / Math.max(1, entries.length - 1);
    return {
      x: entry.x,
      primary: (0.12 + position / 110 + Math.sin(position / 5) * 0.09) * axisMax,
      secondary: (0.08 + position / 190 + Math.sin(position / 7) * 0.04) * axisMax,
    };
  });
  const marker = skeletonData[Math.floor((skeletonData.length - 1) * 0.7)];

  return (
    <div
      className="mg-mindshare-history-loader"
      style={{
        position: "relative",
        width: "100%",
        height,
        minHeight: typeof height === "number" ? Math.min(340, height) : 0,
        borderRadius: 2,
        overflow: "hidden",
        background: "rgba(0,0,0,0.08)",
      }}
    >
      <style>{`
        @keyframes mgMindshareHistoryAreaReveal {
          0% { clip-path: inset(0 100% 0 0); opacity: 0.32; }
          22% { opacity: 0.72; }
          100% { clip-path: inset(0 0 0 0); opacity: 0.72; }
        }
        @keyframes mgMindshareHistorySkeletonPulse {
          0%, 100% { opacity: 0.34; }
          50% { opacity: 0.72; }
        }
        .mg-mindshare-chart-skel-svg {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
        }
        .mg-mindshare-chart-skel-area {
          animation: mgMindshareHistoryAreaReveal 1.35s cubic-bezier(.2,.8,.2,1) both,
                     mgMindshareHistorySkeletonPulse 2.4s ease-in-out 1.35s infinite;
          transform-origin: left center;
        }
        .mg-mindshare-chart-skel-area-secondary {
          animation-delay: 140ms, 1.5s;
        }
        .mg-mindshare-chart-skel-block {
          position: absolute;
          overflow: hidden;
          background: rgba(255,255,255,0.07);
          border-radius: 7px;
          animation: mgMindshareHistorySkeletonPulse 1.45s ease-in-out infinite;
        }
        .mg-mindshare-history-loader::after {
          content: "";
          position: absolute;
          inset: 64px 80px 30px ${axisWidth + MINDSHARE_CHART_MARGIN.left}px;
          pointer-events: none;
          background:
            linear-gradient(#ece6da80, #ece6da80) left top / 12px 1px no-repeat,
            linear-gradient(#ece6da80, #ece6da80) left top / 1px 12px no-repeat,
            linear-gradient(#20a5bb80, #20a5bb80) right bottom / 12px 1px no-repeat,
            linear-gradient(#20a5bb80, #20a5bb80) right bottom / 1px 12px no-repeat;
        }
        @media (prefers-reduced-motion: reduce) {
          .mg-mindshare-chart-skel-area { animation: none; opacity: .6; }
        }
      `}</style>

      <ResponsiveContainer width="100%" height="100%">
      <ComposedChart className="mg-mindshare-chart-skel-svg" data={skeletonData} margin={MINDSHARE_CHART_MARGIN}>
        <CartesianGrid stroke="rgba(236,230,218,0.08)" />
        <XAxis dataKey="x" tickFormatter={formatXAxis} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.16)" }} tick={({ x, y }) => <rect x={x - 22} y={y + 3} width={44} height={2} fill="#ece6da40" />} />
        <YAxis width={axisWidth} domain={[0, axisMax]} tickFormatter={(value) => pct3(value)} tickLine={false} axisLine={{ stroke: "rgba(255,255,255,0.16)" }} tick={({ x, y }) => <rect x={x - 58} y={y - 1} width={50} height={2} fill="#ece6da40" />} />
        <Area className="mg-mindshare-chart-skel-area" dataKey="primary" type="linear" fill="rgba(32,165,187,0.04)" stroke="#20a5bb" strokeWidth={1} isAnimationActive={false} />
        <Line className="mg-mindshare-chart-skel-area mg-mindshare-chart-skel-area-secondary" dataKey="secondary" type="linear" stroke="#ece6da60" strokeWidth={1} strokeDasharray="3 7" dot={false} isAnimationActive={false} />
        <ReferenceDot x={marker.x} y={marker.primary} r={3} fill="#ff6258" stroke="#0b1012" strokeWidth={1} />
      </ComposedChart>
      </ResponsiveContainer>

      {message && <div
        role="status"
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          padding: "10px 14px",
          borderRadius: 2,
          border: "1px solid #ece6da40",
          borderLeft: "2px solid #20a5bb",
          background: "#0b1012",
          color: "#ece6da",
          fontFamily: '"Jura", sans-serif',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: ".16em",
          textTransform: "uppercase",
        }}
      >
        {message}
      </div>}
    </div>
  );
};



/* =============================== SPLASH =============================== */
function useMindshareSplashHeaderOffset() {
  const [headerOffset, setHeaderOffset] = useState(71);

  useLayoutEffect(() => {
    const header = document.querySelector(".mg-app-header");
    if (!header) return;
    const updateOffset = () => setHeaderOffset(Math.max(0, layoutRect(header).bottom));
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(header);
    window.addEventListener("resize", updateOffset);
    window.addEventListener("scroll", updateOffset, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
      window.removeEventListener("scroll", updateOffset);
    };
  }, []);

  return headerOffset;
}

function MindshareSplash({ games, periodLabel, onEnter }) {
  const headerOffset = useMindshareSplashHeaderOffset();
  const [activeIdx, setActiveIdx] = useState(0);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const [videoExpanded, setVideoExpanded] = useState(false);
  const hoverRef = useRef(false);
  const videoOpenRef = useRef(false);
  const active = games[activeIdx] || games[0];

  const activeVideoUrl = active?.videoUrl || null;
  const activeVideo = activeVideoUrl ? getVideoEmbed(activeVideoUrl) : null;
  const controlledVideoSrc = activeVideo?.type === "youtube"
    ? activeVideo.src.replace("controls=0", "controls=1")
    : activeVideo?.src;

  useEffect(() => {
    videoOpenRef.current = false;
    setShowVideoPreview(false);
    setVideoExpanded(false);
  }, [active?.id, active?.name]);

  useEffect(() => {
    if (games.length < 2 || showVideoPreview || videoExpanded) return;
    const id = setInterval(() => {
      if (hoverRef.current || videoOpenRef.current) return;
      setActiveIdx((i) => (i + 1) % games.length);
    }, 6500);
    return () => clearInterval(id);
  }, [games.length, showVideoPreview, videoExpanded]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && videoExpanded) {
        setVideoExpanded(false);
        return;
      }
      if (e.key === "ArrowRight") setActiveIdx((i) => (i + 1) % games.length);
      if (e.key === "ArrowLeft") setActiveIdx((i) => (i - 1 + games.length) % games.length);
      if (e.key === "Escape") onEnter?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [games.length, onEnter, videoExpanded]);

  if (!active) return null;

  const growthColor = marketGrowthColor(active.growthPT);
  const rankRows = [
    { label: "mindSHARE", rank: active.rank_mindshare, icon: MINDGAME_TREE_ICON, alt: "mindGAME" },
    { label: "Search", rank: active.rank_search, icon: GOOGLE_ICON, alt: "Google Search" },
    { label: "Video", rank: active.rank_video, icon: YT_ICON, alt: "YouTube" },
    { label: "Streaming", rank: active.rank_twitch, icon: TWITCH_ICON, alt: "Twitch" },
  ];
  const fmtRank = (n) => (Number.isFinite(Number(n)) ? `#${Number(n)}` : "—");

  return (
    <div
      className="mg-splash-intro"
      style={{
        position: "fixed",
        top: headerOffset,
        left: 0,
        right: 0,
        height: `calc(calc(var(--mg-vh) * 100) - ${headerOffset}px)`,
        zIndex: 7999,
        background: "#000",
        overflow: "hidden",
      }}
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      {active.imageUrl && (
        <div
          key={active.id}
          className="mg-splash-art"
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${active.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center 20%",
            filter: "blur(1.5px) saturate(1.05) brightness(0.34)",
          }}
        />
      )}

      <style>{`
        @keyframes mgSplashArtFade {
          0% { opacity: 0; transform: scale(1.09); }
          100% { opacity: 1; transform: scale(1.05); }
        }
        @keyframes mgSplashVideoIn {
          0% {
            opacity: 0;
            transform: translateX(28px) scale(0.94);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }
        @keyframes mgSplashPlayIn {
          0% { opacity: 0; transform: scale(0.82); }
          100% { opacity: 1; transform: scale(1); }
        }
        .mg-splash-art { animation: mgSplashArtFade 900ms cubic-bezier(.2,.8,.2,1) both; }
        .mg-splash-play-btn {
          animation: mgSplashPlayIn 320ms cubic-bezier(.2,.8,.2,1) both;
          animation-delay: 520ms;
        }
        .mg-splash-video-panel {
          animation: mgSplashVideoIn 420ms cubic-bezier(.2,.75,.2,1) both;
        }
        .mg-splash-rail::-webkit-scrollbar { height: 0; }
        .mg-splash-hero { right: calc(40% + 48px) !important; gap: 24px !important; align-items: stretch !important; }
        .mg-splash-poster { width: 210px !important; height: auto !important; min-height: 280px; }
        .mg-splash-poster > img { position: absolute; inset: 0; }
        .mg-splash-featured-copy { flex: 1; }
        .mg-splash-featured-copy h1 {
          font-size: 34px !important; line-height: 38px !important;
          white-space: normal !important; overflow-wrap: anywhere; letter-spacing: 0 !important;
        }
        .mg-splash-featured-copy > div:nth-child(3) { flex-wrap: wrap !important; gap: 8px !important; }
        .mg-splash-ranks { grid-template-columns: repeat(4, 88px) !important; flex: 0 0 auto !important; }
        .mg-splash-ranks > div { width: 88px !important; margin: 0 !important; box-sizing: border-box !important; }
        .mg-splash-quote { right: 40px !important; width: 32% !important; box-sizing: border-box; }
        .mg-splash-hero-preview { right: 56px !important; flex-wrap: wrap; }
        @container mg-viewport (max-width: 1000px) {
          .mg-splash-intro { display: flex; flex-direction: column; overflow-y: auto !important; }
          .mg-splash-hero {
            position: relative !important; left: auto !important; right: auto !important;
            bottom: auto !important; margin: 20px 20px 0; gap: 14px !important;
            align-items: stretch !important; flex-shrink: 0;
          }
          .mg-splash-poster { width: 90px !important; height: auto !important; min-height: 120px; }
          .mg-splash-featured-copy { max-width: none !important; transform: none !important; }
          .mg-splash-featured-copy h1 { font-size: 24px !important; line-height: 28px !important; }
          .mg-splash-featured-copy > div:first-child { font-size: 10px !important; letter-spacing: 0 !important; }
          .mg-splash-featured-copy > p { margin-bottom: 14px !important; }
          .mg-splash-ranks { grid-template-columns: repeat(2, 88px) !important; gap: 4px !important; }
          .mg-splash-ranks > div { padding: 6px 2px !important; }
          .mg-splash-quote {
            position: relative !important; top: auto !important; right: auto !important;
            width: auto !important; margin: 24px 20px 0; padding: 14px 18px !important; flex-shrink: 0;
          }
          .mg-splash-quote > p { font-size: 14px !important; line-height: 20px !important; letter-spacing: 0 !important; }
          .mg-splash-quote > span { font-size: 40px !important; left: 4px !important; }
          .mg-splash-quote > div { margin-top: 10px !important; }
          .mg-splash-intro > .mg-splash-rail {
            position: relative !important; bottom: auto !important; flex-shrink: 0;
            margin-top: auto; padding: 24px 20px 20px !important; min-height: 180px; box-sizing: border-box;
          }
          .mg-splash-hero-preview .mg-splash-video-panel {
            order: 3; width: 100% !important; height: auto !important; aspect-ratio: 16 / 9; margin-right: 0 !important;
          }
        }
      `}</style>

      {/* spotlight cone from the top */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1100px 720px at 50% -12%, rgba(255,255,255,0.16), transparent 62%)",
          pointerEvents: "none",
        }}
      />

      {/* legibility scrim */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.52) 45%, rgba(0,0,0,0.40) 65%, rgba(0,0,0,0.85) 100%), linear-gradient(180deg, rgba(0,0,0,0.68) 0%, rgba(0,0,0,0.22) 22%, rgba(0,0,0,0.28) 55%, rgba(0,0,0,0.95) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* hero content */}
      <div
        className={`mg-splash-hero${showVideoPreview && activeVideo ? " mg-splash-hero-preview" : ""}`}
        style={{
          position: "absolute",
          left: 56,
          bottom: 268,
          right: 56,
          display: "flex",
          gap: 30,
          alignItems: "flex-start",
          zIndex: 2,
          transition: "all 420ms cubic-bezier(.2,.75,.2,1)",
        }}
      >
        {active.imageUrl ? (
          <div
            key={`poster-wrap-${active.id}`}
            className="mg-splash-poster"
            style={{
              position: "relative",
              width: 210,
              height: 280,
              flexShrink: 0,
              transform: showVideoPreview && activeVideo ? "translateX(0)" : "translateX(0)",
              transition: "all 420ms cubic-bezier(.2,.75,.2,1)",
            }}
          >
            <img
              src={active.imageUrl}
              alt={active.name}
              className="mg-splash-art"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: 14,
                display: "block",
                boxShadow: "0 30px 60px rgba(0,0,0,0.75), 0 0 0 1px rgba(255,255,255,0.14)",
              }}
            />
            {activeVideo && (
              <button
                type="button"
                className="mg-splash-play-btn"
                aria-label={showVideoPreview ? "Pause video" : "Play video"}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowVideoPreview((isOpen) => {
                    videoOpenRef.current = !isOpen;
                    if (isOpen) setVideoExpanded(false);
                    return !isOpen;
                  });
                }}
                style={{
                  position: "absolute",
                  right: 14,
                  bottom: 12,
                  width: 40,
                  height: 40,
                  border: "1.5px solid rgba(255,255,255,0.55)",
                  borderRadius: "50%",
                  background: "rgba(20,20,20,0.62)",
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: "0 10px 20px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.06)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(6px)",
                }}
              >
                {showVideoPreview ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 4 }} aria-hidden="true">
                    <span style={{ width: 5, height: 16, borderRadius: 1.5, background: "#fff" }} />
                    <span style={{ width: 5, height: 16, borderRadius: 1.5, background: "#fff" }} />
                  </span>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 16 16" style={{ marginLeft: 2 }} aria-hidden="true">
                    <path d="M3 1.6v12.8c0 .66.73 1.06 1.29.7l10-6.4a.83.83 0 0 0 0-1.4l-10-6.4C3.73.54 3 .94 3 1.6z" fill="#fff" />
                  </svg>
                )}
              </button>
            )}
          </div>
        ) : (
          <div className="mg-splash-poster" style={{ width: 210, height: 280, borderRadius: 14, background: "linear-gradient(135deg,#202020,#090909)", flexShrink: 0 }} />
        )}

        {activeVideo && showVideoPreview && !videoExpanded && (
          <div
            className="mg-splash-video-panel"
            style={{
              position: "relative",
              width: 440,
              height: 248,
              flexShrink: 0,
              borderRadius: 18,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.16)",
              background: "#050505",
              boxShadow: "0 28px 52px rgba(0,0,0,0.56)",
              marginRight: 8,
            }}
          >
            {activeVideo.type === "file" ? (
              <video src={controlledVideoSrc} autoPlay controls playsInline style={{ width: "100%", height: "100%", display: "block", background: "#000" }} />
            ) : (
              <iframe
                title={`${active.name} video preview`}
                src={controlledVideoSrc}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{ width: "100%", height: "100%", border: 0, display: "block", background: "#000" }}
              />
            )}
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                background: "linear-gradient(180deg, rgba(0,0,0,0.05), rgba(0,0,0,0.18) 52%, rgba(0,0,0,0.08) 100%)",
              }}
            />
            <button
              type="button"
              onClick={() => setVideoExpanded(true)}
              aria-label="Expand video"
              title="Expand video"
              style={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 36,
                height: 36,
                border: "1px solid rgba(255,255,255,0.34)",
                borderRadius: 8,
                background: "rgba(0,0,0,0.68)",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                backdropFilter: "blur(8px)",
                boxShadow: "0 8px 22px rgba(0,0,0,0.45)",
              }}
            >
              <Maximize2 size={18} strokeWidth={2.2} />
            </button>
          </div>
        )}

        {activeVideo && showVideoPreview && videoExpanded && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`${active.name} expanded video`}
            onClick={() => setVideoExpanded(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 21000,
              display: "grid",
              placeItems: "center",
              padding: 40,
              background: "rgba(0,0,0,0.84)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                width: "min(1120px, calc(var(--mg-vw) * 90))",
                aspectRatio: "16 / 9",
                overflow: "hidden",
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.20)",
                background: "#000",
                boxShadow: "0 34px 100px rgba(0,0,0,0.82), 0 0 45px rgba(32,165,187,0.10)",
              }}
            >
              {activeVideo.type === "file" ? (
                <video src={controlledVideoSrc} autoPlay controls playsInline style={{ width: "100%", height: "100%", display: "block" }} />
              ) : (
                <iframe
                  title={`${active.name} expanded video`}
                  src={controlledVideoSrc}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                />
              )}
              <button
                type="button"
                onClick={() => setVideoExpanded(false)}
                aria-label="Close expanded video"
                title="Close"
                style={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  width: 38,
                  height: 38,
                  border: "1px solid rgba(255,255,255,0.34)",
                  borderRadius: 8,
                  background: "rgba(0,0,0,0.72)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  backdropFilter: "blur(8px)",
                }}
              >
                <CloseIcon size={20} strokeWidth={2.4} />
              </button>
            </div>
          </div>
        )}

        <div
          className="mg-splash-featured-copy"
          style={{
            minWidth: 0,
            maxWidth: showVideoPreview && activeVideo ? 620 : 760,
            paddingBottom: 6,
            transform: showVideoPreview && activeVideo ? "translateX(12px)" : "translateX(0)",
            transition: "all 420ms cubic-bezier(.2,.75,.2,1)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              color: "#20a5bb",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            #{activeIdx + 1} Top mindSHARE Growth
          </div>

          <h1
            style={{
              color: "#fff",
              fontSize: 44,
              lineHeight: "48px",
              fontWeight: 950,
              letterSpacing: "-0.02em",
              margin: "0 0 12px 0",
              textShadow: "0 4px 24px rgba(0,0,0,0.75)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {active.name}
          </h1>

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14, flexWrap: "nowrap", overflow: "hidden" }}>
            <span style={{ color: "#fff", fontWeight: 950, fontSize: 20, flexShrink: 0 }}>{pct3(active.mindshare)}</span>
            <span style={{ color: "rgba(207,216,227,0.7)", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>mindSHARE</span>

            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                height: 26,
                padding: "0 10px",
                borderRadius: 999,
                background: `${growthColor}22`,
                border: `1px solid ${growthColor}`,
                color: growthColor,
                fontWeight: 950,
                fontSize: 13,
                flexShrink: 0,
                whiteSpace: "nowrap",
              }}
            >
              {active.growthPT >= 0 ? "▲" : "▼"} {Math.round(Math.abs(active.growthPT) * 100)}% growth
            </span>

            {(active.publisher || active.developer) && (
              <span
                style={{
                  color: "rgba(207,216,227,0.65)",
                  fontSize: 12,
                  fontWeight: 700,
                  minWidth: 0,
                  flexShrink: 1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {active.publisher}
                {active.publisher && active.developer ? " · " : ""}
                {active.developer}
              </span>
            )}
          </div>

          <p
            style={{
              color: "rgba(230,232,236,0.86)",
              fontSize: 14,
              lineHeight: "20px",
              minHeight: 60,
              margin: "0 0 36px 0",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textShadow: "0 2px 8px rgba(0,0,0,0.7)",
            }}
          >
            {active.description || ""}
          </p>

          <div style={{ display: "flex", alignItems: "stretch", gap: 12 }}>
            <div className="mg-splash-ranks" style={{ display: "grid", gridTemplateColumns: "repeat(4, auto)", gap: 8, flex: "0 0 auto" }}>
              {rankRows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    width: "8ch",
                    margin: "0 auto",
                    boxSizing: "content-box",
                    border: "1px solid rgba(255,255,255,0.22)",
                    borderRadius: 9,
                    background: "rgba(0,0,0,0.20)",
                    padding: "6px 5px",
                    textAlign: "center",
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <img src={row.icon} alt={row.alt} style={{ width: 14, height: 14, objectFit: "contain", marginBottom: 3 }} />
                  <div style={{ color: "#fff", fontSize: 14, fontWeight: 950, lineHeight: "16px", fontVariantNumeric: "tabular-nums" }}>
                    {fmtRank(row.rank)}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>

      {active.writeUp && !(showVideoPreview && activeVideo && !videoExpanded) && (
        <div
          className="mg-splash-quote"
          style={{
            position: "absolute",
            top: 172,
            right: 140,
            width: "min(560px, calc(var(--mg-vw) * 42))",
            padding: "28px 34px 26px 38px",
            borderRadius: 16,
            background: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.16)",
            backdropFilter: "blur(8px)",
            boxShadow: "0 24px 54px rgba(0,0,0,0.4)",
            zIndex: 2,
          }}
        >
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: 4,
              left: 18,
              fontSize: 74,
              lineHeight: 1,
              fontFamily: "Georgia, 'Times New Roman', serif",
              color: "rgba(32,165,187,0.45)",
              pointerEvents: "none",
            }}
          >
            "
          </span>
          <p
            style={{
              position: "relative",
              margin: 0,
              color: "#fff",
              fontSize: 26,
              lineHeight: "33px",
              fontWeight: 850,
              fontStyle: "italic",
              letterSpacing: "-0.01em",
              textShadow: "0 3px 14px rgba(0,0,0,0.65)",
            }}
          >
            {active.writeUp}
          </p>
          <div style={{ marginTop: 18, marginLeft: "auto", width: 38, height: 3, borderRadius: 2, background: "#20a5bb" }} />
        </div>
      )}

      {/* thumbnail rail */}
      <div
        className="mg-splash-rail"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 28,
          display: "flex",
          alignItems: "flex-end",
          gap: 14,
          padding: "44px 56px 40px",
          overflowX: "auto",
          overflowY: "visible",
          zIndex: 2,
        }}
      >
        {games.map((g, i) => {
          const isActive = i === activeIdx;
          return (
            <button
              key={g.id ?? g.name}
              type="button"
              onMouseEnter={() => setActiveIdx(i)}
              title={g.name}
              style={{
                position: "relative",
                flexShrink: 0,
                width: isActive ? 118 : 88,
                height: isActive ? 158 : 118,
                borderRadius: 10,
                overflow: "hidden",
                border: "1px solid rgba(255,255,255,0.16)",
                boxShadow: isActive
                  ? "0 0 22px 5px rgba(32,165,187,0.55), 0 14px 30px rgba(0,0,0,0.45)"
                  : "0 6px 14px rgba(0,0,0,0.4)",
                transform: isActive ? "translateY(-6px)" : "translateY(0)",
                opacity: isActive ? 1 : 0.55,
                transition: "all 260ms cubic-bezier(.2,.8,.2,1)",
                cursor: "pointer",
                padding: 0,
                background: "#111",
                alignSelf: "flex-end",
              }}
            >
              {g.imageUrl ? (
                <img
                  src={g.imageUrl}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              ) : null}
              <span
                style={{
                  position: "absolute",
                  top: 4,
                  left: 6,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 950,
                  textShadow: "0 1px 4px rgba(0,0,0,0.95)",
                }}
              >
                #{i + 1}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          className="mg-splash-continue"
          onClick={() => onEnter?.()}
          style={{
            position: "relative",
            zIndex: 3,
            flexShrink: 0,
            alignSelf: "flex-end",
            height: 44,
            padding: "0 18px",
            borderRadius: 8,
            border: "1px solid #fff",
            background: "#fff",
            color: "#000",
            fontWeight: 850,
            fontSize: 13,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          Continue
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

// Shown instantly while the splash's game data loads, so the cards/skeleton
// dashboard never flashes underneath before the intro is ready.
function MindshareSplashSkeleton({ onEnter }) {
  const headerOffset = useMindshareSplashHeaderOffset();
  const shimmerBlock = (extra) => ({
    borderRadius: 8,
    background:
      "linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.11) 37%, rgba(255,255,255,0.05) 63%)",
    backgroundSize: "400% 100%",
    animation: "mgSplashShimmer 1.6s ease-in-out infinite",
    ...extra,
  });

  return (
    <div
      style={{
        position: "fixed",
        top: headerOffset,
        left: 0,
        right: 0,
        height: `calc(calc(var(--mg-vh) * 100) - ${headerOffset}px)`,
        zIndex: 7999,
        background: "#000",
        overflow: "hidden",
      }}
    >
      <style>{`
        @keyframes mgSplashShimmer {
          0% { background-position: 100% 0; }
          100% { background-position: 0 0; }
        }
        @keyframes mgSplashSkeletonPulse {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 0.85; }
        }
      `}</style>

      {/* spotlight cone, same as the real hero for a seamless swap */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(1100px 720px at 50% -12%, rgba(255,255,255,0.10), transparent 62%)",
          pointerEvents: "none",
        }}
      />

      {/* hero placeholder */}
      <div
        style={{
          position: "absolute",
          left: 56,
          bottom: 268,
          right: 56,
          display: "flex",
          gap: 30,
          alignItems: "flex-end",
          zIndex: 2,
        }}
      >
        <div style={{ ...shimmerBlock({ width: 210, height: 280, borderRadius: 14, flexShrink: 0 }), animationDelay: "0.1s" }} />

        <div style={{ minWidth: 0, maxWidth: 640, width: "100%", paddingBottom: 6 }}>
          <div style={shimmerBlock({ width: 180, height: 12, marginBottom: 14 })} />
          <div style={shimmerBlock({ width: "70%", height: 40, marginBottom: 16 })} />
          <div style={{ display: "flex", gap: 14, marginBottom: 18 }}>
            <div style={shimmerBlock({ width: 90, height: 22 })} />
            <div style={shimmerBlock({ width: 130, height: 22, borderRadius: 999 })} />
            <div style={shimmerBlock({ width: 160, height: 22 })} />
          </div>
          <div style={shimmerBlock({ width: "95%", height: 14, marginBottom: 8 })} />
          <div style={shimmerBlock({ width: "88%", height: 14, marginBottom: 8 })} />
          <div style={shimmerBlock({ width: "60%", height: 14, marginBottom: 20 })} />
          <div style={{ ...shimmerBlock({ width: 172, height: 44, marginBottom: 20 }), animationDelay: "0.2s" }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 8, maxWidth: 420 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} style={{ ...shimmerBlock({ height: 46, borderRadius: 9 }), animationDelay: `${0.05 * i}s` }} />
            ))}
          </div>
        </div>
      </div>

      {/* thumbnail rail placeholder */}
      <div
        className="mg-splash-rail"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 28,
          display: "flex",
          alignItems: "flex-end",
          gap: 14,
          padding: "44px 56px 40px",
          overflowX: "auto",
          overflowY: "visible",
          zIndex: 2,
        }}
      >
        {Array.from({ length: 10 }).map((_, i) => (
          <div
            key={i}
            style={{
              ...shimmerBlock({
                width: i === 0 ? 118 : 88,
                height: i === 0 ? 158 : 118,
                borderRadius: 10,
                flexShrink: 0,
              }),
              animationDelay: `${0.04 * i}s`,
              opacity: i === 0 ? 1 : 0.55,
            }}
          />
        ))}
        <button
          type="button"
          className="mg-splash-continue"
          onClick={() => onEnter?.()}
          style={{
            position: "relative",
            zIndex: 3,
            flexShrink: 0,
            alignSelf: "flex-end",
            height: 44,
            padding: "0 18px",
            borderRadius: 8,
            border: "1px solid #fff",
            background: "#fff",
            color: "#000",
            fontWeight: 850,
            fontSize: 13,
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          Continue
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

const releaseCalendarTier = (cumulativePct) => {
  if (cumulativePct > 3) return { label: "XL", width: 164, height: 218 };
  if (cumulativePct >= 1) return { label: "Large", width: 142, height: 189 };
  if (cumulativePct >= 0.2) return { label: "Medium", width: 120, height: 160 };
  if (cumulativePct >= 0.01) return { label: "Small", width: 98, height: 131 };
  return { label: "XS", width: 78, height: 104 };
};

function MindshareReleaseCalendar({ games }) {
  const CALENDAR_PAGE_WEEKS = 9;
  const railRef = useRef(null);
  const currentWeekRef = useRef(null);
  const [pageOffset, setPageOffset] = useState(0);
  const [scrolledAway, setScrolledAway] = useState(false);
  const [hoveredRelease, setHoveredRelease] = useState(null);

  const { weeks, currentWeekKey } = useMemo(() => {
    const startOfWeek = (value) => {
      const raw = String(value || "").trim();
      const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      const local = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      const date = iso
        ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]), 12)
        : local
          ? new Date(Number(local[3]), Number(local[1]) - 1, Number(local[2]), 12)
          : new Date(value);
      if (isNaN(date)) return null;
      date.setHours(12, 0, 0, 0);
      const mondayOffset = (date.getDay() + 6) % 7;
      date.setDate(date.getDate() - mondayOffset);
      return date;
    };
    const weekKey = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const nowWeek = startOfWeek(new Date());
    const nowKey = weekKey(nowWeek);
    const buckets = new Map([[nowKey, { key: nowKey, start: nowWeek, games: [] }]]);

    for (const game of games || []) {
      if (!game.release_date) continue;
      const start = startOfWeek(game.release_date);
      if (!start) continue;

      let cumulative = 0;
      let periods = 0;
      for (const value of game._byDate?.values?.() || []) {
        const numeric = Number(value);
        if (!Number.isFinite(numeric)) continue;
        cumulative += numeric;
        periods += 1;
      }
      const cumulativePct = cumulative * 100;
      const key = weekKey(start);
      if (!buckets.has(key)) buckets.set(key, { key, start, games: [] });
      buckets.get(key).games.push({ game, cumulativePct, periods });
    }

    const sortedWeeks = Array.from(buckets.values())
      .map((bucket) => ({
        ...bucket,
        games: bucket.games.sort((a, b) =>
          b.cumulativePct - a.cumulativePct || a.game.name.localeCompare(b.game.name)
        ),
      }))
      .sort((a, b) => a.start - b.start);

    return { weeks: sortedWeeks, currentWeekKey: nowKey };
  }, [games]);

  const visibleWeeks = useMemo(() => {
    const bucketMap = new Map(weeks.map((week) => [week.key, week]));
    const current = new Date(`${currentWeekKey}T12:00:00`);
    current.setDate(current.getDate() + pageOffset * CALENDAR_PAGE_WEEKS * 7);

    return Array.from({ length: CALENDAR_PAGE_WEEKS }, (_, index) => {
      const start = new Date(current);
      start.setDate(start.getDate() + index * 7);
      const year = start.getFullYear();
      const month = String(start.getMonth() + 1).padStart(2, "0");
      const day = String(start.getDate()).padStart(2, "0");
      const key = `${year}-${month}-${day}`;
      return bucketMap.get(key) || { key, start, games: [] };
    });
  }, [weeks, currentWeekKey, pageOffset]);

  useEffect(() => {
    const rail = railRef.current;
    const current = currentWeekRef.current;
    if (!rail) return;
    rail.scrollLeft = current ? Math.max(0, current.offsetLeft - 18) : 0;
  }, [visibleWeeks, currentWeekKey]);

  const formatWeek = (start) => {
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })} - ${end.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
  };

  return (
    <section style={{ width: "100%", maxWidth: "calc(calc(var(--mg-vw) * 100) - 60px)", minWidth: 0, minHeight: "calc(calc(var(--mg-vh) * 100) - 155px)", color: "#fff" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 18, marginBottom: 14 }}>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 28, lineHeight: "32px", fontWeight: 950, letterSpacing: 0 }}>Release Calendar</h2>
          </div>
          <div style={{ color: "#6f7d88", fontSize: 11, fontWeight: 750, marginTop: 5 }}>
            Weekly new releases ranked by cumulative mindSHARE
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", whiteSpace: "nowrap" }}>
          <button type="button" onClick={() => { setHoveredRelease(null); setPageOffset((offset) => offset - 1); }} style={{ height: 30, padding: "0 10px", borderRadius: 7, border: "1px solid #35414a", background: "#0b0e10", color: "#d9e4eb", cursor: "pointer", fontSize: 11, fontWeight: 850 }}>← Earlier weeks</button>
          {(pageOffset !== 0 || scrolledAway) && <button type="button" onClick={() => { setHoveredRelease(null); setPageOffset(0); setScrolledAway(false); railRef.current?.scrollTo({ left: 0 }); }} style={{ height: 30, padding: "0 10px", borderRadius: 7, border: "1px solid rgba(32,165,187,0.50)", background: "rgba(32,165,187,0.10)", color: "#20a5bb", cursor: "pointer", fontSize: 11, fontWeight: 900 }}>This Week</button>}
          <button type="button" onClick={() => { setHoveredRelease(null); setPageOffset((offset) => offset + 1); }} style={{ height: 30, padding: "0 10px", borderRadius: 7, border: "1px solid #35414a", background: "#0b0e10", color: "#d9e4eb", cursor: "pointer", fontSize: 11, fontWeight: 850 }}>Later weeks →</button>
        </div>
      </div>

      <div
        ref={railRef}
        data-release-calendar-rail
        onScroll={(event) => setScrolledAway(event.currentTarget.scrollLeft > 8)}
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${CALENDAR_PAGE_WEEKS}, minmax(120px, 1fr))`,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          gap: 8,
          alignItems: "flex-start",
          overflowX: "auto",
          overflowY: "hidden",
          padding: "0 2px 20px",
        }}
      >
        {visibleWeeks.map((week) => {
          const isCurrent = week.key === currentWeekKey;
          return (
            <div
              key={week.key}
              ref={isCurrent ? currentWeekRef : null}
              style={{
                minWidth: 0,
                minHeight: 520,
                padding: "0 6px 14px",
                borderLeft: isCurrent ? "2px solid rgba(32,165,187,0.72)" : "1px solid rgba(255,255,255,0.08)",
                background: isCurrent
                  ? "linear-gradient(180deg, rgba(32,165,187,0.10), rgba(32,165,187,0.015) 32%, transparent 75%)"
                  : "linear-gradient(180deg, rgba(255,255,255,0.025), transparent 34%)",
              }}
            >
              <div style={{ position: "sticky", top: 0, zIndex: 2, padding: "10px 2px 12px", background: "linear-gradient(180deg, #050607 70%, transparent)" }}>
                <div style={{ color: isCurrent ? "#6fc2ff" : "#dce4ea", fontSize: 12, fontWeight: 950, letterSpacing: 0 }}>
                  {isCurrent ? "THIS WEEK" : formatWeek(week.start)}
                </div>
                <div style={{ color: "#66747f", fontSize: 10, fontWeight: 750, marginTop: 3 }}>
                  {isCurrent ? formatWeek(week.start) : `${week.games.length} release${week.games.length === 1 ? "" : "s"}`}
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                {week.games.length ? week.games.map(({ game, cumulativePct, periods }, index) => {
                  const tier = releaseCalendarTier(cumulativePct);
                  return (
                    <article
                      key={`${week.key}-${game.name}`}
                      aria-label={`${game.name}, ${cumulativePct.toFixed(3)} cumulative mindSHARE points, ${tier.label} card`}
                      data-cumulative-mindshare={cumulativePct.toFixed(6)}
                      data-size-tier={tier.label}
                      onMouseEnter={(event) => {
                        const rect = layoutRect(event.currentTarget);
                        setHoveredRelease({ game, cumulativePct, periods, x: rect.right + 12, y: rect.top });
                      }}
                      onMouseMove={(event) => {
                        const rect = layoutRect(event.currentTarget);
                        setHoveredRelease((current) => current?.game === game ? { ...current, x: rect.right + 12, y: rect.top } : current);
                      }}
                      onMouseLeave={() => setHoveredRelease(null)}
                      style={{
                        position: "relative",
                        width: tier.width,
                        maxWidth: "100%",
                        aspectRatio: `${tier.width} / ${tier.height}`,
                        flexShrink: 0,
                        overflow: "hidden",
                        borderRadius: 7,
                        border: "1px solid rgba(255,255,255,0.13)",
                        background: "#111",
                        boxShadow: index === 0
                          ? "0 12px 26px rgba(0,0,0,0.46), 0 0 20px rgba(32,165,187,0.09)"
                          : "0 8px 18px rgba(0,0,0,0.40)",
                      }}
                    >
                      {game.imageUrl ? (
                        <img src={game.imageUrl} alt={game.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      ) : (
                        <div style={{ height: "100%", display: "grid", placeItems: "center", padding: 8, color: "#aeb8c0", textAlign: "center", fontSize: 11, fontWeight: 850 }}>{game.name}</div>
                      )}
                      <div style={{ position: "absolute", inset: "auto 0 0", padding: tier.width < 90 ? "18px 5px 5px" : "26px 7px 7px", background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.92))" }}>
                        <div style={{ color: "#fff", fontSize: tier.width < 90 ? 9 : 11, lineHeight: tier.width < 90 ? "11px" : "13px", fontWeight: 900, overflow: "hidden", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 2 }}>{game.name}</div>
                        {tier.width >= 98 && <div style={{ color: "#9ed5fb", fontSize: 9, fontWeight: 850, marginTop: 3 }}>{cumulativePct.toFixed(2)} pts</div>}
                      </div>
                    </article>
                  );
                }) : (
                  <div style={{ marginTop: 44, color: "#52606a", fontSize: 11, fontWeight: 800, textAlign: "center" }}>No releases</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {hoveredRelease && (() => {
        const { game, cumulativePct, periods } = hoveredRelease;
        const panelWidth = 370;
        const left = Math.min(hoveredRelease.x, layoutViewportWidth() - panelWidth - 18);
        const top = Math.min(Math.max(84, hoveredRelease.y), layoutViewportHeight() - 500);
        const ranks = [
          { icon: MINDGAME_TREE_ICON, alt: "mindGAME", value: game.rank_mindshare },
          { icon: GOOGLE_ICON, alt: "Google Search", value: game.search_rank },
          { icon: YT_ICON, alt: "YouTube", value: game.video_rank },
          { icon: TWITCH_ICON, alt: "Twitch", value: game.twitch_rank },
        ];
        return (
          <aside
            style={{
              position: "fixed",
              left,
              top,
              width: panelWidth,
              zIndex: 12000,
              overflow: "hidden",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.16)",
              background: "linear-gradient(180deg, rgba(18,21,24,0.98), rgba(5,7,9,0.99))",
              boxShadow: "0 24px 70px rgba(0,0,0,0.80), 0 0 28px rgba(32,165,187,0.10)",
              pointerEvents: "none",
            }}
          >
            <div style={{ position: "relative", height: 154, overflow: "hidden", background: "#101214" }}>
              {game.imageUrl && <img src={game.imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 24%", filter: "saturate(1.05) brightness(0.72)" }} />}
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, transparent 26%, rgba(5,7,9,0.96) 100%)" }} />
              <div style={{ position: "absolute", left: 14, right: 14, bottom: 11 }}>
                <div style={{ color: "#fff", fontSize: 20, lineHeight: "23px", fontWeight: 950, letterSpacing: 0 }}>{game.name}</div>
                <div style={{ color: "#20a5bb", fontSize: 12, fontWeight: 900, marginTop: 4 }}>{cumulativePct.toFixed(3)} cumulative mindSHARE points</div>
              </div>
            </div>
            <div style={{ padding: "10px 14px 14px" }}>
              <div style={{ display: "flex", gap: 8, color: "#aab8c2", fontSize: 11, fontWeight: 800, marginBottom: 9 }}>
                <span>Release {fmtMDY(game.release_date)}</span>
                <span>·</span>
                <span>{periods} period{periods === 1 ? "" : "s"}</span>
                <span>·</span>
                <span>{releaseCalendarTier(cumulativePct).label}</span>
              </div>
              {(game.publisher || game.developer) && <div style={{ color: "#d4dce2", fontSize: 11, lineHeight: "16px", fontWeight: 800, marginBottom: 8 }}>{game.publisher}{game.publisher && game.developer ? " · " : ""}{game.developer}</div>}
              {game.description && <div style={{ color: "rgba(220,226,231,0.76)", fontSize: 11, lineHeight: "16px", display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 4, overflow: "hidden", marginBottom: 11 }}>{game.description}</div>}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 7 }}>
                {ranks.map((rank) => (
                  <div key={rank.alt} style={{ height: 44, borderRadius: 7, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.035)", display: "grid", placeItems: "center", alignContent: "center", gap: 2 }}>
                    <img src={rank.icon} alt={rank.alt} style={{ width: 14, height: 14, objectFit: "contain" }} />
                    <div style={{ color: "#fff", fontSize: 11, fontWeight: 950 }}>{Number.isFinite(Number(rank.value)) ? `#${Number(rank.value)}` : "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        );
      })()}
    </section>
  );
}

/* =============================== PAGE =============================== */
export default function MindSHARE() {
  const location = useLocation();
  const { currentUser } = useAuth();
  // Signed-in users without the mind-share permission get a locked public
  // preview: Top Growth + the top 3 ranked games, with everything else gated.
  // "?preview=public" forces the preview for any user (testing/demos); it only
  // ever restricts the view, never grants access.
  const forcePublicPreview = new URLSearchParams(location.search).get("preview") === "public";
  const isPublicPreview = forcePublicPreview || (Boolean(currentUser) && !currentUser.is_admin &&
    !(Array.isArray(currentUser.permissions) && currentUser.permissions.includes("mind-share")));
  const editorial = isMindshareEditorial(location);
  const legacyData = new URLSearchParams(location.search).get("mindshareData") === "legacy";
  const [snapshotRows, setSnapshotRows] = useState(null);
  const [dataError, setDataError] = useState(null);
  const [dataRetry, setDataRetry] = useState(0);
  const snapshotGames = useMemo(() => snapshotRows ? timeMindshare("model", () => hydrateSnapshot(snapshotRows)) : [], [snapshotRows]);
  const embeddedMetadata = useMemo(() => snapshotRows ? timeMindshare("embedded-metadata", () => snapshotMetadata(snapshotRows)) : [], [snapshotRows]);
  const {
  metaData: sharedMetadata,
  tikTokData,
  getAllTags,
  getAllPublishers,
  getAllGenres,
  getAllDevelopers,
  getAllLiveServiceOptions,
  getAllPricingOptions,
  getAllUGCOptions,
  getAllFocusOptions,
  getAllPlatformGroups,
  getAllReleaseDates,
  allTags,
  allPublishers,
  allGenres,
  allDevelopers,
  allLiveService,
  allPricing,
  allUGC,
  allFocus,
  allPlatforms,
  releaseDateRange: sharedReleaseDateRange,
  loading: metaLoading,
} = useTikTokData();
  const metaData = legacyData ? sharedMetadata : embeddedMetadata;
  const releaseDateRange = useMemo(() => {
    if (legacyData) return sharedReleaseDateRange;
    return snapshotRows ? timeMindshare("release-range", () => snapshotReleaseDateRange(snapshotRows)) : [null, null];
  }, [legacyData, sharedReleaseDateRange, snapshotRows]);

  useEffect(() => {
    if (legacyData) return;
    let cancelled = false;
    setDataError(null);
    loadSnapshot().then(rows => {
      if (!cancelled) setSnapshotRows(rows);
    }).catch(error => {
      console.error("mindSHARE snapshot load failed:", error);
      if (!cancelled) setDataError({ source: "snapshot", message: "Unable to load mindSHARE cards. Please retry." });
    });
    return () => { cancelled = true; };
  }, [legacyData, dataRetry]);

  useEffect(() => {
    if (!legacyData) return;
    getAllTags();
    getAllPublishers();
    getAllGenres();
    getAllDevelopers();
    getAllLiveServiceOptions();
    getAllPricingOptions();
    getAllUGCOptions();
    getAllFocusOptions();
    getAllPlatformGroups();
    getAllReleaseDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacyData]);


  /* ---------------- Filters & page state ---------------- */
  const [searchTerm, setSearchTerm] = useState("");
  const [cardSearchOpen, setCardSearchOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedDevelopers, setSelectedDevelopers] = useState([]);
  const [selectedLiveService, setSelectedLiveService] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [selectedUGC, setSelectedUGC] = useState(null);
  const [selectedFocus, setSelectedFocus] = useState([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);

  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [onlyUnreleased, setOnlyUnreleased] = useState(false);

  const [comparisonGames, setComparisonGames] = useState([]);

  const [showTopGrowth, setShowTopGrowth] = useState(true);
  const [headerToolsTarget, setHeaderToolsTarget] = useState(null);
  useEffect(() => {
    const findTarget = () => setHeaderToolsTarget(document.getElementById("mindshare-header-saved-controls"));
    findTarget();
    const observer = new MutationObserver(findTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  // Lightweight Top 10 data for the Top Growth card-view section.
  const [splashGames, setSplashGames] = useState([]);
  const [splashLoading, setSplashLoading] = useState(true);
  const [splashPeriodLabel, setSplashPeriodLabel] = useState("");

  const [viewMode, setViewMode] = useState("trended"); // "trended" | "launches" | "calendar" | "market" | "business"

  const [businessDimension, setBusinessDimension] = useState("publishers");
  const [businessWindow, setBusinessWindow] = useState("52w");
  const [businessRollup, setBusinessRollup] = useState(null);
  const [businessIndex, setBusinessIndex] = useState(null);
  const [businessExternalSeries, setBusinessExternalSeries] = useState({});
  const [businessSeries, setBusinessSeries] = useState([]);
  const [businessQuery, setBusinessQuery] = useState("");
  const [businessLoading, setBusinessLoading] = useState(false);
  const [businessSeriesLoading, setBusinessSeriesLoading] = useState(null);

  const [mindshareXAxisMode, setMindshareXAxisMode] = useState("date"); // 'date' | 'w2l'
  const [mindshareAgg, setMindshareAgg] = useState("weekly"); // 'weekly' | 'cumulative'
  const [chartBgChoice, setChartBgChoice] = useState("none"); // 'none' | <game name>
  const chartExportRef = useRef(null);
  const chartViewportWidthRef = useRef(layoutViewportWidth());
  const mindshareChartHeightUserAdjustedRef = useRef(false);
  const [mindshareChartHeight, setMindshareChartHeight] = useState(getMindshareDefaultChartHeight);
  const handleTopGrowthSlotHeight = useCallback((height) => {
    if (!mindshareChartHeightUserAdjustedRef.current) {
      setMindshareChartHeight(Math.max(340, height - CHART_PANEL_NONPLOT_HEIGHT));
    }
  }, []);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsSeries, setAnalyticsSeries] = useState({
    average: false,
    median: false,
    top5: false,
    top10: false,
    top25: false,
  });
  const showAverage = analyticsSeries.average; // saved-view/back-compat alias
  const [showAllGames, setShowAllGames] = useState(false); // Trended: include all filtered games
  const [launchesShowAll, setLaunchesShowAll] = useState(false); // Launches: top 100 by default, optional all
  const [launchChartPreset, setLaunchChartPreset] = useState("custom"); // "upcoming" | "recent" | "custom"
  // Keep manual series edits separate so exported preset membership stays authoritative.
  const [favoriteSelectionEdited, setFavoriteSelectionEdited] = useState(false);
  const [chartBooting, setChartBooting] = useState(false);

  const [loading, setLoading] = useState(false);
  const [hiddenCards, setHiddenCards] = useState([]);

// Hover preview passed into MarketView (for dropdown highlighting)
  const [preview, setPreview] = useState(null); // { type, value } | null
  const [marketFavorites, setMarketFavorites] = useState([]);
  const favoriteHighlights = useMemo(() => marketFavorites.map(value => ({ type: "favorite", value })), [marketFavorites]);
  const isMarketHighlighted = (type, value) =>
    Array.isArray(preview) && preview.some((item) => item.type === type && item.value === value);
  const toggleMarketHighlight = (type, value) => {
    setPreview((previous) => {
      const items = Array.isArray(previous) ? previous : previous ? [previous] : [];
      return items.some((item) => item.type === type && item.value === value)
        ? items.filter((item) => item.type !== type || item.value !== value)
        : [...items, { type, value }];
    });
  };

  // Default to Cards + Charts: show the chart panel on load
const [showInsights, setShowInsights] = useState(false);
const [showChartJump, setShowChartJump] = useState(false);
const [w2lInitialWindow, setW2lInitialWindow] = useState({
  start: -52,
  end: 0,
});
const [showGuidedOverlay, setShowGuidedOverlay] = useState(false);

useEffect(() => {
  const updateDefaultHeight = () => {
    if (chartViewportWidthRef.current === layoutViewportWidth()) return;
    chartViewportWidthRef.current = layoutViewportWidth();
    if (!mindshareChartHeightUserAdjustedRef.current) {
      setMindshareChartHeight(getMindshareDefaultChartHeight());
    }
  };
  window.addEventListener("resize", updateDefaultHeight);
  return () => window.removeEventListener("resize", updateDefaultHeight);
}, []);

useEffect(() => {
  if (viewMode !== "trended" || !showInsights || !comparisonGames.length) {
    setShowChartJump(false);
    return undefined;
  }

  const panel = chartExportRef.current;
  if (!panel) return undefined;

  const updateChartJump = () => {
    const rect = layoutRect(panel);
    const visible = rect.bottom > 72 && rect.top < layoutViewportHeight() - 72;
    setShowChartJump(!visible);
  };

  updateChartJump();
  window.addEventListener("scroll", updateChartJump, { passive: true });
  window.addEventListener("resize", updateChartJump);
  return () => {
    window.removeEventListener("scroll", updateChartJump);
    window.removeEventListener("resize", updateChartJump);
  };
}, [viewMode, showInsights, comparisonGames.length, chartBooting, mindshareChartHeight]);

// Market Mind sort mode (default = Growth %)
const [marketSort, setMarketSort] = useState("growth"); // "growth" | "mindshare"
const [marketViewType, setMarketViewType] = useState("squares");
const [marketSplitView, setMarketSplitView] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarContentVisible, setSidebarContentVisible] = useState(true);
  const [sidebarButtonVisible, setSidebarButtonVisible] = useState(true);

  // 🔹 Top filter popover state (same pattern as Steam/Twitch)
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterCloseTimerRef = useRef(null);
  // Filters/search/saved views are disabled in the public preview.
  useEffect(() => {
    if (isPublicPreview) setFiltersOpen(false);
  }, [isPublicPreview]);

  // Portaled react-select menus stay open while their input keeps focus, so
  // drop focus from the panel whenever it closes.
  useEffect(() => {
    if (filtersOpen) return;
    const active = document.activeElement;
    const panel = document.querySelector(".mg-mindshare-filter-popover");
    if (active && panel && panel.contains(active) && typeof active.blur === "function") {
      active.blur();
    }
  }, [filtersOpen]);

  useEffect(() => {
    let contentTimer;
    let buttonTimer;
    setSidebarButtonVisible(false);

    if (sidebarCollapsed) {
      setSidebarContentVisible(false);
      buttonTimer = window.setTimeout(() => setSidebarButtonVisible(true), 420);
    } else {
      contentTimer = window.setTimeout(() => setSidebarContentVisible(true), 210);
      buttonTimer = window.setTimeout(() => setSidebarButtonVisible(true), 520);
    }

    return () => {
      if (contentTimer) window.clearTimeout(contentTimer);
      if (buttonTimer) window.clearTimeout(buttonTimer);
    };
  }, [sidebarCollapsed]);

  const handleMindshareChartHeightResizeStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const panel = chartExportRef.current;
    if (!panel) return;
    mindshareChartHeightUserAdjustedRef.current = true;

    const rect = layoutRect(panel);
    const startY = layoutCoordinate(e.clientY);
    const startHeight = rect.height || mindshareChartHeight;
    const minHeight = viewMode === "business" ? 420 : 340;
    const bottomPad = 12;

    const onMove = (moveEvent) => {
      // Shared clamp for all resizable chart panels: the resize state stops at
      // the bottom of the visible browser window instead of continuing off-screen.
      const maxHeight = Math.max(minHeight, layoutViewportHeight() - rect.top - bottomPad);
      const next = startHeight + (layoutCoordinate(moveEvent.clientY) - startY);
      setMindshareChartHeight(Math.max(minHeight, Math.min(maxHeight, next)));
    };

    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "ns-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const chartTargetsRef = useRef([]);
  const favoriteChartActive = !legacyData && (
    (viewMode === "trended" && (
      launchChartPreset === "top100" ? !showAllGames : ["recent", "upcoming"].includes(launchChartPreset)
    )) ||
    (viewMode === "launches" && ["recent", "upcoming"].includes(launchChartPreset))
  );
  const handleToggleChartGame = useCallback((game) => {
    if (isPublicPreview || !game?.name) return;
    if (mindshareXAxisMode === "w2l" || (viewMode === "trended" && mindshareXAxisMode === "date")) mindWindowUserAdjustedRef.current = false;
    const currentSelection = favoriteChartActive && !favoriteSelectionEdited && launchChartPreset !== "top100"
      ? chartTargetsRef.current
      : comparisonGames;
    const exists = currentSelection.includes(game.name);
    const nextComparisonGames = exists
      ? currentSelection.filter((name) => name !== game.name)
      : [...currentSelection, game.name];
    setComparisonGames(nextComparisonGames);
    if (favoriteChartActive) {
      setFavoriteSelectionEdited(true);
      setShowInsights(true);
    } else {
      if (viewMode === "launches") setLaunchChartPreset("custom");
      setShowInsights(viewMode === "trended" ? nextComparisonGames.length > 0 : true);
    }
  }, [favoriteChartActive, mindshareXAxisMode, viewMode, launchChartPreset, favoriteSelectionEdited, comparisonGames]);

  // --- Market Mind layout (full-screen bubble view like Twitch) ---
  const mainRef = useRef(null);
  const headerRef = useRef(null);
  const [marketDims, setMarketDims] = useState({ width: 1200, height: 720 });

// Separate period selector just for Market Mind (independent of Trended slider)
const [marketPeriod, setMarketPeriod] = useState("1w");

// Gate the heavy bubble render behind a frame so the skeleton actually paints
// first when Market Mind is opened (avoids the freeze-on-open feeling).
const [marketReady, setMarketReady] = useState(false);
useEffect(() => {
  if (viewMode !== "market") {
    setMarketReady(false);
    return;
  }
  setMarketReady(false);
  let raf2 = 0;
  const raf1 = requestAnimationFrame(() => {
    raf2 = requestAnimationFrame(() => setMarketReady(true));
  });
  return () => {
    cancelAnimationFrame(raf1);
    if (raf2) cancelAnimationFrame(raf2);
  };
}, [viewMode]);


  // Compute available bubble area under the header — reactive to layout changes
  // (filter popover, header wrap, window resize), not just window resize.
  useEffect(() => {
    if (viewMode !== "market") return;

    const updateDims = () => {
      const mainEl = mainRef.current;
      if (!mainEl) return;
      const rect = layoutRect(mainEl);
      const headerEl = headerRef.current;
      const headerH = headerEl
        ? layoutRect(headerEl).height
        : 0;

      // Give Market Mind the whole main panel (full-bleed like Steam/Twitch).
      const w = Math.max(320, rect.width);
      const h = Math.max(320, rect.height - headerH);

      setMarketDims({ width: w, height: h });
    };

    updateDims();
    const ro = new ResizeObserver(updateDims);
    if (mainRef.current) ro.observe(mainRef.current);
    if (headerRef.current) ro.observe(headerRef.current);
    window.addEventListener("resize", updateDims);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", updateDims);
    };
  }, [viewMode, marketViewType]);

  // Lock page scroll when Market Mind is active (full-screen bubbles)
  useEffect(() => {
    const lock = viewMode === "market";
    const { body, documentElement } = document;
    if (lock) {
      body.style.overflow = "hidden";
      documentElement.style.overflow = "hidden";
      body.style.height = "calc(var(--mg-vh) * 100)";
      documentElement.style.height = "calc(var(--mg-vh) * 100)";
    } else {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    }
    return () => {
      body.style.overflow = "";
      documentElement.style.overflow = "";
      body.style.height = "";
      documentElement.style.height = "";
    };
  }, [viewMode]);

    // Legend highlighting — multi (Ctrl/Cmd+click) + per-legend hide/show (×)
  const [highlighted, setHighlighted] = useState(() => new Set());
  const [legendHidden, setLegendHidden] = useState(() => new Set());

  // Track which series point is under the cursor for the tooltip
  const [hoverSeriesKey, setHoverSeriesKey] = useState(null);
  const [seriesManagerOpen, setSeriesManagerOpen] = useState(false);
  const [legendOpen, setLegendOpen] = useState(false);
  const [legendSearchOpen, setLegendSearchOpen] = useState(false);
  const [legendToggleHover, setLegendToggleHover] = useState(false);
  useEffect(() => {
    setLegendOpen(false);
    setLegendToggleHover(false);
  }, [viewMode, launchChartPreset, showInsights]);
  const [chartStyle, setChartStyle] = useState("area"); // "line" | "area"
  const [chartColors, setChartColors] = useState({});
  const getChartColor = (name, index) => chartColors[name] || chartSeriesColor(index);
  useEffect(() => { if (chartStyle === "boxes") setChartStyle("area"); }, [chartStyle]);
  const [seriesQuery, setSeriesQuery] = useState("");
  const seriesManagerRef = useRef(null);
  const analyticsRef = useRef(null);

  useEffect(() => {
    if (!seriesManagerOpen) return;
    const onDown = (e) => {
      if (seriesManagerRef.current && !seriesManagerRef.current.contains(e.target)) {
        setSeriesManagerOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [seriesManagerOpen]);

  useEffect(() => {
    if (!analyticsOpen) return;
    const onDown = (e) => {
      if (analyticsRef.current && !analyticsRef.current.contains(e.target)) {
        setAnalyticsOpen(false);
      }
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [analyticsOpen]);

  const handleRemoveGame = useCallback((name) => {
    if (isPublicPreview) return;
    setHiddenCards((prev) => [...prev, name]);
  }, [isPublicPreview]);

  const handleReset = () => {
  // --- chart selection / state back to defaults ---
    setFavoriteSelectionEdited(false);
    setComparisonGames([]);
  setHighlighted(new Set());
  setPreview(null);
  setMarketFavorites([]);
  setLegendHidden(new Set());
  setHoverSeriesKey(null);
  setAnalyticsSeries({ average: false, median: false, top5: false, top10: false, top25: false });
  setShowAllGames(false);
  setLaunchesShowAll(false);
  setLaunchChartPreset("custom");
  if (viewMode === "launches") setMindshareChartHeight(510);
  setChartBooting(false);
  mindshareChartHeightUserAdjustedRef.current = false;
  setMindshareChartHeight(getMindshareDefaultChartHeight());
  setChartBgChoice("none");
  setLegendOpen(false);
  setLegendToggleHover(false);
  setChartStyle("area");
  setMarketViewType("squares");
  setMarketSplitView(false);
  setMarketPeriod("1w");
  setBusinessSeries(viewMode === "business"
    ? (businessRollup?.series || []).slice(0, 100).map(getBusinessSeriesName).filter(Boolean)
    : []);
  setBusinessExternalSeries({});
  setBusinessQuery("");
  setMindshareXAxisMode("date");
  setMindshareAgg("weekly");
  // optional: collapse chart back to cards-only
  setShowInsights(viewMode === "launches");
  mindWindowUserAdjustedRef.current = false;

  // --- clear sidebar filters back to "all games" ---
  setSearchTerm("");
  setHiddenCards([]);
  setSelectedTags([]);
  setSelectedPublishers([]);
  setSelectedGenres([]);
  setSelectedDevelopers([]);
  setSelectedLiveService(null);
  setSelectedPricing(null);
  setSelectedUGC(null);
  setSelectedFocus([]);
  setSelectedPlatforms([]);
  setSelectedDateRange([null, null]); // no release-date filter
  setOnlyUnreleased(false);
};

const getLatestMindshareDataDate = (items = games) => {
  let latest = null;
  for (const g of items || []) {
    for (const key of g?._dateKeys || []) {
      const d = new Date(String(key));
      if (!isNaN(d) && (!latest || d > latest)) latest = d;
    }
  }
  return latest;
};

const getLatestAvailableReleaseDate = (items = games) => {
  let latest = null;
  for (const g of items || []) {
    if (!g?.release_date) continue;
    const d = new Date(g.release_date);
    if (!isNaN(d) && (!latest || d > latest)) latest = d;
  }
  return latest;
};

const applyLaunchesCumulativePreset = (targetView = "launches") => {
  if (!games.length) return;

  setFavoriteSelectionEdited(false);
  const latestDataDate = getLatestMindshareDataDate(games);
  if (!latestDataDate) return;

  setViewMode(targetView);
  setLaunchChartPreset("upcoming");
  setChartStyle("area");
  setShowInsights(true);
  setShowAllGames(true);
  setAnalyticsSeries({ average: false, median: false, top5: false, top10: false, top25: false });
  setMindshareXAxisMode("w2l");
  setMindshareAgg("cumulative");
  setSelectedDateRange([null, null]);
  setOnlyUnreleased(false);

  // This is a pre-baked launch view, so clear manual chart-only state
  // while leaving metadata filters available for further narrowing.
  setComparisonGames([]);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);
  setHiddenCards([]);

  // Let the W2L window effect apply the default -30 → 0 launch window.
  mindWindowUserAdjustedRef.current = false;
};

const applyLaunchesRecentReleasesPreset = (targetView = "launches") => {
  if (!games.length) return;

  setFavoriteSelectionEdited(false);
  setViewMode(targetView);
  setLaunchChartPreset("recent");
  setChartStyle("area");
  setShowInsights(true);
  setShowAllGames(true);
  setAnalyticsSeries({ average: false, median: false, top5: false, top10: false, top25: false });
  setMindshareXAxisMode("w2l");
  setMindshareAgg("weekly");
  setSelectedDateRange([null, null]);
  setOnlyUnreleased(false);

  setComparisonGames([]);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);
  setHiddenCards([]);

  // Recent Releases starts at -30 → +30 W2L, then the user can adjust.
  mindWindowUserAdjustedRef.current = false;
};

const handleLaunchChartPresetChange = (preset, targetView = viewMode === "trended" ? "trended" : "launches") => {
  setFavoriteSelectionEdited(false);
  setLegendOpen(false);
  setLegendToggleHover(false);
  if (preset === "upcoming") {
    applyLaunchesCumulativePreset(targetView);
    return;
  }

  if (preset === "recent") {
    applyLaunchesRecentReleasesPreset(targetView);
    return;
  }

  setLaunchChartPreset(preset);
  setShowInsights(true);
  setViewMode(targetView);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);
  mindWindowUserAdjustedRef.current = false;

  if (preset === "custom" || preset === "top100") {
    // Custom should be a clean, from-scratch chart over the full game universe,
    // not a continuation of the Upcoming/Recent release-date windows.
    setSearchTerm("");
    setSelectedTags([]);
    setSelectedPublishers([]);
    setSelectedGenres([]);
    setSelectedDevelopers([]);
    setSelectedLiveService(null);
    setSelectedPricing(null);
    setSelectedUGC(null);
    setSelectedFocus([]);
    setSelectedPlatforms([]);
    setSelectedDateRange([null, null]);
    setOnlyUnreleased(false);
    setHiddenCards([]);
    setComparisonGames([]);
    setLaunchesShowAll(false);
    setShowAllGames(false);
    mindshareChartHeightUserAdjustedRef.current = false;
    setMindshareChartHeight(510);
    setChartBooting(false);
    if (preset === "top100") {
      setDateWindowSize(13);
      setComparisonGames([]);
      setMindshareXAxisMode("date");
      setMindshareAgg("weekly");
      setShowInsights(true);
    }
    return;
  }

  setShowAllGames(true);
  setChartBooting(true);
  window.setTimeout(() => setChartBooting(false), 120);
};

const handleViewModeChange = (mode) => {
  setFavoriteSelectionEdited(false);
  if (mode === "publishers") {
    setFiltersOpen(false);
    setShowInsights(false);
    setViewMode(mode);
    return;
  }
  if (mode === "launches") {
    setFiltersOpen(false);
    handleLaunchChartPresetChange("upcoming", "launches");
    return;
  }

  // Close the filter popover FIRST so it never sits open while a heavy view loads.
  if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
  setFiltersOpen(false);

  if (mode === "market") {
    // Show the market container + skeleton immediately, then let the heavy
    // bubble pack compute on the next frame so the UI doesn't freeze.
    setViewMode("market");
    if (chartStyle === "boxes") setChartStyle("area");
    setShowInsights(false);
    return;
  }

  setViewMode(mode);
  if (mode !== "business" && chartStyle === "boxes") setChartStyle("area");
  if (mode === "business" || mode === "calendar") {
    setShowInsights(false);
    return;
  }

  // Back to normal Cards + Charts.
  setLaunchChartPreset("custom");
  setShowAllGames(false);
  if (mode === "trended" && !mindshareChartHeightUserAdjustedRef.current) {
    setMindshareChartHeight(getMindshareDefaultChartHeight());
  }
  if (!comparisonGames.length) setShowInsights(false);
};

  const handleExportVisibleGamesCsv = () => {
    if (!cardFiltered.length) return;
    const escapeCsv = (value) => {
      const s = String(value ?? "");
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = cardFiltered.map((g, idx) => ({
      rank: idx + 1, game: g.name, mindshare: Number(g.views || 0), mindshare_pct: (Number(g.views || 0) * 100).toFixed(3),
      delta: Number(g.videos || 0), delta_pct: (Number(g.videos || 0) * 100).toFixed(3),
      publisher: g.publisher || "", developer: g.developer || "", genres: (g.genres || []).join(", "), tags: (g.tags || []).join(", "),
      platforms: (g.platforms || []).join(", "), release_date: g.release_date || "", unreleased: g.isUnreleased ? "Yes" : "No",
    }));
    const headers = Object.keys(rows[0] || {});
    const csv = [headers.join(","), ...rows.map((row) => headers.map((h) => escapeCsv(row[h])).join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "mindGAME - mindSHARE Visible Games.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  // seed Release Date once it arrives
  useEffect(() => {
    const [r0, r1] = coerceRangeToDates(releaseDateRange || [null, null]);
    if (r0 && r1) {
      const [c0, c1] = selectedDateRange;
      if (!c0 || !c1) setSelectedDateRange([r0, r1]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseDateRange]);

  // Splash Top 10 (Momentum file) — tiny, independent fetch so the intro
  // carousel never waits on game_stats.csv.gz or the TikTok metadata load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(MINDSHARE_MOMENTUM_URL, { mode: "cors", cache: "no-store" });
        if (!res.ok) throw new Error(`${MINDSHARE_MOMENTUM_URL} HTTP ${res.status}`);
        const text = (await res.text()).trim();
        const rows = d3.csvParse(text);

        const mapped = rows
          .map((r) => {
            const name = (r.Game || "").trim();
            if (!name) return null;
            const momentumRank = Number(r.Momentum_Rank);
            const growth = Number(r.Growth);
            return {
              id: name,
              name,
              imageUrl: (r.Image || "").trim(),
              backgroundUrl: (r.Background || "").trim(),
              videoUrl: (r.Video || "").trim(),
              mindshare: Number(r.mindSHARE) || 0,
              growthPT: Number.isFinite(growth) ? growth : (Number(r.Growth_Percent) || 0) / 100,
              publisher: (r.Publisher || "").trim(),
              developer: (r.Developer || "").trim(),
              description: (r.Description || "").trim(),
              writeUp: (r["Write Up"] || "").trim(),
              rank_mindshare: Number.isFinite(+r.mindSHARE_Rank) ? +r.mindSHARE_Rank : null,
              rank_search: Number.isFinite(+r.Google_Rank) ? +r.Google_Rank : null,
              rank_video: Number.isFinite(+r.YouTube_Rank) ? +r.YouTube_Rank : null,
              rank_twitch: Number.isFinite(+r.Twitch_Rank) ? +r.Twitch_Rank : null,
              _momentumRank: Number.isFinite(momentumRank) ? momentumRank : 999,
              _period: (r.Period || "").trim(),
            };
          })
          .filter(Boolean)
          .sort((a, b) => a._momentumRank - b._momentumRank);

        if (cancelled || !mapped.length) return;
        setSplashGames(mapped);

        const periodEnd = new Date(mapped[0]._period);
        if (!isNaN(periodEnd)) {
          const periodStart = new Date(periodEnd);
          periodStart.setDate(periodStart.getDate() - 6);
          setSplashPeriodLabel(
            `${periodStart.toLocaleDateString()} - ${periodEnd.toLocaleDateString()}`
          );
        }
      } catch (e) {
        console.error("❌ mindSHARE Momentum (Top 10) load error:", e);
      } finally {
        if (!cancelled) setSplashLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ---------------- Build mindSHARE index (+ ranks & search) ---------------- */

  // mindIndexRef: Map<normalizedGameName, Array<Row>>
  const mindIndexRef = useRef(new Map());
  const favoriteMemberNamesRef = useRef({ scope: null, names: null });
  const favoriteLoadStartRef = useRef(null);
  const [indexLoaded, setMindIndexReady] = useState(false);
  const [loadedHistoryScope, setLoadedHistoryScope] = useState(null);
  const [businessHistoryRequested, setBusinessHistoryRequested] = useState(false);
  const isAllReleaseDateRange = useMemo(() => {
    const [selectedStart, selectedEnd] = coerceRangeToDates(selectedDateRange);
    const [availableStart, availableEnd] = coerceRangeToDates(releaseDateRange || [null, null]);
    if (!selectedStart || !selectedEnd || !availableStart || !availableEnd) return false;
    const dayKey = date => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized.getTime();
    };
    return dayKey(selectedStart) === dayKey(availableStart) && dayKey(selectedEnd) === dayKey(availableEnd);
  }, [selectedDateRange, releaseDateRange]);
  const isReleasePreset = ["upcoming", "recent"].includes(launchChartPreset) &&
    (viewMode === "launches" || viewMode === "trended");
  const favoriteHistoryPreset = !legacyData && (
    (viewMode === "trended" && launchChartPreset === "top100" && !showAllGames)
      ? "top100"
      : isReleasePreset
        ? launchChartPreset === "recent" ? "recent12m" : "upcoming"
        : null
  );
  const perGameHistory = !legacyData && (
    !favoriteHistoryPreset && usesPerGameHistory({ viewMode, launchChartPreset }) && !showAllGames
  );
  const selectedHistoryNames = JSON.stringify([...new Set(comparisonGames)].sort());
  const favoriteHistoryScope = favoriteHistoryPreset
    ? `favorites:${favoriteHistoryPreset}:${snapshotLatestPeriod(snapshotRows) || "unknown"}`
    : null;
  const historyScope = favoriteHistoryScope
    ? `${favoriteHistoryScope}${favoriteSelectionEdited ? `:${selectedHistoryNames}` : ""}`
    : perGameHistory ? selectedHistoryNames : "all";
  const favoriteBaseReady = Boolean(favoriteHistoryPreset && loadedHistoryScope &&
    (loadedHistoryScope === favoriteHistoryScope || loadedHistoryScope.startsWith(`${favoriteHistoryScope}:`)));
  const mindIndexReady = indexLoaded && (legacyData || (perGameHistory
    ? JSON.parse(selectedHistoryNames).every(name => mindIndexRef.current.has(norm(name)))
    : loadedHistoryScope === historyScope));
  const chartHistoryReady = mindIndexReady || (perGameHistory && indexLoaded) || favoriteBaseReady;
  const snapshotMarket = !legacyData && viewMode === "market" && marketPeriod === "1w";
  const marketDataReady = snapshotMarket ? Boolean(snapshotRows) : mindIndexReady;
  const historyRequested = needsMindshareHistory({ viewMode, marketPeriod, comparisonGames, launchChartPreset, businessHistoryRequested, showAllGames });
  const historyPending = !legacyData && historyRequested && (!mindIndexReady || !snapshotRows);

  useEffect(() => {
    if (legacyData || !historyRequested || mindIndexReady || !snapshotRows) return;
    let cancelled = false;
    if (favoriteHistoryPreset) favoriteLoadStartRef.current = performance.now();
    setLoading(true);
    setDataError(null);
    const loadFavoriteBase = favoriteHistoryPreset
      ? loadFavoriteHistory(favoriteHistoryPreset, snapshotRows).then(result => ({
        index: result.historyIndex,
        members: result.data.members,
      })).catch(error => {
        if (cancelled) throw error;
        console.warn(`mindSHARE ${favoriteHistoryPreset} export failed; falling back to the existing history loader.`, error);
        if (favoriteHistoryPreset === "top100") {
          const names = [...snapshotGames]
            .sort((first, second) => Number(second.views || 0) - Number(first.views || 0))
            .slice(0, 100)
            .map(game => game.name);
          const selectedNames = favoriteSelectionEdited ? JSON.parse(selectedHistoryNames) : [];
          return loadGameHistories([...new Set([...names, ...selectedNames])]).then(index => ({
            index,
            members: names.map(name => ({ name })),
          }));
        }
        const latestPeriod = snapshotLatestPeriod(snapshotRows);
        const latestDataDate = latestPeriod ? new Date(`${latestPeriod}T00:00:00`) : null;
        if (favoriteHistoryPreset === "upcoming" && latestDataDate) {
          const start = new Date(latestDataDate);
          start.setDate(start.getDate() - 7);
          start.setHours(0, 0, 0, 0);
          const end = getLatestAvailableReleaseDate(snapshotGames);
          if (end) {
            end.setHours(0, 0, 0, 0);
            setSelectedDateRange([start, end]);
          }
        } else if (favoriteHistoryPreset === "recent12m" && latestDataDate) {
          const end = new Date(latestDataDate);
          end.setHours(0, 0, 0, 0);
          const start = new Date(end);
          const originalDay = start.getDate();
          start.setDate(1);
          start.setFullYear(start.getFullYear() - 1);
          start.setDate(Math.min(originalDay, new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate()));
          start.setHours(0, 0, 0, 0);
          setSelectedDateRange([start, end]);
        }
        return loadHistory().then(index => ({ index, members: null }));
      })
      : null;
    const request = favoriteHistoryPreset
      ? loadFavoriteBase.then(async ({ index, members }) => {
        if (!favoriteSelectionEdited) return { index, members };
        const selectedNames = JSON.parse(selectedHistoryNames);
        const missingNames = selectedNames.filter(name => !index.has(norm(name)));
        if (!missingNames.length) return { index, members };
        const extraHistory = await loadGameHistories(missingNames);
        const combinedIndex = new Map(index);
        for (const [key, history] of extraHistory) combinedIndex.set(key, history);
        return { index: combinedIndex, members };
      })
      : (perGameHistory ? loadGameHistories(JSON.parse(selectedHistoryNames)) : loadHistory())
        .then(index => ({ index, members: null }));
    request.then(({ index, members }) => {
      if (!cancelled) {
        mindIndexRef.current = index;
        if (favoriteHistoryPreset) {
          favoriteMemberNamesRef.current = {
            scope: favoriteHistoryScope,
            names: members ? new Set(members.map(member => norm(member.name))) : null,
          };
        }
        if (favoriteHistoryPreset === "top100" && members && !favoriteSelectionEdited) {
          setComparisonGames(members.map(member => member.name));
        }
        setLoadedHistoryScope(historyScope);
        setMindIndexReady(true);
      }
    }).catch(error => {
      console.error("mindSHARE history load failed:", error);
      if (!cancelled) setDataError({ source: "history", message: "Unable to load complete mindSHARE history. Please retry." });
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [legacyData, historyRequested, mindIndexReady, dataRetry, perGameHistory, favoriteHistoryPreset, favoriteSelectionEdited, snapshotRows, snapshotGames, selectedHistoryNames, historyScope]);

  useEffect(() => {
    if (!legacyData) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const text = await fetchGzipText(GAME_STATS_URL);
        const rows = d3.csvParse(text);

        const idx = new Map();
        for (const r of rows) {
          const game = (r.Game ?? r.game ?? "").trim();
          if (!game) continue;

          const periodRaw =
            r.Period ??
            r["Period (DATE)"] ??
            r.period ??
            r.Date ??
            r.date ??
            "";
          const d = new Date(periodRaw);
          if (Number.isNaN(d)) continue;

          const dateISO = d.toISOString().slice(0, 10);
          const w2lRaw =
            r.Weeks_to_Launch ??
            r["Weeks_to_Launch"] ??
            r["Weeks to Launch"] ??
            r.weeks_to_launch ??
            r.w2l ??
            r.W2L ??
            null;

          const ms = Number(
            r.mindSHARE ??
              r.MindSHARE ??
              r.mindshare ??
              r.Mindshare ??
              r["mindSHARE"]
          );
          if (!Number.isFinite(ms)) continue;

          const rank =
            r.Rank_mindSHARE ??
            r["Rank__mindSHARE"] ??
            r["Rank___mindSHARE"];
          const searchRank =
            r.Rank_Search ?? r["Rank__Search"] ?? r["Rank___Search"];
          const videoRank =
            r.Rank_Video ?? r["Rank__Video"] ?? r["Rank___Video"];
          const twitchRank =
            r.Rank_Twitch ?? r["Rank__Twitch"] ?? r["Rank___Twitch"];
          const search = r.Search_Volume ?? r.search_volume ?? null;

          const rec = {
            date: dateISO,
            w2l: Number.isFinite(+w2lRaw) ? +w2lRaw : null,
            level: ms,
            weekly: 0,
            rank: Number.isFinite(+rank) ? +rank : null,
            searchRank: Number.isFinite(+searchRank) ? +searchRank : null,
            videoRank: Number.isFinite(+videoRank) ? +videoRank : null,
            twitchRank: Number.isFinite(+twitchRank) ? +twitchRank : null,
            search: Number.isFinite(+search) ? +search : null,
          };

          const k = norm(game);
          if (!idx.has(k)) idx.set(k, []);
          idx.get(k).push(rec);
        }

        // sort & compute weekly deltas
        for (const arr of idx.values()) {
          arr.sort((a, b) => (a.date < b.date ? -1 : 1));
          for (let i = 0; i < arr.length; i++) {
            const prev = i > 0 ? arr[i - 1].level : null;
            arr[i].weekly = prev == null ? 0 : arr[i].level - prev;
          }
        }

        if (!cancelled) {
          mindIndexRef.current = idx;
          setMindIndexReady(true);
        }
      } catch (e) {
        console.error("❌ mindSHARE load error:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legacyData]);

  /* -------------------- Cards with fast lookup tables -------------------- */
  const historicalGames = useMemo(() => {
    if (!chartHistoryReady || !metaData?.length) return [];
    const idx = mindIndexRef.current;
    return cachedHistoryGames(idx, metaData, () => timeMindshare("history-game-model", () => {
    const out = [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

  // ---- TikTok 7-day rank (by views delta) ----
  const tiktokRankMap = new Map();
  if (tikTokData && tikTokData.length) {
    const grouped = new Map();

    for (const row of tikTokData) {
      const gname = (row.game || row.Game || "").trim();
      if (!gname) continue;
      const dtRaw = row.dt || row.date;
      const d = new Date(dtRaw);
      if (isNaN(d)) continue;
      const views = Number(row.views ?? row.Views);
      if (!Number.isFinite(views)) continue;

      if (!grouped.has(gname)) grouped.set(gname, []);
      grouped.get(gname).push({ date: d, views });
    }

    const deltas = [];
    for (const [gname, arr] of grouped) {
      arr.sort((a, b) => a.date - b.date);
      if (!arr.length) continue;
      const latest = arr[arr.length - 1];
      const target = new Date(latest.date);
      target.setHours(0, 0, 0, 0);
      target.setDate(target.getDate() - 7);

      let k = arr.length - 2;
      while (k >= 0 && arr[k].date > target) k--;
      if (k < 0) k = 0;

      const baseViews = arr[k].views || 0;
      const delta7d = (latest.views || 0) - baseViews;
      deltas.push({ name: gname, delta: delta7d });
    }

    deltas
      .sort((a, b) => (b.delta || 0) - (a.delta || 0))
      .forEach((entry, idx) => {
        // use the same norm() as mindSHARE so names line up
        tiktokRankMap.set(norm(entry.name), idx + 1);
      });
  }

    for (const meta of metaData) {
      const name = (meta?.Game || "").trim();
      if (!name) continue;
      const key = norm(name);
      const series = idx.get(key);
      if (!series || series.length === 0) continue;

      const byDate = new Map();
      const byW2L = new Map();
      const byDateMeta = new Map();
      const byW2LMeta = new Map();

      const dateKeys = [];
      const w2lKeys = [];

      for (const r of series) {
        byDate.set(r.date, r.level);
        byDateMeta.set(r.date, {
          rank: r.rank ?? null,
          searchRank: r.searchRank ?? null,
          videoRank: r.videoRank ?? null,
          twitchRank: r.twitchRank ?? null,
          search: r.search ?? null,
        });
        dateKeys.push(r.date);
        if (r.w2l != null) {
          byW2L.set(r.w2l, r.level);
          byW2LMeta.set(r.w2l, {
            rank: r.rank ?? null,
            searchRank: r.searchRank ?? null,
            videoRank: r.videoRank ?? null,
            twitchRank: r.twitchRank ?? null,
            search: r.search ?? null,
          });
          w2lKeys.push(r.w2l);
        }
      }
            dateKeys.sort();
      w2lKeys.sort((a, b) => a - b);

      const last = series[series.length - 1];
      const prev = series.length > 1 ? series[series.length - 2] : null;

      // Latest ranks for card display
      const latestRank = last?.rank;
      const latestSearchRank = last?.searchRank;
      const latestVideoRank = last?.videoRank;
      const latestTwitchRank = last?.twitchRank;


      const rdStr = (meta?.Release_Date || "").slice(0, 10) || null;
      let isUnreleased = true;
      if (rdStr) {
        const d = new Date(rdStr);
        if (!isNaN(d)) isUnreleased = d > today;
      }

      const tiktokRank = tiktokRankMap.get(key) ?? null;

                        out.push({
        name,
        imageUrl: meta?.ImageURL || "",
        publisher: meta?.Publisher || "",
        tags: (meta?.Tags || "")
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        genres: (meta?.Genre || "")
          .split(",")
          .map((g) => g.trim())
          .filter(Boolean),
        developer: (meta?.Developer || "").trim() || "",
        description: (meta?.Description || meta?.Summary || meta?.Game_Description || meta?.About || "").trim() || "",
        live_service_static: (meta?.Live_Service_Static || "").trim() || "",
        pricing: (meta?.Pricing_Monetization || "").trim() || "",
        user_generated_content:
          (meta?.["User_Generated_Content_(UGC)"] || "").trim() || "",
        platforms: (meta?.["Platform_Group_Filter"] || "")
          .split(",")
          .map((p) => p.trim())
          .filter(Boolean),
        single_multiplayer_focus:
          (meta?.Single_Multiplayer_Focus || "").trim() || "",
        release_date: rdStr,
        isUnreleased,

        // cards: latest mindSHARE level + weekly Δ
        views: Number(last?.level ?? 0),
        videos: prev ? Number(last.level - (prev.level ?? 0)) : 0,

        // latest ranks for card stats
        rank_mindshare: Number.isFinite(+latestRank) ? +latestRank : null,
        search_rank: Number.isFinite(+latestSearchRank) ? +latestSearchRank : null,
        video_rank: Number.isFinite(+latestVideoRank) ? +latestVideoRank : null,
        twitch_rank: Number.isFinite(+latestTwitchRank) ? +latestTwitchRank : null,

        // TikTok 7d rank
        tiktok_rank: Number.isFinite(+tiktokRank) ? +tiktokRank : null,

        // chart lookups
        _byDate: byDate,
        _byW2L: byW2L,
        _dateKeys: dateKeys,
        _w2lKeys: w2lKeys,
        _metaByDate: byDateMeta,
        _metaByW2L: byW2LMeta,
      });



    }

    out.sort((a, b) => (b.views || 0) - (a.views || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return out;
    }));
    }, [chartHistoryReady, loadedHistoryScope, metaData]);
  const games = useMemo(() => {
      if (legacyData) return historicalGames;
    if (!historyRequested || !chartHistoryReady) return snapshotGames;
    const detailed = new Map(historicalGames.map(game => [game.name, game]));
      if (favoriteHistoryPreset) {
        return snapshotGames.map(game => {
          const historical = detailed.get(game.name);
          return historical ? { ...game, ...historical, launch_score: game.launch_score } : game;
        });
      }
      if (mindIndexReady && !perGameHistory) return historicalGames;
      return snapshotGames.map(game => {
        const historical = detailed.get(game.name);
        return historical ? { ...game, ...historical, launch_score: game.launch_score } : game;
      });
  }, [legacyData, historyRequested, mindIndexReady, chartHistoryReady, perGameHistory, favoriteHistoryPreset, historicalGames, snapshotGames]);

  /* ------------------------------ Filtering ------------------------------ */
  const filtered = useMemo(() => {
    const start = performance.now();
    const result = filterMindshareGames(games, {
      selectedTags, selectedPublishers, selectedGenres, selectedDevelopers,
      selectedLiveService, selectedPricing, selectedUGC, selectedFocus,
      selectedPlatforms, selectedDateRange, onlyUnreleased,
    }, isAllReleaseDateRange);
    recordMindshareTiming("filter-games", start);
    return result;
  }, [
    games,
    selectedTags,
    selectedPublishers,
    selectedGenres,
    selectedDevelopers,
    selectedLiveService,
    selectedPricing,
    selectedUGC,
    selectedFocus,
    selectedPlatforms,
    selectedDateRange,
    isAllReleaseDateRange,
    onlyUnreleased,
  ]);

  const cardFiltered = useMemo(() => {
    const start = performance.now();
    const q = searchTerm.trim().toLowerCase();
    const result = filtered
      .filter((g) => !hiddenCards.includes(g.name))
      .filter((g) => !q || g.name.toLowerCase().includes(q));
    recordMindshareTiming("card-search", start);
    return result;
  }, [filtered, hiddenCards, searchTerm, viewMode, launchChartPreset]);

  const filteredByName = useMemo(() => {
    const m = new Map();
    for (const g of filtered) m.set(g.name, g);
    return m;
  }, [filtered]);

  const gamesByName = useMemo(() => {
    const m = new Map();
    for (const g of games) m.set(g.name, g);
    return m;
  }, [games]);

  // Filter dropdowns: prefer context-provided lists, but fall back to the loaded
  // mindSHARE/meta games so the filters still populate if the TikTok context helpers
  // are still warming up or return empty arrays.
  const filterOptions = useMemo(() => {
    if (!legacyData && snapshotRows) return timeMindshare("filter-options", () => snapshotFilterOptions(snapshotRows));
    const start = performance.now();
    const uniq = (values) =>
      Array.from(
        new Set(
          values
            .flat()
            .map((v) => String(v || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    const options = {
      tags: (legacyData && allTags?.length ? allTags : uniq(games.map((g) => g.tags || []))).map((v) => ({ label: v, value: v })),
      genres: (legacyData && allGenres?.length ? allGenres : uniq(games.map((g) => g.genres || []))).map((v) => ({ label: v, value: v })),
      publishers: (legacyData && allPublishers?.length ? allPublishers : uniq(games.map((g) => [g.publisher]))).map((v) => ({ label: v, value: v })),
      developers: (legacyData && allDevelopers?.length ? allDevelopers : uniq(games.map((g) => [g.developer]))).map((v) => ({ label: v, value: v })),
      liveService: (legacyData && allLiveService?.length ? allLiveService : uniq(games.map((g) => [g.live_service_static]))).map((v) => ({ label: v, value: v })),
      pricing: (legacyData && allPricing?.length ? allPricing : uniq(games.map((g) => [g.pricing]))).map((v) => ({ label: v, value: v })),
      ugc: (legacyData && allUGC?.length ? allUGC : uniq(games.map((g) => [g.user_generated_content]))).map((v) => ({ label: v, value: v })),
      focus: (legacyData && allFocus?.length ? allFocus : uniq(games.map((g) => String(g.single_multiplayer_focus || "").split(",").map((s) => s.trim())))).map((v) => ({ label: v, value: v })),
      platforms: (legacyData && allPlatforms?.length ? allPlatforms : uniq(games.map((g) => g.platforms || []))).map((v) => ({ label: v, value: v })),
    };
    recordMindshareTiming("filter-options", start);
    return options;
  }, [
    games,
    snapshotRows,
    legacyData,
    allTags,
    allGenres,
    allPublishers,
    allDevelopers,
    allLiveService,
    allPricing,
    allUGC,
    allFocus,
    allPlatforms,
  ]);

// ---------- Guided presets for initial chart ----------

const applyComingSoonPreset = () => {
  if (!games.length) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);
  start.setDate(start.getDate() + 1); // tomorrow

  const end = new Date(today);
  end.setDate(end.getDate() + 90); // +90 days

  const subset = games.filter((g) => {
    if (!g.release_date) return false;
    const d = new Date(g.release_date);
    if (isNaN(d)) return false;
    d.setHours(0, 0, 0, 0);
    return d >= start && d <= end;
  });

  if (!subset.length) {
    // nothing matches – just close the overlay and leave defaults
    setShowGuidedOverlay(false);
    return;
  }

  subset.sort((a, b) => (b.views || 0) - (a.views || 0));
  const picked = subset.slice(0, 30);
  const names = picked.map((g) => g.name);

  // Filter cards by date, and show unreleased
  setSelectedDateRange([start, end]);
  setOnlyUnreleased(true);

  // Standard size chart, trended view
  setViewMode("trended");
  setShowInsights(true);

  // W2L cumulative, window -30 → 0
  setW2lInitialWindow({ start: -30, end: 0 });
  setMindshareXAxisMode("w2l");
  setMindshareAgg("cumulative");

  // Limit chart to this set
  setComparisonGames(names);
  setHiddenCards([]);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);

  setShowGuidedOverlay(false);
};

const applyPastYearPreset = () => {
  if (!games.length) return;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);
  start.setDate(start.getDate() - 365); // last year

  const subset = games.filter((g) => {
    if (!g.release_date) return false;
    const d = new Date(g.release_date);
    if (isNaN(d)) return false;
    d.setHours(0, 0, 0, 0);
    return d >= start && d <= today;
  });

  if (!subset.length) {
    setShowGuidedOverlay(false);
    return;
  }

  subset.sort((a, b) => (b.views || 0) - (a.views || 0));
  const picked = subset.slice(0, 30);
  const names = picked.map((g) => g.name);

  // Filter cards by date, include released games
  setSelectedDateRange([start, today]);
  setOnlyUnreleased(false);

  // Standard size chart, trended
  setViewMode("trended");
  setShowInsights(true);

  // Same cumulative W2L window as Coming Soon
  setW2lInitialWindow({ start: -30, end: 0 });
  setMindshareXAxisMode("w2l");
  setMindshareAgg("cumulative");

  setComparisonGames(names);
  setHiddenCards([]);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);

  setShowGuidedOverlay(false);
};

const applyTopGrowthPreset = () => {
  if (!games.length) return;

  // Rank games by growthPT for the *latest* period only
  const candidates = [];

  for (const g of games) {
    const keys = g._dateKeys;
    if (!keys || keys.length < 2) continue;

    const lastIdx = keys.length - 1;
    const lastKey = keys[lastIdx];
    const prevKey = keys[lastIdx - 1];

    const prevVal = Number(g._byDate.get(prevKey) || 0);
    const lastVal = Number(g._byDate.get(lastKey) || 0);
    const delta = lastVal - prevVal;
    if (!Number.isFinite(delta)) continue;

    const growthPT = prevVal > 0 ? delta / prevVal : 0;
    if (!Number.isFinite(growthPT)) continue;

    candidates.push({ game: g, growthPT });
  }

  if (!candidates.length) {
    setShowGuidedOverlay(false);
    return;
  }

  // Sort by latest-period growthPT descending and take top 30
  candidates.sort((a, b) => (b.growthPT || 0) - (a.growthPT || 0));
  const picked = candidates.slice(0, 30).map((c) => c.game);
  const names = picked.map((g) => g.name);

  // Reset filters so everything is visible
  setSelectedTags([]);
  setSelectedPublishers([]);
  setSelectedGenres([]);
  setSelectedDevelopers([]);
  setSelectedLiveService(null);
  setSelectedPricing(null);
  setSelectedUGC(null);
  setSelectedFocus([]);
  setSelectedPlatforms([]);
  setOnlyUnreleased(false);

  // Weekly Date view, show last ~12 periods for context
  setViewMode("trended");
  setShowInsights(true);
  setMindshareXAxisMode("date");
  setMindshareAgg("weekly");
  setDateWindowSize(12);        // use last 12 periods on Date axis

  setComparisonGames(names);
  setHiddenCards([]);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);

  setShowGuidedOverlay(false);
};

const handleGuideClose = () => {
  // Close overlay and hide chart → “just cards”
  setShowGuidedOverlay(false);
  setShowInsights(false);
  setComparisonGames([]);
  setFavoriteSelectionEdited(false);
  setHighlighted(new Set());
  setLegendHidden(new Set());
  setHoverSeriesKey(null);
};


    /* ------------------------------ Chart data ------------------------------ */
  const launchesRankedGames = useMemo(() => {
    if (!historyRequested || !mindIndexReady || !filtered.length) return [];
    const membership = favoriteMemberNamesRef.current;
    const candidates = !favoriteHistoryPreset
      ? filtered
      : membership.scope !== favoriteHistoryScope
        ? []
        : membership.names
          ? filtered.filter(game => membership.names.has(norm(game.name)))
          : filtered;
    return [...candidates].sort(compareLaunchGames);
  }, [historyRequested, mindIndexReady, filtered, favoriteHistoryPreset, favoriteHistoryScope]);

  const targetsForChart = useMemo(() => {
    let base;

    if (favoriteHistoryPreset && favoriteSelectionEdited) {
      base = comparisonGames.slice();
    } else if (viewMode === "launches" || (viewMode === "trended" && (launchChartPreset === "upcoming" || launchChartPreset === "recent"))) {
      if (launchChartPreset === "custom") {
        base = comparisonGames.slice();
      } else {
        // Upcoming Releases is a pre-baked chart over the current launch window.
        // Rank by cumulative mindSHARE from W2L -30 → 0, not alphabetically.
        const launchPool = launchesShowAll ? launchesRankedGames : launchesRankedGames.slice(0, 100);
        base = launchPool.map((g) => g.name);
      }
    } else if (showAllGames) {
      // all currently filtered games (honors sidebar filters)
      base = filtered.map((g) => g.name);
    } else if (comparisonGames.length > 0) {
      // manually selected comparison set
      base = comparisonGames.slice();
    } else {
      // no default selection — cards start unchecked until the user picks games
      base = [];
    }

    // drop hidden series, preserving ranking / user selection order.
    const visible = base.filter((n) => !legendHidden.has(n) &&
      ((!perGameHistory && !favoriteHistoryPreset) || historicalGames.some(game => norm(game.name) === norm(n))));

    return visible;
  }, [
    viewMode,
    launchChartPreset,
    launchesShowAll,
    favoriteHistoryPreset,
    favoriteSelectionEdited,
    launchesRankedGames,
    showAllGames,
    comparisonGames,
    filtered,
    legendHidden,
    perGameHistory,
    historicalGames,
  ]);

  chartTargetsRef.current = targetsForChart;



  function getGameByName(name) {
    if (viewMode === "launches" && launchChartPreset === "custom") {
      return gamesByName.get(name) || filteredByName.get(name);
    }
    return filteredByName.get(name);
  }
  function keysForAxis(g) {
    return mindshareXAxisMode === "w2l" ? g._w2lKeys : g._dateKeys;
  }
  function levelAt(g, key) {
    return mindshareXAxisMode === "w2l"
      ? g._byW2L.get(key)
      : g._byDate.get(key);
  }

  // K-way merge of sorted key arrays (only the plotted targets), W2L clamped to <=400
  function unionKeysForTargets(names) {
    const sources = names
      .map(getGameByName)
      .filter(Boolean)
      .map((g) => keysForAxis(g));

    if (mindshareXAxisMode === "w2l" && launchChartPreset !== "custom") {
      for (let s = 0; s < sources.length; s++) {
        const arr = sources[s];
        let end = arr.length;
        while (end > 0 && Number(arr[end - 1]) > 400) end--;
        if (end < arr.length) sources[s] = arr.slice(0, end);
      }
    }

    const idx = new Array(sources.length).fill(0);
    const out = [];
    while (true) {
      let minKey = null;
      let minSrc = -1;
      for (let s = 0; s < sources.length; s++) {
        const arr = sources[s];
        const i = idx[s];
        if (i >= arr.length) continue;
        const k = arr[i];
        if (
          mindshareXAxisMode === "w2l" &&
          launchChartPreset !== "custom" &&
          Number(k) > 400
        ) {
          idx[s] = arr.length;
          continue;
        }
        if (
          minKey == null ||
          (mindshareXAxisMode === "w2l"
            ? k < minKey
            : String(k) < String(minKey))
        ) {
          minKey = k;
          minSrc = s;
        }
      }
      if (minSrc < 0) break;
      if (out.length === 0 || out[out.length - 1] !== minKey)
        out.push(minKey);
      idx[minSrc]++;
    }
    return out;
  }

    const lineChartData = useMemo(() => timeMindshare("chart-data-preparation", () => {
    if (!showInsights || !chartHistoryReady) return [];
    const hasChartUniverse =
      viewMode === "launches" && launchChartPreset === "custom"
        ? games.length > 0
        : filtered.length > 0;
    if (!hasChartUniverse || !targetsForChart.length) return [];

    const keys = unionKeysForTargets(targetsForChart);

    // Always base data = raw level values (no cumulative here)
    return keys.map((key) => {
      const row = { x: key };
      for (const name of targetsForChart) {
        const g = getGameByName(name);
        const v = g ? levelAt(g, key) : undefined;
        if (v != null) row[name] = v;
      }
      return row;
    });
  }), [showInsights, chartHistoryReady, filteredByName, gamesByName, targetsForChart, mindshareXAxisMode, viewMode, launchChartPreset]);

  useEffect(() => {
    if (!favoriteHistoryPreset || !mindIndexReady || !lineChartData.length || favoriteLoadStartRef.current === null) return;
    recordMindshareTiming("favorites-chart-usable", favoriteLoadStartRef.current);
    favoriteLoadStartRef.current = null;
  }, [favoriteHistoryPreset, mindIndexReady, lineChartData.length]);


  /* --------- Default W2L window to [-52, 0] on entry to W2L mode --------- */
  const [mindWinStartIdx, setMindWinStartIdx] = useState(0);
const [mindWinEndIdx, setMindWinEndIdx] = useState(0);
const prevAxisRef = useRef(null);
const mindWindowUserAdjustedRef = useRef(false);
const [dateWindowSize, setDateWindowSize] = useState(52); // default date window (weeks)
const chartTargetSignature = useMemo(() => targetsForChart.join("\u0001"), [targetsForChart]);

useEffect(() => {
  if (chartBgChoice === "none") return;
  if (!targetsForChart.includes(chartBgChoice)) setChartBgChoice("none");
}, [chartTargetSignature, chartBgChoice, targetsForChart]);

useEffect(() => {
  const len = lineChartData.length;
  if (!len) return;

  const prevAxis = prevAxisRef.current;
  const enteringDate = mindshareXAxisMode === "date" && prevAxis !== "date";
  prevAxisRef.current = mindshareXAxisMode;

  // Launches Upcoming opens to the standard Tableau window: -30 → 0 weeks to launch.
  // Recent Releases opens to the full available W2L spread for the selected release window.
  if (
    viewMode === "launches" &&
    mindshareXAxisMode === "w2l" &&
    !mindWindowUserAdjustedRef.current
  ) {
    const keys = lineChartData.map((row) => Number(row.x));

    // Custom: show the full available history for the chosen games, no clipping.
    if (launchChartPreset === "custom") {
      setMindWinStartIdx(0);
      setMindWinEndIdx(len - 1);
      return;
    }

    if (launchChartPreset === "recent") {
      // Tableau-style Recent Releases starts at -30 → +30 W2L,
      // then the user can widen/tighten from the slider.
      let startIdx = keys.findIndex((k) => Number.isFinite(k) && k >= -30);
      if (startIdx < 0) startIdx = 0;

      let endIdx = -1;
      for (let i = 0; i < keys.length; i++) {
        if (Number.isFinite(keys[i]) && keys[i] <= 30) endIdx = i;
      }
      if (endIdx < startIdx) endIdx = Math.min(len - 1, startIdx + 1);

      setMindWinStartIdx(startIdx);
      setMindWinEndIdx(Math.min(len - 1, endIdx));
      return;
    }

    let startIdx = keys.findIndex((k) => Number.isFinite(k) && k >= -30);
    if (startIdx < 0) startIdx = 0;

    let endIdx = -1;
    for (let i = 0; i < keys.length; i++) {
      if (Number.isFinite(keys[i]) && keys[i] <= 0) endIdx = i;
    }
    if (endIdx < startIdx) endIdx = Math.min(len - 1, startIdx + 1);

    setMindWinStartIdx(startIdx);
    setMindWinEndIdx(Math.min(len - 1, endIdx));
    return;
  }

  // Weeks-to-launch opens to the Tableau-style -30 → +2 window outside pre-baked
  // launches; the slider position shows the extra range still available.
  if (mindshareXAxisMode === "w2l" && !mindWindowUserAdjustedRef.current) {
    const keys = lineChartData.map((row) => Number(row.x));

    let startIdx = keys.findIndex((k) => Number.isFinite(k) && k >= -30);
    if (startIdx < 0) startIdx = 0;

    let endIdx = -1;
    for (let i = 0; i < keys.length; i++) {
      if (Number.isFinite(keys[i]) && keys[i] <= 2) endIdx = i;
    }
    if (endIdx < startIdx) endIdx = Math.min(len - 1, startIdx + 1);

    setMindWinStartIdx(startIdx);
    setMindWinEndIdx(Math.min(len - 1, endIdx));
    return;
  }

  // Date mode keeps the Twitch-style default: most recent N periods on first entry
  // and whenever chart games change unless the user manually adjusted the window.
  if (mindshareXAxisMode === "date" && !mindWindowUserAdjustedRef.current) {
    const windowSize = dateWindowSize;
    const endIdx = len - 1;
    let startIdx = Math.max(0, endIdx - (windowSize - 1));
    if (launchChartPreset === "top100") {
      const cutoff = new Date(String(lineChartData[endIdx].x));
      cutoff.setMonth(cutoff.getMonth() - 3);
      const first = lineChartData.findIndex((row) => new Date(String(row.x)) >= cutoff);
      if (first >= 0) startIdx = first;
    }
    setMindWinStartIdx(startIdx);
    setMindWinEndIdx(endIdx);
    return;
  }

  setMindWinStartIdx((prev) =>
    Math.max(0, Math.min(prev, Math.max(0, len - 2)))
  );
  setMindWinEndIdx((prev) =>
    Math.max(1, Math.min(prev, Math.max(1, len - 1)))
  );
}, [lineChartData.length, mindshareXAxisMode, dateWindowSize, viewMode, launchChartPreset, chartTargetSignature]);



  const mindshareRenderData = useMemo(() => {
    const len = lineChartData.length;
    if (!len) return [];
    const s = Math.max(0, Math.min(mindWinStartIdx, len - 1));
    const e = Math.max(s, Math.min(mindWinEndIdx, len - 1));
    return lineChartData.slice(s, e + 1);
  }, [lineChartData, mindWinStartIdx, mindWinEndIdx]);

  // Build a cumulative version *within the current window only*
  const cumulativeRenderData = useMemo(() => {
    if (mindshareAgg !== "cumulative") return [];
    if (!mindshareRenderData.length) return [];

    const run = Object.create(null); // per-series running total

    return mindshareRenderData.map((row) => {
      const out = { x: row.x };
      for (const name of targetsForChart) {
        const val = Number(row[name]);
        if (Number.isFinite(val)) {
          // keep same behavior as before: only add positive values
          const inc = Math.max(0, val);
          run[name] = (run[name] || 0) + inc;
          out[name] = run[name];
        }
      }
      return out;
    });
  }, [mindshareAgg, mindshareRenderData, targetsForChart]);

  // Dataset actually sent to the chart: weekly base vs window-local cumulative
  const chartData = useMemo(
    () =>
      mindshareAgg === "cumulative"
        ? cumulativeRenderData
        : mindshareRenderData,
    [mindshareAgg, cumulativeRenderData, mindshareRenderData]
  );

  // visibleKeys now follow whatever we are charting
  const visibleKeys = useMemo(
    () => chartData.map((r) => r.x),
    [chartData]
  );

const marketFilterSignature = useMemo(
    () =>
      JSON.stringify({
        selectedTags: selectedTags.map((o) => o.value).sort(),
        selectedPublishers: selectedPublishers.map((o) => o.value).sort(),
        selectedGenres: selectedGenres.map((o) => o.value).sort(),
        selectedDevelopers: selectedDevelopers.map((o) => o.value).sort(),
        selectedLiveService: selectedLiveService?.value || null,
        selectedPricing: selectedPricing?.value || null,
        selectedUGC: selectedUGC?.value || null,
        selectedFocus: selectedFocus.map((o) => o.value).sort(),
        selectedPlatforms: selectedPlatforms.map((o) => o.value).sort(),
        selectedDateRange: selectedDateRange.map((d) => (d ? new Date(d).toISOString().slice(0, 10) : null)),
        onlyUnreleased,
      }),
    [
      selectedTags,
      selectedPublishers,
      selectedGenres,
      selectedDevelopers,
      selectedLiveService,
      selectedPricing,
      selectedUGC,
      selectedFocus,
      selectedPlatforms,
      selectedDateRange,
      onlyUnreleased,
    ]
  );

// Build a separate key axis for Market Mind (independent of the chart slider).
const marketKeys = useMemo(() => {
  if (!marketDataReady || !["market", "publishers"].includes(viewMode)) return [];
  if (!filtered.length) return [];

  const keySet = new Set();
  for (const g of filtered) {
    const keys = g._dateKeys || [];
    for (const k of keys) keySet.add(k);
  }

  return Array.from(keySet).sort();
}, [marketDataReady, viewMode, marketFilterSignature, filtered]);

// Keys currently used for Market Mind averaging / growth.
// Presets are anchored to the latest available mindSHARE period in the current filtered universe.
const marketVisibleKeys = useMemo(() => {
  if (!marketKeys.length) return [];

  const selectedOption =
    MARKET_PERIOD_OPTIONS.find((opt) => opt.value === marketPeriod) ||
    MARKET_PERIOD_OPTIONS[0];

  if (selectedOption.value === "all" || !Number.isFinite(selectedOption.days)) {
    return marketKeys;
  }

  const latestDate = new Date(String(marketKeys[marketKeys.length - 1]));
  if (isNaN(latestDate)) return [marketKeys[marketKeys.length - 1]];

  latestDate.setHours(0, 0, 0, 0);
  const cutoff = new Date(latestDate);
  cutoff.setDate(cutoff.getDate() - Math.max(0, selectedOption.days - 1));

  const selectedKeys = marketKeys.filter((key) => {
    const d = new Date(String(key));
    if (isNaN(d)) return false;
    d.setHours(0, 0, 0, 0);
    return d >= cutoff && d <= latestDate;
  });

  return selectedKeys.length ? selectedKeys : [marketKeys[marketKeys.length - 1]];
}, [marketKeys, marketPeriod]);


  // Adapt filtered list into the shape the old MarketView bubble chart expects,
  // using the current window (visibleKeys) as the averaging horizon.
  const marketFiltered = useMemo(() => {
  if (!marketDataReady || !["market", "publishers"].includes(viewMode)) return [];
  const hasWindow = marketVisibleKeys && marketVisibleKeys.length > 0;

  const rows = filtered.filter((game) => matchesMarketSearch(game, searchTerm)).map((g, idx) => {
    // --- 1) Average mindSHARE across the current window ---
    let current = Number(g.views || 0); // fallback to latest
    if (hasWindow) {
      let sum = 0;
      let cnt = 0;
            for (const key of marketVisibleKeys) {
        const v = g._byDate.get(String(key));
        if (v != null) {
          sum += Number(v) || 0;
          cnt++;
        }
      }

      if (cnt > 0) current = sum / cnt;
    }

    // --- 2) Growth over the window (start → end) for radial distance ---
    let delta = Number(g.videos || 0); // fallback to weekly Δ
    if (hasWindow && marketVisibleKeys.length > 1) {
      const firstKey = marketVisibleKeys[0];
      const lastKey = marketVisibleKeys[marketVisibleKeys.length - 1];

      const firstVal = g._byDate.get(String(firstKey)) || 0;
      const lastVal = g._byDate.get(String(lastKey)) || 0;


      delta = lastVal - firstVal;
    }

    const previous = current - delta;
    const rawPercent =
      previous > 0 ? (delta / previous) * 100 : current > 0 ? 100 : 0;
    const growthPT = previous > 0 ? delta / previous : 0;

    return {
      ...g,
      id: g.id ?? idx,
      mindshare: current, // bubble size
      delta,
      percentGrowth: Number.isFinite(rawPercent) ? rawPercent : 0,
      growthPT, // bubble radial / color logic in MarketView

      // match the old naming style
      rank_mindshare: g.rank_mindshare ?? g.rank ?? null,
      rank_search: g.search_rank ?? null,
      rank_video: g.video_rank ?? null,
      rank_twitch: g.twitch_rank ?? null,
      search_volume: g.search_volume ?? null,
    };
  });

  // Steam-style: default sort by Growth %, optional sort by raw mindSHARE
  rows.sort((a, b) => {
    if (marketSort === "mindshare") {
      return (b.mindshare || 0) - (a.mindshare || 0);
    }
    // Growth: prefer growthPT, fall back to percentGrowth
    const ga = Number.isFinite(a.growthPT)
      ? a.growthPT
      : (a.percentGrowth || 0) / 100;
    const gb = Number.isFinite(b.growthPT)
      ? b.growthPT
      : (b.percentGrowth || 0) / 100;
    return gb - ga;
  });

  return rows;
}, [marketDataReady, viewMode, filtered, marketVisibleKeys, marketSort, searchTerm]);

const hasActiveMarketFilters = useMemo(() => (
  Boolean(searchTerm.trim()) ||
  selectedTags.length > 0 ||
  selectedPublishers.length > 0 ||
  selectedGenres.length > 0 ||
  selectedDevelopers.length > 0 ||
  Boolean(selectedLiveService) ||
  Boolean(selectedPricing) ||
  Boolean(selectedUGC) ||
  selectedFocus.length > 0 ||
  selectedPlatforms.length > 0 ||
  (!isAllReleaseDateRange && selectedDateRange.some(Boolean)) ||
  onlyUnreleased
), [
  searchTerm,
  selectedTags,
  selectedPublishers,
  selectedGenres,
  selectedDevelopers,
  selectedLiveService,
  selectedPricing,
  selectedUGC,
  selectedFocus,
  selectedPlatforms,
  selectedDateRange,
  isAllReleaseDateRange,
  onlyUnreleased,
]);

useEffect(() => {
  if (
    viewMode !== "trended" ||
    hasActiveMarketFilters ||
    hiddenCards.length > 0 ||
    showAllGames ||
    launchesShowAll ||
    launchChartPreset !== "custom" ||
    filtersOpen ||
    cardSearchOpen ||
    analyticsOpen ||
    chartBgChoice !== "none" ||
    mindshareXAxisMode !== "date" ||
    mindshareAgg !== "weekly"
  ) {
    setShowTopGrowth(false);
  }
}, [
  viewMode,
  hasActiveMarketFilters,
  hiddenCards,
  showAllGames,
  launchesShowAll,
  launchChartPreset,
  filtersOpen,
  cardSearchOpen,
  analyticsOpen,
  chartBgChoice,
  mindshareXAxisMode,
  mindshareAgg,
]);

const formatFilterValue = (opt) =>
  String(opt?.label ?? opt?.value ?? opt ?? "").trim();

const appliedMarketFilterLabel = useMemo(() => {
  const parts = [];
  if (searchTerm.trim()) parts.push(`Search - ${searchTerm.trim()}`);

  const addMulti = (label, values) => {
    const vals = (values || []).map(formatFilterValue).filter(Boolean);
    if (vals.length) parts.push(`${label} - ${vals.join(", ")}`);
  };

  addMulti("Tags", selectedTags);
  addMulti("Publishers", selectedPublishers);
  addMulti("Genre", selectedGenres);
  addMulti("Developers", selectedDevelopers);
  if (selectedLiveService) parts.push(`Business Model - ${formatFilterValue(selectedLiveService)}`);
  if (selectedPricing) parts.push(`Pricing - ${formatFilterValue(selectedPricing)}`);
  if (selectedUGC) parts.push(`UGC - ${formatFilterValue(selectedUGC)}`);
  addMulti("Play Type", selectedFocus);
  addMulti("Platform", selectedPlatforms);

  const [r0, r1] = coerceRangeToDates(selectedDateRange);
  if (r0 && r1 && !isAllReleaseDateRange) parts.push(`Release Date - ${fmtMDY(r0)} to ${fmtMDY(r1)}`);
  if (onlyUnreleased) parts.push("Release Status - Unreleased");

  return parts.join("  •  ");
}, [
  searchTerm,
  selectedTags,
  selectedPublishers,
  selectedGenres,
  selectedDevelopers,
  selectedLiveService,
  selectedPricing,
  selectedUGC,
  selectedFocus,
  selectedPlatforms,
  selectedDateRange,
  isAllReleaseDateRange,
  onlyUnreleased,
]);

const appliedMarketPreview = useMemo(() => {
  if (!hasActiveMarketFilters) return null;
  return {
    type: "appliedFilters",
    value: {
      searchTerm,
      selectedTags,
      selectedPublishers,
      selectedGenres,
      selectedDevelopers,
      selectedLiveService,
      selectedPricing,
      selectedUGC,
      selectedFocus,
      selectedPlatforms,
      selectedDateRange,
      onlyUnreleased,
    },
  };
}, [
  hasActiveMarketFilters,
  searchTerm,
  selectedTags,
  selectedPublishers,
  selectedGenres,
  selectedDevelopers,
  selectedLiveService,
  selectedPricing,
  selectedUGC,
  selectedFocus,
  selectedPlatforms,
  selectedDateRange,
  onlyUnreleased,
]);

const buildMarketRows = (sourceRows) => {
  const hasWindow = marketVisibleKeys && marketVisibleKeys.length > 0;

  const rows = sourceRows.map((g, idx) => {
    let current = Number(g.views || 0);
    if (hasWindow) {
      let sum = 0;
      let cnt = 0;
      for (const key of marketVisibleKeys) {
        const v = g._byDate.get(String(key));
        if (v != null) {
          sum += Number(v) || 0;
          cnt++;
        }
      }
      if (cnt > 0) current = sum / cnt;
    }

    let delta = Number(g.videos || 0);
    if (hasWindow && marketVisibleKeys.length > 1) {
      const firstKey = marketVisibleKeys[0];
      const lastKey = marketVisibleKeys[marketVisibleKeys.length - 1];
      const firstVal = g._byDate.get(String(firstKey)) || 0;
      const lastVal = g._byDate.get(String(lastKey)) || 0;
      delta = lastVal - firstVal;
    }

    const previous = current - delta;
    const rawPercent = previous > 0 ? (delta / previous) * 100 : current > 0 ? 100 : 0;
    const growthPT = previous > 0 ? delta / previous : 0;

    return {
      ...g,
      id: g.id ?? idx,
      mindshare: current,
      delta,
      percentGrowth: Number.isFinite(rawPercent) ? rawPercent : 0,
      growthPT,
      rank_mindshare: g.rank_mindshare ?? g.rank ?? null,
      rank_search: g.search_rank ?? null,
      rank_video: g.video_rank ?? null,
      rank_twitch: g.twitch_rank ?? null,
      search_volume: g.search_volume ?? null,
    };
  });

  rows.sort((a, b) => {
    if (marketSort === "mindshare") return (b.mindshare || 0) - (a.mindshare || 0);
    const ga = Number.isFinite(a.growthPT) ? a.growthPT : (a.percentGrowth || 0) / 100;
    const gb = Number.isFinite(b.growthPT) ? b.growthPT : (b.percentGrowth || 0) / 100;
    return gb - ga;
  });

  return rows;
};

const marketAllFiltered = useMemo(() => marketDataReady && viewMode === "market" ? buildMarketRows(games) : [], [marketDataReady, viewMode, games, marketVisibleKeys, marketSort]);
const splitMarketMainRows = marketSplitView && hasActiveMarketFilters ? marketAllFiltered : marketFiltered;
const splitMarketPreview = useMemo(() => {
  const current = marketSplitView && hasActiveMarketFilters ? appliedMarketPreview : preview;
  return [...(Array.isArray(current) ? current : current ? [current] : []), ...favoriteHighlights];
}, [marketSplitView, hasActiveMarketFilters, appliedMarketPreview, preview, favoriteHighlights]);
const splitMarketEnabled = marketSplitView && hasActiveMarketFilters && viewMode === "market";
const marketControlsLocked = splitMarketEnabled;

useEffect(() => {
  if (!hasActiveMarketFilters) setMarketSplitView(false);
}, [hasActiveMarketFilters]);




  // Analytics overlays for the currently plotted chart values (weekly or cumulative).
  const analyticsRenderData = useMemo(() => {
    const enabled = analyticsSeries || {};
    const anyEnabled = enabled.average || enabled.median || enabled.top5 || enabled.top10 || enabled.top25;
    if (!showInsights || !anyEnabled || !chartData.length || !targetsForChart.length) return [];

    const percentile = (values, p) => {
      if (!values.length) return null;
      const sorted = values.slice().sort((a, b) => a - b);
      const idx = (sorted.length - 1) * p;
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      if (lo === hi) return sorted[lo];
      return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
    };

    return chartData.map((row) => {
      const vals = [];
      for (const name of targetsForChart) {
        const val = Number(row[name]);
        if (Number.isFinite(val)) vals.push(val);
      }

      const out = { ...row };
      if (!vals.length) return out;

      if (enabled.average) out.__avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      if (enabled.median) out.__median = percentile(vals, 0.5);
      if (enabled.top5) out.__top5 = percentile(vals, 0.95);
      if (enabled.top10) out.__top10 = percentile(vals, 0.90);
      if (enabled.top25) out.__top25 = percentile(vals, 0.75);
      return out;
    });
  }, [showInsights, analyticsSeries, chartData, targetsForChart]);

  const avgRenderData = useMemo(
    () => analyticsRenderData.map((row) => ({ x: row.x, avg: row.__avg ?? null })),
    [analyticsRenderData]
  );

  const chartDataWithAverage = useMemo(() => {
    if (!analyticsRenderData.length) return chartData;
    return analyticsRenderData;
  }, [chartData, analyticsRenderData]);


  useEffect(() => {
    if (!chartBooting) return;
    if (!showInsights || !targetsForChart.length || !chartData.length) return;
    const t = window.setTimeout(() => setChartBooting(false), 120);
    return () => window.clearTimeout(t);
  }, [chartBooting, showInsights, targetsForChart.length, chartData.length]);

  const labelForMindIdx = (idx) => {
    const len = lineChartData.length;
    if (!len) return "";
    const x =
      lineChartData[Math.min(Math.max(idx, 0), len - 1)].x;
    if (mindshareXAxisMode === "w2l") {
      const n = Number(x);
      const signed = Number.isFinite(n) && n > 0 ? `+${n}` : String(x);
      return `W2L ${signed}`;
    }
    const d = new Date(String(x));
    return isNaN(d) ? String(x) : d.toLocaleDateString();
  };

  const formatMindshareXAxisTick = (v) => {
    if (mindshareXAxisMode === "w2l") {
      const n = Number(v);
      if (!Number.isFinite(n)) return String(v);
      return n > 0 ? `+${n}` : String(n);
    }
    return fmtMDY(v);
  };

  const handleMindshareXAxisTickClick = (tickValue) => {
    const idx = lineChartData.findIndex((row) => String(row.x) === String(tickValue));
    if (idx < 0) return;
    mindWindowUserAdjustedRef.current = true;
    setMindWinEndIdx(Math.max(idx, Math.min(lineChartData.length - 1, mindWinStartIdx + 1)));
  };

  const MindshareXAxisTick = ({ x, y, payload }) => {
    const raw = payload?.value;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#cfd8e3"
          fontSize={13}
          style={{
            cursor: "pointer",
            userSelect: "none",
            fontWeight: 700,
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleMindshareXAxisTickClick(raw);
          }}
        >
          {formatMindshareXAxisTick(raw)}
        </text>
      </g>
    );
  };

const marketPeriodLabel = useMemo(() => {
  const opt = MARKET_PERIOD_OPTIONS.find((item) => item.value === marketPeriod);
  return opt?.label || "Past 1 Week";
}, [marketPeriod]);

const marketWindowLabel = useMemo(() => {
  if (!marketKeys.length) return "—";

  const opt =
    MARKET_PERIOD_OPTIONS.find((item) => item.value === marketPeriod) ||
    MARKET_PERIOD_OPTIONS[0];

  const fmt = (key) => {
    const d = new Date(String(key));
    return isNaN(d) ? String(key) : d.toLocaleDateString();
  };

  if (opt.value === "all" || !Number.isFinite(opt.days)) {
    const first = marketKeys[0];
    const last = marketKeys[marketKeys.length - 1];
    return first === last ? fmt(last) : `${fmt(first)} - ${fmt(last)}`;
  }

  const latest = new Date(String(marketKeys[marketKeys.length - 1]));
  if (isNaN(latest)) return fmt(marketKeys[marketKeys.length - 1]);

  latest.setHours(0, 0, 0, 0);
  const start = new Date(latest);
  start.setDate(start.getDate() - Math.max(0, opt.days - 1));

  return `${fmt(start)} - ${fmt(latest)}`;
}, [marketKeys, marketPeriod]);

const cardsPeriodLabel = useMemo(() => {
  const latestKey = filtered.reduce((latest, game) => {
    const key = game._dateKeys?.[game._dateKeys.length - 1];
    return key && (!latest || key > latest) ? key : latest;
  }, null);
  if (!latestKey) return "Past Week";

  const fmt = (key) => {
    const d = new Date(String(key));
    return isNaN(d) ? String(key) : d.toLocaleDateString();
  };

  const latest = new Date(String(latestKey));
  if (isNaN(latest)) return "Past Week";
  latest.setHours(0, 0, 0, 0);
  const start = new Date(latest);
  start.setDate(start.getDate() - 6);
  return `Past Week ${fmt(start)} - ${fmt(latest)}`;
}, [filtered]);

  // Compute windowed Y max (include avg)
    const yMaxWindow = useMemo(() => {
    let hi = 0;
    for (const row of chartData) {
      for (const [k, v] of Object.entries(row)) {
        if (k === "x") continue;
        const n = Number(v);
        if (Number.isFinite(n) && n > hi) hi = n;
      }
    }
    for (const row of analyticsRenderData) {
      for (const key of ["__avg", "__median", "__top5", "__top10", "__top25"]) {
        const n = Number(row[key]);
        if (Number.isFinite(n) && n > hi) hi = n;
      }
    }
    return hi;
  }, [chartData, analyticsRenderData]);


  const yUpper = useMemo(
    () =>
      Math.max(
        0.000001,
        Math.ceil((yMaxWindow || 0) * 1.08 * 1e6) / 1e6
      ),
    [yMaxWindow]
  );

  const mindshareYAxisWidth = useMemo(() => {
    const label = (yUpper || 0).toLocaleString(undefined, {
      maximumFractionDigits: 3,
    });
    const minWidth = viewMode === "trended" || viewMode === "launches" ? 102 : 90;
    return Math.min(240, Math.max(minWidth, 42 + label.length * 10));
  }, [yUpper, viewMode]);

   // Custom dot with a large invisible hit area
const CustomDot = ({ cx, cy, stroke, seriesName, dim, onToggle }) => {
  if (cx == null || cy == null) return null;

  const handleEnter = () => setHoverSeriesKey(seriesName);
  const handleLeave = () => setHoverSeriesKey(null);

  return (
    <g>
      {/* Big invisible hit circle for easier hover targeting + click */}
      <circle
        cx={cx}
        cy={cy}
        r={10}
        fill="transparent"
        stroke="transparent"
        pointerEvents="all"
        onMouseOver={handleEnter}
        onMouseOut={handleLeave}
        onClick={onToggle}
      />
      {/* Visible dot – fades with line when dimmed */}
      <circle
        cx={cx}
        cy={cy}
        r={3}
        stroke="none"
        fill={stroke || "#fff"}
        fillOpacity={dim ? 0.25 : 1}
        pointerEvents="none"
      />
    </g>
  );
};

  const toggleSeriesHighlight = (name) => {
    setHighlighted((prev) => {
      const s = new Set(prev);
      if (s.size === 1 && s.has(name)) {
        // clicking again clears
        s.clear();
      } else {
        s.clear();
        s.add(name);
      }
      return s;
    });

    setHoverSeriesKey((prev) => (prev === name ? null : name));
  };


  
  /* ------------------------------ Layout tweaks ------------------------------ */
  const [gridLeftPad, setGridLeftPad] = useState(0);
  const gridHostRef = useRef(null);

  useEffect(() => {
    const CARD_WIDTH = 200;
    const GRID_COL_GAP = 12;

    const computeLeft = () => {
      const host = gridHostRef.current;
      if (!host) return;
      const w = host.clientWidth || 0;
      if (w <= 0) return;
      const cols = Math.max(
        1,
        Math.floor((w + GRID_COL_GAP) / (CARD_WIDTH + GRID_COL_GAP))
      );
      const gridW =
        cols * CARD_WIDTH +
        Math.max(0, cols - 1) * GRID_COL_GAP;
      const offset = Math.max(
        0,
        Math.floor((w - gridW) / 2)
      );
      setGridLeftPad(offset);
    };

    computeLeft();
    window.addEventListener("resize", computeLeft);
    return () => window.removeEventListener("resize", computeLeft);
  }, []);

  /* ---------------- Custom Legend + manager ---------------- */
  const removeSeriesFromChart = (name) => {
    if (!name) return;
    if (favoriteChartActive) {
      const currentSelection = favoriteSelectionEdited || launchChartPreset === "top100"
        ? comparisonGames
        : chartTargetsRef.current;
      setComparisonGames(currentSelection.filter(seriesName => seriesName !== name));
      setFavoriteSelectionEdited(true);
      setShowInsights(true);
    } else if (viewMode === "launches" || showAllGames) {
      setLegendHidden((prev) => {
        const s = new Set(prev);
        s.add(name);
        return s;
      });
    } else {
      const nextComparisonGames = comparisonGames.filter((seriesName) => seriesName !== name);
      setComparisonGames(nextComparisonGames);
      if (viewMode === "trended") setShowInsights(nextComparisonGames.length > 0);
    }
    setHighlighted((h) => {
      const h2 = new Set(h);
      h2.delete(name);
      return h2;
    });
    setHoverSeriesKey((prev) => (prev === name ? null : prev));
  };

  const LegendContent = () => {
    const MAX_INLINE = 24;
    const baseNames =
      targetsForChart.length > 0
        ? targetsForChart
        : filtered.slice(0, 5).map((g) => g.name).sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );

    const names = baseNames.slice(0, MAX_INLINE);
    const hasMore = baseNames.length > MAX_INLINE;
    const searchableNames = baseNames.filter((name) =>
      name.toLowerCase().includes(seriesQuery.trim().toLowerCase())
    );

    const clearHighlights = () => setHighlighted(new Set());
    const restoreHidden = () => setLegendHidden(new Set());

    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          paddingTop: 10,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={() => setLegendOpen((v) => !v)}
          onMouseEnter={() => legendOpen && setLegendToggleHover(true)}
          onMouseLeave={() => setLegendToggleHover(false)}
          aria-label={legendOpen ? "Hide legend" : "Show legend"}
          style={{
            width: "auto",
            height: 26,
            minWidth: legendOpen ? 26 : 104,
            borderRadius: 8,
            border: "1px solid rgba(255,255,255,0.16)",
            background: "rgba(255,255,255,0.03)",
            color: "rgba(238,243,247,0.88)",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 750,
            lineHeight: "15px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            padding: legendOpen ? "0" : "0 8px",
            flexShrink: 0,
            position: "relative",
            order: 1,
          }}
        >
          {legendOpen ? "−" : "+ Show Legend"}
          {legendOpen && legendToggleHover && (
            <span
              style={{
                position: "absolute",
                left: 0,
                bottom: 36,
                whiteSpace: "nowrap",
                background: "#050505",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.16)",
                borderRadius: 7,
                padding: "6px 8px",
                fontSize: 11,
                fontWeight: 850,
                lineHeight: "14px",
                boxShadow: "0 12px 28px rgba(0,0,0,0.85)",
                pointerEvents: "none",
                zIndex: 100,
              }}
            >
              {legendOpen ? "Hide legend" : "Show legend"}
            </span>
          )}
        </button>

        <div style={{ position: "relative", display: "inline-flex", alignItems: "center", flexShrink: 0, order: 0 }}>
          <button
            type="button"
            onClick={() => {
              setLegendSearchOpen((v) => !v);
              if (legendSearchOpen) setSearchTerm("");
            }}
            title="Search games to add"
            aria-label="Search games to add"
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              border: legendSearchOpen ? "1px solid rgba(32,165,187,0.65)" : "1px solid rgba(255,255,255,0.16)",
              background: legendSearchOpen ? "rgba(32,165,187,0.12)" : "rgba(255,255,255,0.03)",
              color: legendSearchOpen ? "#20a5bb" : "rgba(238,243,247,0.88)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
              flexShrink: 0,
            }}
          >
            <SearchIcon size={14} />
          </button>

          <div
            style={{
              width: legendSearchOpen ? 190 : 0,
              opacity: legendSearchOpen ? 1 : 0,
              overflow: "hidden",
              transition: "width 180ms ease, opacity 150ms ease",
              marginLeft: legendSearchOpen ? 6 : 0,
            }}
          >
            {/* Filters the card grid below so games can be added straight from the cards. */}
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setSearchTerm("");
                  setLegendSearchOpen(false);
                }
              }}
              placeholder="Search games below..."
              style={{
                width: 190,
                height: 26,
                borderRadius: 8,
                border: "1px solid rgba(32,165,187,0.55)",
                background: "#0c0c0c",
                color: "#fff",
                fontSize: 12,
                padding: "0 9px",
                boxSizing: "border-box",
                outline: "none",
              }}
            />
          </div>
        </div>

        {legendOpen && (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 8,
              rowGap: 8,
              justifyContent: "flex-start",
              alignItems: "center",
              position: "relative",
              minWidth: 0,
              flex: 1,
              order: 2,
            }}
          >
            {names.map((name) => {
          const idx = targetsForChart.findIndex((n) => n === name);
          const color = getChartColor(name, Math.max(0, idx));
          const isHi = highlighted.size > 0 ? highlighted.has(name) : true;
          const dim = highlighted.size > 0 && !isHi;
          const isHidden = legendHidden.has(name);

          return (
            <span
              key={name}
              onClick={(e) => {
                const multi = e.ctrlKey || e.metaKey;
                setHighlighted((prev) => {
                  const s = new Set(prev);
                  if (multi) {
                    if (s.has(name)) s.delete(name);
                    else s.add(name);
                  } else if (s.size === 1 && s.has(name)) {
                    s.clear();
                  } else {
                    s.clear();
                    s.add(name);
                  }
                  return s;
                });
              }}
              title={
                highlighted.has(name)
                  ? "Unhighlight (click). Ctrl/Cmd+Click to multi-select."
                  : "Highlight — Ctrl/Cmd+Click to multi-select."
              }
              style={{
                cursor: "pointer",
                color: "#fff",
                fontWeight: 750,
                fontSize: 11,
                lineHeight: "15px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                opacity: isHidden ? 0.45 : dim ? 0.62 : 1,
                background: "transparent",
                boxShadow: highlighted.has(name)
                  ? `0 0 16px ${color}66, inset 0 0 0 1px ${color}22`
                  : "inset 0 0 0 1px rgba(255,255,255,0.03)",
                padding: "3px 4px",
                borderRadius: 7,
                userSelect: "none",
                textShadow: "0 1px 4px rgba(0,0,0,0.85)",
              }}
            >
              <input
                type="color"
                value={color}
                onChange={(e) => setChartColors((prev) => ({ ...prev, [name]: e.target.value }))}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                title={`Change ${name} color`}
                aria-label={`Change ${name} color`}
                style={{
                  width: 12,
                  height: 12,
                  padding: 0,
                  border: "2px solid #050505",
                  borderRadius: 4,
                  background: color,
                  cursor: "pointer",
                  flexShrink: 0,
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.28), 0 2px 7px rgba(0,0,0,0.65)",
                }}
              />
              <span
                style={{
                  maxWidth: 190,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {name}
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSeriesFromChart(name);
                }}
                title="Remove series"
                style={{
                  width: 16,
                  height: 16,
                  border: "none",
                  background: "transparent",
                  color: "#fff",
                  opacity: 0.72,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  lineHeight: 1,
                }}
              >
                <CloseIcon size={12} strokeWidth={1.8} aria-hidden="true" />
              </button>
            </span>
          );
        })}

            {hasMore && (
              <div ref={seriesManagerRef} style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setSeriesManagerOpen((v) => !v)}
              style={{
                height: 30,
                borderRadius: 7,
                border: "1px solid rgba(32,165,187,0.55)",
                background: "rgba(5,5,5,0.92)",
                color: "#cfeeff",
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 900,
                padding: "0 10px",
                boxShadow: "0 0 12px rgba(32,165,187,0.10)",
              }}
            >
              Manage {baseNames.length.toLocaleString()} series ▾
            </button>
            {seriesManagerOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  bottom: 36,
                  width: 560,
                  maxWidth: "calc(calc(var(--mg-vw) * 100) - 80px)",
                  maxHeight: 430,
                  zIndex: 50,
                  background: "#070707",
                  border: "1px solid rgba(255,255,255,0.16)",
                  borderRadius: 10,
                  boxShadow: "0 18px 45px rgba(0,0,0,0.85)",
                  padding: 10,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div style={{ color: "#fff", fontSize: 13, fontWeight: 950 }}>
                    Chart Series
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      onClick={clearHighlights}
                      style={{
                        height: 24,
                        borderRadius: 6,
                        border: "1px solid #333",
                        background: "#111",
                        color: "#ddd",
                        fontSize: 11,
                        fontWeight: 850,
                        cursor: "pointer",
                      }}
                    >
                      Clear focus
                    </button>
                    <button
                      type="button"
                      onClick={restoreHidden}
                      style={{
                        height: 24,
                        borderRadius: 6,
                        border: "1px solid #333",
                        background: "#111",
                        color: "#ddd",
                        fontSize: 11,
                        fontWeight: 850,
                        cursor: "pointer",
                      }}
                    >
                      Restore hidden
                    </button>
                  </div>
                </div>

                <input
                  value={seriesQuery}
                  onChange={(e) => setSeriesQuery(e.target.value)}
                  placeholder="Search series..."
                  style={{
                    width: "100%",
                    height: 30,
                    borderRadius: 7,
                    border: "1px solid #333",
                    background: "#0e0e0e",
                    color: "#fff",
                    padding: "0 9px",
                    marginBottom: 8,
                    fontSize: 12,
                    boxSizing: "border-box",
                    outline: "none",
                  }}
                />

                <div style={{ maxHeight: 340, overflowY: "auto", paddingRight: 2 }}>
                  {searchableNames.map((name) => {
                    const idx = targetsForChart.findIndex((n) => n === name);
                    const color = chartSeriesColor(Math.max(0, idx));
                    const isHi = highlighted.has(name);
                    const isHidden = legendHidden.has(name);

                    return (
                      <div
                        key={name}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "10px minmax(0,1fr) auto auto",
                          alignItems: "center",
                          gap: 8,
                          height: 30,
                          borderBottom: "1px solid rgba(255,255,255,0.05)",
                          opacity: isHidden ? 0.45 : 1,
                        }}
                      >
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 99,
                            background: color,
                            boxShadow: `0 0 8px ${color}99`,
                          }}
                        />
                        <span
                          title={name}
                          style={{
                            color: "#eee",
                            fontSize: 12,
                            fontWeight: 800,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {name}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setHighlighted((prev) => {
                              const s = new Set(prev);
                              if (s.has(name)) s.delete(name);
                              else s.add(name);
                              return s;
                            });
                          }}
                          style={{
                            height: 22,
                            borderRadius: 6,
                            border: `1px solid ${isHi ? color : "#333"}`,
                            background: isHi ? `${color}22` : "#101010",
                            color: isHi ? "#fff" : "#ccc",
                            fontSize: 11,
                            fontWeight: 850,
                            cursor: "pointer",
                          }}
                        >
                          Focus
                        </button>
                        <button
                          type="button"
                          onClick={() => removeSeriesFromChart(name)}
                          style={{
                            height: 22,
                            width: 24,
                            borderRadius: 6,
                            border: "1px solid #333",
                            background: "#101010",
                            color: "#fff",
                            fontSize: 14,
                            fontWeight: 950,
                            cursor: "pointer",
                            lineHeight: "18px",
                          }}
                          title="Remove series"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
          </div>
        )}
      </div>
    );
  };

  const AnalyticsMenu = () => {
    const items = [
      { key: "average", label: "Average" },
      { key: "median", label: "Median" },
      { key: "top5", label: "Top 5%" },
      { key: "top10", label: "Top 10%" },
      { key: "top25", label: "Top 25%" },
    ];
    const activeCount = items.filter((item) => analyticsSeries[item.key]).length;

    return (
      <div ref={analyticsRef} style={{ position: "relative", flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => setAnalyticsOpen((v) => !v)}
          style={{
            height: 32,
            borderRadius: 7,
            border: "1px solid rgba(255,255,255,0.16)",
            background: activeCount ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)",
            color: activeCount ? "#fff" : "#cfd8e3",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 9,
            padding: "0 10px",
            fontSize: 12,
            fontWeight: 900,
            minWidth: 168,
            height: 26,
            borderRadius: 8,
            boxShadow: activeCount ? "0 0 12px rgba(180,190,200,0.16)" : "none",
          }}
        >
          <span>{activeCount ? `Benchmarks (${activeCount})` : "Benchmarks"}</span>
          <span style={{ transform: analyticsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s ease" }}>▾</span>
        </button>

        {analyticsOpen && (
          <div
            style={{
              position: "absolute",
              top: 36,
              right: 0,
              width: 190,
              zIndex: 1000,
              background: "#070707",
              border: "1px solid rgba(255,255,255,0.16)",
              borderRadius: 10,
              boxShadow: "0 18px 45px rgba(0,0,0,0.85)",
              padding: 6,
            }}
          >
            {items.map((item) => (
              <label
                key={item.key}
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#ddd",
                  fontSize: 12,
                  fontWeight: 850,
                  cursor: "pointer",
                  borderRadius: 8,
                  padding: "0 6px",
                }}
              >
                <input
                  type="checkbox"
                  checked={Boolean(analyticsSeries[item.key])}
                  onChange={(e) =>
                    setAnalyticsSeries((prev) => ({ ...prev, [item.key]: e.target.checked }))
                  }
                  style={{ accentColor: "#20a5bb" }}
                />
                {item.label}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };


     /* ------------------------- Custom per-point Tooltip ------------------------- */
    const PointTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const chosen = selectMindshareTooltipPoint(payload, highlighted, hoverSeriesKey);
    if (!chosen) return null;

    const name = String(chosen.name || chosen.dataKey || "");
    const game = getGameByName(name);
    const value = Number(chosen.value) || 0;
    if (!game) return null;

    const key = label;
    const meta =
      mindshareXAxisMode === "w2l"
        ? game._metaByW2L.get(Number(key)) || {}
        : game._metaByDate.get(String(key)) || {};

    let delta = null;
    if (mindshareAgg !== "cumulative") {
      const keys = mindshareXAxisMode === "w2l" ? game._w2lKeys : game._dateKeys;
      const i = keys.findIndex((k) => String(k) === String(key));
      if (i > 0) {
        const prevKey = keys[i - 1];
        const prevVal = levelAt(game, prevKey) || 0;
        delta = value - prevVal;
      }
    }

    const box = {
      name,
      value,
      delta,
      image: game.imageUrl,
      publisher: game.publisher,
      developer: game.developer,
      description: game.description,
      release_date: game.release_date,
      genres: game.genres,
      tags: game.tags,
      live_service_static: game.live_service_static,
      pricing: game.pricing,
      user_generated_content: game.user_generated_content,
      single_multiplayer_focus: game.single_multiplayer_focus,
      platforms: game.platforms,
      rank_mindshare: meta?.rank,
      search_rank: meta?.searchRank,
      video_rank: meta?.videoRank,
      twitch_rank: meta?.twitchRank,
      search_volume: meta?.search,
      xLabel: mindshareXAxisMode === "w2l" ? `W2L ${key}` : fmtMDY(String(key)),
    };

    return <GameDetailsTooltip box={box} />;
  };


  /* ------------------------------ Business Charts ------------------------------ */
  useEffect(() => {
    if (viewMode !== "business") return;
    let cancelled = false;
    (async () => {
      try {
        setBusinessLoading(true);
        const base = BUSINESS_ROLLUPS_BASE_URL;
        const [rollup, index] = await Promise.all([
          fetchGzipJson(`${base}${businessDimension}/top100_${businessWindow}.json.gz`),
          fetchGzipJson(`${base}${businessDimension}/index.json.gz`),
        ]);
        if (cancelled) return;
        setBusinessRollup(rollup);
        setBusinessIndex(index);
        setBusinessExternalSeries({});
        setHighlighted(new Set());
        setLegendHidden(new Set());
        setHoverSeriesKey(null);
        const defaultNames = (rollup?.series || []).slice(0, 100).map(getBusinessSeriesName).filter(Boolean);
        setBusinessSeries(defaultNames);
      } catch (e) {
        console.error("❌ business rollup load error:", e);
        if (!cancelled) {
          setBusinessRollup(null);
          setBusinessIndex(null);
          setBusinessSeries([]);
        }
      } finally {
        if (!cancelled) setBusinessLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewMode, businessDimension, businessWindow]);

  const businessIndexEntries = useMemo(() => {
    const raw = businessIndex?.series || businessRollup?.series || [];
    return raw
      .map((entry) => ({
        name: getBusinessSeriesName(entry),
        slug: getBusinessSeriesSlug(entry),
        latest_share: Number(entry?.latest_share),
        latest_rank: Number(entry?.latest_rank),
      }))
      .filter((entry) => entry.name);
  }, [businessIndex, businessRollup]);

  const businessIndexByName = useMemo(() => {
    const m = new Map();
    for (const entry of businessIndexEntries) m.set(entry.name, entry);
    return m;
  }, [businessIndexEntries]);

  const addBusinessSeries = async (name) => {
    if (!name || businessSeries.includes(name)) return;
    setBusinessSeries((prev) => [...prev, name]);

    const rollupHasSeries = Boolean((businessRollup?.series || []).map(getBusinessSeriesName).includes(name));
    if (rollupHasSeries || businessExternalSeries[name]) return;

    const entry = businessIndexByName.get(name);
    const slug = entry?.slug || businessSlugify(name);
    try {
      setBusinessSeriesLoading(name);
      const data = await fetchGzipJson(
        `${BUSINESS_ROLLUPS_BASE_URL}${businessDimension}/series/${slug}.json.gz`
      );
      setBusinessExternalSeries((prev) => ({ ...prev, [name]: data }));
    } catch (e) {
      console.error("❌ business series load error:", name, e);
    } finally {
      setBusinessSeriesLoading(null);
    }
  };

  const removeBusinessSeries = (name) => {
    setBusinessSeries((prev) => prev.filter((n) => n !== name));
    setLegendHidden((prev) => { const set = new Set(prev); set.delete(name); return set; });
    setHighlighted((prev) => { const set = new Set(prev); set.delete(name); return set; });
    setHoverSeriesKey((prev) => (prev === name ? null : prev));
  };

  const businessChartData = useMemo(() => {
    const rowsByPeriod = new Map();
    const ensure = (period) => {
      if (!rowsByPeriod.has(period)) rowsByPeriod.set(period, { period });
      return rowsByPeriod.get(period);
    };

    for (const row of businessRollup?.rows || []) {
      const period = row.period || row.date || row.Period;
      if (!period) continue;
      const out = ensure(period);
      for (const name of businessSeries) {
        if (row[name] == null) continue;
        const v = Number(row[name]);
        if (Number.isFinite(v)) out[name] = v;
      }
    }

    for (const name of businessSeries) {
      const payload = businessExternalSeries[name];
      if (!payload) continue;
      for (const row of payload.rows || payload.data || []) {
        const period = row.period || row.date || row.Period;
        if (!period) continue;
        const v = pickBusinessValue(row, name);
        if (Number.isFinite(v)) ensure(period)[name] = v;
      }
    }

    return Array.from(rowsByPeriod.values()).sort((a, b) =>
      String(a.period).localeCompare(String(b.period))
    );
  }, [businessRollup, businessExternalSeries, businessSeries]);

  const businessVisibleSeries = useMemo(
    () => businessSeries.filter((name) => !legendHidden.has(name) && businessChartData.some((row) => Number.isFinite(Number(row[name])))),
    [businessSeries, businessChartData, legendHidden]
  );

  const businessSearchResults = useMemo(() => {
    const q = businessQuery.trim().toLowerCase();
    return businessIndexEntries
      .filter((entry) => !businessSeries.includes(entry.name))
      .filter((entry) => !q || entry.name.toLowerCase().includes(q))
      .slice(0, 60);
  }, [businessIndexEntries, businessSeries, businessQuery]);

  const businessAnalyticsRenderData = useMemo(() => {
    const enabled = analyticsSeries || {};
    const anyEnabled = enabled.average || enabled.median || enabled.top5 || enabled.top10 || enabled.top25;
    if (viewMode !== "business" || !anyEnabled || !businessChartData.length || !businessVisibleSeries.length) return [];

    const percentile = (values, pct) => {
      if (!values.length) return null;
      const sorted = values.slice().sort((a, b) => a - b);
      const idx = (sorted.length - 1) * pct;
      const lo = Math.floor(idx);
      const hi = Math.ceil(idx);
      if (lo === hi) return sorted[lo];
      return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
    };

    return businessChartData.map((row) => {
      const vals = [];
      for (const name of businessVisibleSeries) {
        const val = Number(row[name]);
        if (Number.isFinite(val)) vals.push(val);
      }
      const out = { ...row };
      if (!vals.length) return out;
      if (enabled.average) out.__avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      if (enabled.median) out.__median = percentile(vals, 0.5);
      if (enabled.top5) out.__top5 = percentile(vals, 0.95);
      if (enabled.top10) out.__top10 = percentile(vals, 0.90);
      if (enabled.top25) out.__top25 = percentile(vals, 0.75);
      return out;
    });
  }, [viewMode, analyticsSeries, businessChartData, businessVisibleSeries]);

  const businessChartDataWithAverage = useMemo(() => {
    if (!businessAnalyticsRenderData.length) return businessChartData;
    return businessAnalyticsRenderData;
  }, [businessChartData, businessAnalyticsRenderData]);

  const businessYMax = useMemo(() => {
    let hi = 0;
    for (const row of businessChartDataWithAverage) {
      for (const [k, v] of Object.entries(row)) {
        if (k === "period") continue;
        const n = Number(v);
        if (Number.isFinite(n) && n > hi) hi = n;
      }
    }
    return Math.max(0.000001, Math.ceil(hi * 1.08 * 1e6) / 1e6);
  }, [businessChartDataWithAverage]);

  const businessHeaderMeta = useMemo(() => {
    const copy = {
      publishers: {
        title: "Publishers",
        description:
          "Track aggregate mindSHARE by publisher over time. Starts with the top 100, then add any publisher from the series manager.",
      },
      developers: {
        title: "Developers",
        description:
          "Track aggregate mindSHARE by developer over time to spot studio momentum, launch spikes, and long-tail strength.",
      },
      genres: {
        title: "Genres",
        description:
          "Track genre-level attention shifts across the market using the same weekly mindSHARE history.",
      },
    };
    return copy[businessDimension] || copy.publishers;
  }, [businessDimension]);

  const BusinessPointTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    let metricPayload = payload.filter((p) => p && !String(p.dataKey || "").startsWith("__"));

    // If the user has focused/highlighted a business series, keep the tooltip locked
    // to that focused series at the hovered period instead of letting Recharts pick
    // any nearby/top series. This makes dense 100-series charts much easier to read.
    if (highlighted.size > 0) {
      metricPayload = metricPayload.filter((p) => highlighted.has(String(p.dataKey || p.name || "")));
    }

    const chosen =
      metricPayload.find((p) => p.dataKey === hoverSeriesKey) ||
      metricPayload.sort((a, b) => Number(b.value || 0) - Number(a.value || 0))[0];

    if (!chosen) return null;

    const seriesName = String(chosen.dataKey || chosen.name || "");
    const seriesValue = Number(chosen.value || 0);
    const period = String(label || "");

    const matchesSeries = (game) => {
      if (businessDimension === "publishers") return game.publisher === seriesName;
      if (businessDimension === "developers") return game.developer === seriesName;
      if (businessDimension === "genres") return (game.genres || []).includes(seriesName);
      return false;
    };

    const topGames = historicalGames
      .filter(matchesSeries)
      .map((game) => ({
        game,
        value: Number(game._byDate?.get(period) ?? 0),
      }))
      .filter((row) => Number.isFinite(row.value) && row.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return (
      <div
        style={{
          background: "linear-gradient(180deg, rgba(18,18,18,0.98), rgba(8,8,8,0.98))",
          border: "1px solid rgba(255,255,255,0.14)",
          color: "#fff",
          padding: 12,
          borderRadius: 12,
          minWidth: 360,
          maxWidth: 460,
          boxShadow: "0 22px 60px rgba(0,0,0,0.72), 0 0 0 1px rgba(32,165,187,0.08)",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "#fff", fontSize: 17, fontWeight: 950, letterSpacing: "-0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {seriesName}
            </div>
            <div style={{ color: "#9fb7c9", fontSize: 12, fontWeight: 850, marginTop: 2 }}>
              {fmtMDY(period)} · {businessHeaderMeta.title.slice(0, -1)} rollup
            </div>
          </div>
          <div style={{ color: "#fff", fontSize: 18, fontWeight: 950 }}>
            {pct3(seriesValue)}
          </div>
        </div>

        <div style={{ height: 1, background: "rgba(255,255,255,0.10)", margin: "9px 0" }} />

        <div style={{ color: "#cfd8e3", fontSize: 11, fontWeight: 950, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 }}>
          Top games at this point
        </div>

        {!mindIndexReady ? <div role="status">Loading complete mindSHARE history...</div> : topGames.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {topGames.map(({ game, value }, idx) => (
              <div key={`${game.name}-${idx}`} style={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr) auto", alignItems: "center", gap: 8 }}>
                <div style={{ width: 28, height: 36, borderRadius: 5, overflow: "hidden", background: "#111", border: "1px solid rgba(255,255,255,0.10)" }}>
                  {game.imageUrl ? (
                    <img src={game.imageUrl} alt={game.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  ) : null}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "#fff", fontSize: 12, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {game.name}
                  </div>
                  <div style={{ color: "#8fa0ad", fontSize: 10, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {game.release_date ? `Release ${fmtMDY(game.release_date)}` : "No release date"}
                  </div>
                </div>
                <div style={{ color: "#cfeeff", fontSize: 12, fontWeight: 950 }}>
                  {pct3(value)}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: "#8f8f8f", fontSize: 12, fontWeight: 800 }}>
            No game-level rows found for this period.
          </div>
        )}
      </div>
    );
  };


  const BusinessBoxesView = () => {
    const [hoveredBox, setHoveredBox] = useState(null);
    const [selectedBox, setSelectedBox] = useState(null);

    const boxDims = useMemo(() => {
      const h = Math.min(mindshareChartHeight, Math.max(420, layoutViewportHeight() - 108));
      return { w: 1200, h: Math.max(360, h - 6) };
    }, [mindshareChartHeight]);

    const latestBusinessPeriod = useMemo(() => {
      if (businessRollup?.latest_period) return businessRollup.latest_period;
      const last = businessChartData[businessChartData.length - 1];
      return last?.period || "";
    }, [businessRollup, businessChartData]);

    const businessBoxRows = useMemo(() => {
      const latestRow =
        businessChartDataWithAverage.find((row) => String(row.period) === String(latestBusinessPeriod)) ||
        businessChartDataWithAverage[businessChartDataWithAverage.length - 1] ||
        {};

      return businessVisibleSeries
        .map((name, idx) => {
          const value = Number(latestRow[name]);
          if (!Number.isFinite(value) || value <= 0) return null;
          return { id: `${businessDimension}-${name}`, name, value, color: chartSeriesColor(idx) };
        })
        .filter(Boolean)
        .sort((a, b) => b.value - a.value);
    }, [businessChartDataWithAverage, businessVisibleSeries, latestBusinessPeriod, businessDimension]);

    const businessBoxNodes = useMemo(() => {
      if (!businessBoxRows.length) return [];
      const root = d3
        .hierarchy({ children: businessBoxRows })
        .sum((d) => d.value)
        .sort((a, b) => (b.value || 0) - (a.value || 0));

      d3
        .treemap()
        .tile(d3.treemapSquarify.ratio(1.18))
        .size([boxDims.w, boxDims.h])
        .paddingInner(0.8)
        .round(true)(root);

      return root.leaves();
    }, [businessBoxRows, boxDims.w, boxDims.h]);

    const buildBusinessBoxTooltip = (leaf) => {
      if (!leaf) return null;
      setBusinessHistoryRequested(true);
      const x = (leaf.x0 + leaf.x1) / 2;
      const y = (leaf.y0 + leaf.y1) / 2;
      const seriesName = leaf.data.name;
      const period = latestBusinessPeriod;

      const matchesSeries = (game) => {
        if (businessDimension === "publishers") return game.publisher === seriesName;
        if (businessDimension === "developers") return game.developer === seriesName;
        if (businessDimension === "genres") return (game.genres || []).includes(seriesName);
        return false;
      };

      const topGames = historicalGames
        .filter(matchesSeries)
        .map((game) => ({ game, value: Number(game._byDate?.get(period) ?? 0) }))
        .filter((row) => Number.isFinite(row.value) && row.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 8);

      return { x, y, name: seriesName, value: leaf.data.value, color: leaf.data.color, period, topGames };
    };

    const activeBox = selectedBox || hoveredBox;

    return (
      <div style={{ flex: "1 1 auto", minHeight: 0, width: "100%", height: "100%", position: "relative", overflow: "hidden", borderRadius: 8, background: "radial-gradient(1000px 520px at 50% -10%, rgba(32,165,187,0.08), transparent 58%)" }}>
        {!businessBoxNodes.length ? (
          <div style={{ color: "#888", fontSize: 13, fontWeight: 850, display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
            No business rollup rows available for boxes.
          </div>
        ) : (
          <svg width="100%" height="100%" viewBox={`0 0 ${boxDims.w} ${boxDims.h}`} preserveAspectRatio="none">
            {businessBoxNodes.map((leaf, idx) => {
              const x = leaf.x0;
              const y = leaf.y0;
              const w = Math.max(0, leaf.x1 - leaf.x0);
              const h = Math.max(0, leaf.y1 - leaf.y0);
              const area = w * h;
              const focused = highlighted.size === 0 || highlighted.has(leaf.data.name);
              const dim = !focused;
              const showLabel = area > 1600 && w > 46 && h > 22;
              const maxChars = w > 190 ? 30 : w > 110 ? 18 : 10;
              const label = leaf.data.name.length > maxChars ? `${leaf.data.name.slice(0, maxChars)}…` : leaf.data.name;

              return (
                <g
                  key={leaf.data.id || idx}
                  opacity={dim ? 0.24 : 1}
                  onMouseEnter={() => !selectedBox && setHoveredBox(buildBusinessBoxTooltip(leaf))}
                  onMouseMove={() => !selectedBox && setHoveredBox(buildBusinessBoxTooltip(leaf))}
                  onMouseLeave={() => setHoveredBox(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = buildBusinessBoxTooltip(leaf);
                    setSelectedBox(selectedBox?.name === next.name ? null : next);
                    setHoveredBox(null);
                    setHighlighted((prev) => {
                      const set = new Set(prev);
                      if (set.size === 1 && set.has(leaf.data.name)) set.clear();
                      else {
                        set.clear();
                        set.add(leaf.data.name);
                      }
                      return set;
                    });
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    rx={2}
                    ry={2}
                    fill={leaf.data.color}
                    fillOpacity={0.88}
                    stroke={selectedBox?.name === leaf.data.name ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.52)"}
                    strokeWidth={selectedBox?.name === leaf.data.name ? 1.6 : 0.5}
                    vectorEffect="non-scaling-stroke"
                  />
                  {showLabel && (
                    <>
                      <text x={x + w / 2} y={y + h / 2 - 7} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize={Math.max(10, Math.min(15, Math.sqrt(area) / 16))} fontWeight={900} style={{ pointerEvents: "none", userSelect: "none", paintOrder: "stroke", stroke: "rgba(0,0,0,0.62)", strokeWidth: 3 }}>
                        {label}
                      </text>
                      <text x={x + w / 2} y={y + h / 2 + 10} textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.86)" fontSize={Math.max(9, Math.min(12, Math.sqrt(area) / 22))} fontWeight={850} style={{ pointerEvents: "none", userSelect: "none", paintOrder: "stroke", stroke: "rgba(0,0,0,0.62)", strokeWidth: 3 }}>
                        {pct3(leaf.data.value)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
          </svg>
        )}

        {activeBox && (
          <div
            style={{
              position: "absolute",
              left: `${Math.min(Math.max(12, (activeBox.x / boxDims.w) * 100), 72)}%`,
              top: `${Math.min(Math.max(12, (activeBox.y / boxDims.h) * 100), 70)}%`,
              transform: "translate(-8%, -8%)",
              width: 390,
              maxWidth: "calc(100% - 28px)",
              background: "linear-gradient(180deg, rgba(18,18,18,0.98), rgba(8,8,8,0.98))",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 12,
              padding: 12,
              boxShadow: "0 22px 60px rgba(0,0,0,0.72), 0 0 0 1px rgba(32,165,187,0.08)",
              zIndex: 20,
              pointerEvents: selectedBox ? "auto" : "none",
            }}
          >
            {selectedBox && (
              <button type="button" onClick={() => setSelectedBox(null)} style={{ position: "absolute", right: 8, top: 8, width: 24, height: 24, borderRadius: 7, border: "1px solid rgba(255,255,255,0.14)", background: "rgba(0,0,0,0.45)", color: "#fff", cursor: "pointer", fontWeight: 950 }}>×</button>
            )}
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingRight: selectedBox ? 26 : 0 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#fff", fontSize: 17, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{activeBox.name}</div>
                <div style={{ color: "#9fb7c9", fontSize: 12, fontWeight: 850, marginTop: 2 }}>{fmtMDY(activeBox.period)} · Boxes</div>
              </div>
              <div style={{ color: "#fff", fontSize: 18, fontWeight: 950 }}>{pct3(activeBox.value)}</div>
            </div>
            <div style={{ height: 1, background: "rgba(255,255,255,0.10)", margin: "9px 0" }} />
            <div style={{ color: "#cfd8e3", fontSize: 11, fontWeight: 950, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 }}>Top games</div>
            {!mindIndexReady ? <div role="status">Loading complete mindSHARE history...</div> : activeBox.topGames?.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                {activeBox.topGames.map(({ game, value }, idx) => (
                  <div key={`${activeBox.name}-${game.name}-${idx}`} style={{ display: "grid", gridTemplateColumns: "28px minmax(0,1fr) auto", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 36, borderRadius: 5, overflow: "hidden", background: "#111", border: "1px solid rgba(255,255,255,0.10)" }}>
                      {game.imageUrl ? <img src={game.imageUrl} alt={game.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} /> : null}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ color: "#fff", fontSize: 12, fontWeight: 900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{game.name}</div>
                      <div style={{ color: "#8fa0ad", fontSize: 10, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{game.release_date ? `Release ${fmtMDY(game.release_date)}` : "No release date"}</div>
                    </div>
                    <div style={{ color: "#cfeeff", fontSize: 12, fontWeight: 950 }}>{pct3(value)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: "#8f8f8f", fontSize: 12, fontWeight: 800 }}>No game-level rows found for this series.</div>
            )}
          </div>
        )}
      </div>
    );
  };


  const businessSeriesPicker = (
    <div style={{ position: "relative", width: 200, maxWidth: "100%" }}>
      <input
        value={businessQuery}
        onChange={(e) => setBusinessQuery(e.target.value)}
        placeholder={`Add ${businessDimension.slice(0, -1)}...`}
        style={{
          width: "100%",
          height: 26,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,0.16)",
          background: "rgba(255,255,255,0.03)",
          color: "#fff",
          padding: "0 10px",
          fontSize: 14,
          fontWeight: 450,
          boxSizing: "border-box",
          outline: "none",
        }}
      />
      {businessQuery.trim() && (
        <div
          style={{
            position: "absolute",
            top: 33,
            left: 0,
            right: 0,
            maxHeight: 330,
            overflowY: "auto",
            background: "#070707",
            border: "1px solid rgba(255,255,255,0.16)",
            borderRadius: 10,
            boxShadow: "0 18px 45px rgba(0,0,0,0.85)",
            zIndex: 1000,
            padding: 6,
          }}
        >
          {businessSearchResults.length ? (
            businessSearchResults.map((entry) => (
              <button
                key={entry.name}
                type="button"
                onClick={() => {
                  addBusinessSeries(entry.name);
                  setBusinessQuery("");
                }}
                style={{
                  width: "100%",
                  border: "none",
                  background: "transparent",
                  color: "#ddd",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  padding: "7px 8px",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 12,
                  fontWeight: 850,
                  textAlign: "left",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#151515")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {entry.name}
                </span>
                {Number.isFinite(entry.latest_rank) && <span>#{entry.latest_rank}</span>}
              </button>
            ))
          ) : (
            <div style={{ color: "#888", fontSize: 12, padding: 8 }}>No matches</div>
          )}
        </div>
      )}
    </div>
  );

  const BusinessLegendContent = () => {
    const baseNames = businessSeries.filter((name) =>
      businessChartData.some((row) => Number.isFinite(Number(row[name])))
    );
    const MAX_INLINE = 24;
    const visibleInline = baseNames.filter((name) => !legendHidden.has(name)).slice(0, MAX_INLINE);
    const hasMore = baseNames.length > MAX_INLINE || legendHidden.size > 0;
    const searchableNames = baseNames.filter((name) =>
      name.toLowerCase().includes(seriesQuery.trim().toLowerCase())
    );
    const clearHighlights = () => setHighlighted(new Set());
    const restoreHidden = () => setLegendHidden(new Set());

    return (
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, paddingTop: 10, position: "relative" }}>
        <button type="button" onClick={() => setLegendOpen((v) => !v)} onMouseEnter={() => legendOpen && setLegendToggleHover(true)} onMouseLeave={() => setLegendToggleHover(false)} aria-label={legendOpen ? "Hide legend" : "Show legend"} style={{ width: "auto", height: 26, minWidth: legendOpen ? 26 : 104, borderRadius: 8, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.03)", color: "rgba(238,243,247,0.88)", cursor: "pointer", fontSize: 11, fontWeight: 750, lineHeight: "15px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, padding: legendOpen ? "0" : "0 8px", flexShrink: 0, position: "relative" }}>
          {legendOpen ? "−" : "+ Show Legend"}
          {legendOpen && legendToggleHover && (
            <span style={{ position: "absolute", left: 0, bottom: 36, whiteSpace: "nowrap", background: "#050505", color: "#fff", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 7, padding: "6px 8px", fontSize: 11, fontWeight: 850, lineHeight: "14px", boxShadow: "0 12px 28px rgba(0,0,0,0.85)", pointerEvents: "none", zIndex: 100 }}>{legendOpen ? "Hide legend" : "Show legend"}</span>
          )}
        </button>

        {legendOpen && (
          <div style={{ display: "flex", flexWrap: "wrap", columnGap: 8, rowGap: 8, justifyContent: "flex-start", alignItems: "center", position: "relative", minWidth: 0, flex: 1 }}>
            {visibleInline.map((name) => {
              const idx = Math.max(0, businessVisibleSeries.findIndex((n) => n === name));
              const color = getChartColor(name, idx);
              const isHi = highlighted.size > 0 ? highlighted.has(name) : true;
              const dim = highlighted.size > 0 && !isHi;
              return (
                <span key={name} onClick={(e) => { const multi = e.ctrlKey || e.metaKey; setHighlighted((prev) => { const set = new Set(prev); if (multi) { if (set.has(name)) set.delete(name); else set.add(name); } else if (set.size === 1 && set.has(name)) { set.clear(); } else { set.clear(); set.add(name); } return set; }); }} title={highlighted.has(name) ? "Unhighlight (click). Ctrl/Cmd+Click to multi-select." : "Highlight — Ctrl/Cmd+Click to multi-select."} style={{ cursor: "pointer", color: "#fff", fontWeight: 750, fontSize: 11, lineHeight: "15px", display: "inline-flex", alignItems: "center", gap: 6, opacity: dim ? 0.62 : 1, background: "transparent", boxShadow: highlighted.has(name) ? `0 0 16px ${color}66` : "none", padding: "3px 4px", borderRadius: 7, userSelect: "none", textShadow: "0 1px 4px rgba(0,0,0,0.85)" }}>
                  <input type="color" value={color} onChange={(e) => setChartColors((prev) => ({ ...prev, [name]: e.target.value }))} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} title={`Change ${name} color`} aria-label={`Change ${name} color`} style={{ width: 12, height: 12, padding: 0, border: "2px solid #050505", borderRadius: 4, background: color, cursor: "pointer", flexShrink: 0, boxShadow: "0 0 0 1px rgba(255,255,255,0.28), 0 2px 7px rgba(0,0,0,0.65)" }} />
                  <span style={{ maxWidth: 190, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
                  <button type="button" onClick={(e) => { e.stopPropagation(); removeBusinessSeries(name); }} title="Remove series" style={{ width: 16, height: 16, border: "none", background: "transparent", color: "#fff", opacity: 0.72, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, lineHeight: 1 }}><CloseIcon size={12} strokeWidth={1.8} aria-hidden="true" /></button>
                </span>
              );
            })}

            {hasMore && (
              <div ref={seriesManagerRef} style={{ position: "relative" }}>
                <button type="button" onClick={() => setSeriesManagerOpen((v) => !v)} style={{ height: 30, borderRadius: 7, border: "1px solid rgba(32,165,187,0.55)", background: "rgba(5,5,5,0.92)", color: "#cfeeff", cursor: "pointer", fontSize: 12, fontWeight: 900, padding: "0 10px", boxShadow: "0 0 12px rgba(32,165,187,0.10)" }}>Manage {baseNames.length.toLocaleString()} series ▾</button>
                {seriesManagerOpen && (
                  <div style={{ position: "absolute", right: 0, bottom: 36, width: 560, maxWidth: "calc(calc(var(--mg-vw) * 100) - 80px)", maxHeight: 430, zIndex: 50, background: "#070707", border: "1px solid rgba(255,255,255,0.16)", borderRadius: 10, boxShadow: "0 18px 45px rgba(0,0,0,0.85)", padding: 10, overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                      <div style={{ color: "#fff", fontSize: 13, fontWeight: 950 }}>Chart Series</div>
                      <div style={{ display: "flex", gap: 6 }}><button type="button" onClick={clearHighlights} style={{ height: 24, borderRadius: 6, border: "1px solid #333", background: "#111", color: "#ddd", fontSize: 11, fontWeight: 850, cursor: "pointer" }}>Clear focus</button><button type="button" onClick={restoreHidden} style={{ height: 24, borderRadius: 6, border: "1px solid #333", background: "#111", color: "#ddd", fontSize: 11, fontWeight: 850, cursor: "pointer" }}>Restore hidden</button></div>
                    </div>
                    <input value={seriesQuery} onChange={(e) => setSeriesQuery(e.target.value)} placeholder="Search series..." style={{ width: "100%", height: 30, borderRadius: 7, border: "1px solid #333", background: "#0e0e0e", color: "#fff", padding: "0 9px", marginBottom: 8, fontSize: 12, boxSizing: "border-box", outline: "none" }} />
                    <div style={{ maxHeight: 340, overflowY: "auto", paddingRight: 2 }}>
                      {searchableNames.map((name) => { const idx = Math.max(0, businessVisibleSeries.findIndex((n) => n === name)); const color = chartSeriesColor(idx); const isHi = highlighted.has(name); const isHidden = legendHidden.has(name); return (
                        <div key={name} style={{ display: "grid", gridTemplateColumns: "16px minmax(0,1fr) auto auto auto", alignItems: "center", gap: 8, height: 30, borderBottom: "1px solid rgba(255,255,255,0.05)", opacity: isHidden ? 0.45 : 1 }}>
                          <input type="color" value={color} onChange={(e) => setChartColors((prev) => ({ ...prev, [name]: e.target.value }))} title={`Change ${name} color`} aria-label={`Change ${name} color`} style={{ width: 12, height: 12, padding: 0, border: "2px solid #050505", borderRadius: 4, background: color, cursor: "pointer", boxShadow: "0 0 0 1px rgba(255,255,255,0.28)" }} />
                          <span title={name} style={{ color: "#eee", fontSize: 12, fontWeight: 800, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{name}</span>
                          <button type="button" onClick={() => setHighlighted((prev) => { const set = new Set(prev); if (set.has(name)) set.delete(name); else set.add(name); return set; })} style={{ height: 22, borderRadius: 6, border: `1px solid ${isHi ? color : "#333"}`, background: isHi ? `${color}22` : "#101010", color: isHi ? "#fff" : "#ccc", fontSize: 11, fontWeight: 850, cursor: "pointer" }}>Focus</button>
                          <button type="button" onClick={() => setLegendHidden((prev) => { const set = new Set(prev); isHidden ? set.delete(name) : set.add(name); return set; })} style={{ height: 22, borderRadius: 6, border: "1px solid #333", background: "#101010", color: "#fff", fontSize: 11, fontWeight: 850, cursor: "pointer" }}>{isHidden ? "Show" : "Hide"}</button>
                          <button type="button" onClick={() => removeBusinessSeries(name)} style={{ height: 22, width: 24, borderRadius: 6, border: "1px solid #333", background: "#101010", color: "#fff", fontSize: 14, fontWeight: 950, cursor: "pointer", lineHeight: "18px" }} title="Remove series">×</button>
                        </div>
                      ); })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const mindshareChartBgArt = (() => {
    if (viewMode !== "trended") return "";
    if (chartBgChoice === "none") return "";
    return getGameByName(chartBgChoice)?.imageUrl || "";
  })();
  const isEmptyCustomChart = !(perGameHistory ? comparisonGames.length : targetsForChart.length) && (
    (viewMode === "trended" && launchChartPreset === "custom") ||
    (viewMode === "launches" && launchChartPreset === "custom")
  );
  const hasVisibleCardsChart = viewMode === "trended" && showInsights && (
    comparisonGames.length > 0 || targetsForChart.length > 0 || (favoriteHistoryPreset && favoriteSelectionEdited)
  );
  const showMindshareTopGrowthSection = showTopGrowth &&
    (splashGames.length > 0 || splashLoading) && viewMode === "trended";
  const showMindshareTrendsHeader = !isPublicPreview && (viewMode === "launches" ||
    !showMindshareTopGrowthSection ||
    comparisonGames.length > 0 ||
    targetsForChart.length > 0 ||
    hasVisibleCardsChart);
  const showEmptyCustomChartPanel = viewMode === "trended" &&
    showMindshareTrendsHeader &&
    isEmptyCustomChart;
  const showMindshareChartSkeleton = (historyPending && !(favoriteBaseReady || (perGameHistory && targetsForChart.length > 0))) || (viewMode === "business"
    ? businessLoading && !businessChartDataWithAverage.length
    : viewMode !== "market" && viewMode !== "calendar" && (
      !hasVisibleCardsChart && (
        chartBooting ||
        (isEmptyCustomChart && showMindshareTrendsHeader) ||
        ((metaLoading || loading) && !chartDataWithAverage.length)
      )
    ));
  const showLaunchChartOptions = !isEmptyCustomChart;

  /* -------------------------------- RENDER -------------------------------- */
  return (
    <div
      className="mg-mindshare-page"
      style={{
        display: "flex",
        background: "transparent",
        color: "#fff",
        minHeight: "calc(calc(var(--mg-vh) * 100) - 78px)",
        position: "relative",
        height: viewMode === "market" ? "calc(calc(var(--mg-vh) * 100) - 78px)" : undefined,
        alignItems: viewMode === "market" ? "stretch" : undefined,
      }}
    >
      <MindshareHeaderControls
        visible
        neutralViews
        readOnly={isPublicPreview}
        viewMode={viewMode}
        onViewChange={isPublicPreview ? () => {} : handleViewModeChange}
        onOpenFilters={isPublicPreview ? () => {} : () => setFiltersOpen((open) => !open)}
        filtersOpen={!isPublicPreview && filtersOpen}
        filtersActive={!isPublicPreview && (hasActiveMarketFilters || Boolean(searchTerm.trim()))}
        searchTerm={isPublicPreview ? "" : searchTerm}
        onSearchChange={isPublicPreview ? () => {} : setSearchTerm}
        centerSearchTargetId="mindshare-header-search"
      />
      {showChartJump && (
        <button
          type="button"
          onClick={() => {
            window.scrollTo({ top: 0, behavior: "smooth" });
            setShowChartJump(false);
          }}
          title="Jump to chart"
          aria-label="Jump to chart"
          style={{
            position: "fixed",
            top: 76,
            right: 24,
            zIndex: 12000,
            width: 38,
            height: 38,
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.28)",
            background: "rgba(8,10,12,0.92)",
            color: "#dfe7ef",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 8px 24px rgba(0,0,0,0.55), 0 0 14px rgba(255,255,255,0.12)",
            animation: "mgChartJumpPulse 1.5s ease-in-out infinite",
          }}
        >
          <span aria-hidden style={{ fontSize: 25, lineHeight: 1, fontWeight: 950, textShadow: "0 0 2px currentColor", transform: "translateY(-2px) scaleX(1.12)" }}>↑</span>
        </button>
      )}
      <style>{`
        @keyframes mgChartJumpPulse {
          0%, 100% { transform: translateY(0); box-shadow: 0 8px 24px rgba(0,0,0,0.55), 0 0 14px rgba(255,255,255,0.12); }
          50% { transform: translateY(-5px); box-shadow: 0 12px 28px rgba(0,0,0,0.62), 0 0 22px rgba(32,165,187,0.30); }
        }
        .mg-mindshare-range {
          -webkit-appearance: none;
          appearance: none;
        }
        .mg-mindshare-range::-webkit-slider-runnable-track {
          height: 18px;
          background: transparent;
          border: none;
        }
        .mg-mindshare-range::-moz-range-track {
          height: 18px;
          background: transparent;
          border: none;
        }
        .mg-mindshare-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 28px;
          height: 14px;
          border-radius: 7px;
          border: none;
          background: #9aa3ad;
          cursor: grab;
          box-shadow: 0 0 10px rgba(180,190,200,0.28);
          pointer-events: auto;
          margin-top: 2px;
        }
        .mg-mindshare-range:active::-webkit-slider-thumb {
          cursor: grabbing;
        }
        @keyframes mgHeaderFadeIn {
          from { opacity: 0; transform: translateY(-3px) scale(0.97); }
          to { opacity: 1; transform: none; }
        }
        .mg-header-fade-in {
          animation: mgHeaderFadeIn 260ms cubic-bezier(.2,.8,.2,1) both;
        }
        .mg-mindshare-range::-moz-range-thumb {
          width: 28px;
          height: 14px;
          border-radius: 7px;
          border: none;
          background: #9aa3ad;
          cursor: grab;
          box-shadow: 0 0 10px rgba(180,190,200,0.28);
          pointer-events: auto;
        }
      `}</style>

      {!isPublicPreview && filtersOpen && (
        <div
          className="mg-steam-filter-backdrop"
          onMouseEnter={() => {
            if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
          }}
          onClick={() => setFiltersOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* mindSHARE filter workspace (top popover, same pattern as Steam/Twitch) */}
<MGFilterControl
  className="mg-twitch-filter-popover mg-mindshare-filter-popover"
  onMouseEnter={(event) => {
    if (!event.currentTarget.contains(event.target)) return;
    if (filterCloseTimerRef.current) clearTimeout(filterCloseTimerRef.current);
    setFiltersOpen(true);
  }}
  onMouseLeave={(event) => {
    if (event.currentTarget.contains(event.target)) setFiltersOpen(false);
  }}
  style={{
    position: "fixed",
    top: 82,
    left: 20,
    height: "auto",
    maxHeight: "calc(calc(var(--mg-vh) * 100) - 102px)",
    overflowY: "auto",
    overflowX: "hidden",
    padding: "42px 20px 20px",
    background: "rgba(8,10,12,0.97)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 18,
    width: "min(440px, calc(calc(var(--mg-vw) * 100) - 40px))",
    minWidth: 0,
    flexShrink: 0,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: "12px 16px",
    zIndex: 10000,
    opacity: !isPublicPreview && filtersOpen ? 1 : 0,
    visibility: !isPublicPreview && filtersOpen ? "visible" : "hidden",
    pointerEvents: !isPublicPreview && filtersOpen ? "auto" : "none",
    transform: !isPublicPreview && filtersOpen ? "translateY(0)" : "translateY(-12px)",
    boxShadow: "0 22px 60px rgba(0,0,0,0.82)",
    transition: "opacity 150ms ease, transform 150ms ease, visibility 150ms ease",
  }}
>
  <div
    style={{
      display: "contents",
    }}
  >

{/* Search now lives in the header (Steam/Twitch-style expanding search) */}

    {headerToolsTarget && !isPublicPreview && createPortal(<div className="ms-header-saved-controls">
        <div style={{ gridColumn: 3, gridRow: 1 }}>
        <SavedViewsManager
      compact
          type="mindshare"
          currentFilters={{ searchTerm, tags: selectedTags, publishers: selectedPublishers, genres: selectedGenres, developers: selectedDevelopers, liveService: selectedLiveService, pricing: selectedPricing, ugc: selectedUGC, focus: selectedFocus, platforms: selectedPlatforms, dateRange: selectedDateRange, onlyUnreleased, showInsights, showAverage, analyticsSeries, mindshareXAxisMode, mindshareAgg, viewMode, marketSort, marketViewType, marketSplitView, marketPeriod, launchesShowAll, launchChartPreset, chartStyle, businessDimension, businessWindow, businessSeries }}
          onApplyFilters={(f) => {
            setSearchTerm(f.searchTerm ?? ""); setSelectedTags(f.tags ?? []); setSelectedPublishers(f.publishers ?? []); setSelectedGenres(f.genres ?? []); setSelectedDevelopers(f.developers ?? []); setSelectedLiveService(f.liveService ?? null); setSelectedPricing(f.pricing ?? null); setSelectedUGC(f.ugc ?? null); setSelectedFocus(f.focus ?? []); setSelectedPlatforms(f.platforms ?? []); setOnlyUnreleased(f.onlyUnreleased ?? false); setShowInsights(f.showInsights ?? false); setAnalyticsSeries(f.analyticsSeries ?? { average: f.showAverage ?? false, median: false, top5: false, top10: false, top25: false }); setMindshareXAxisMode(f.mindshareXAxisMode ?? "date"); setMindshareAgg(f.mindshareAgg ?? "weekly"); setViewMode(f.viewMode ?? "trended"); setMarketSort(f.marketSort ?? "growth"); setMarketViewType(f.marketViewType === "squares" ? "squares" : "bubbles"); setMarketSplitView(f.marketSplitView ?? false); setMarketPeriod("1w"); setLaunchesShowAll(f.launchesShowAll ?? false); setLaunchChartPreset(f.launchChartPreset ?? "upcoming"); setFavoriteSelectionEdited(false); setChartStyle(f.chartStyle === "line" ? "line" : "area"); setBusinessDimension(f.businessDimension ?? "publishers"); setBusinessWindow(f.businessWindow ?? "52w"); setBusinessSeries(f.businessSeries ?? []);
            const [d0, d1] = coerceRangeToDates(f.dateRange ?? [null, null]); setSelectedDateRange([d0, d1]);
          }}
          onClearFilters={handleReset}
        />
        </div>

        <MGIconButton className="ms-header-icon" onClick={handleReset} aria-label="Reset all" data-tooltip="Reset all"><RotateCcw size={16} /></MGIconButton>
        <CsvExportButton as={MGIconButton} className="ms-header-icon" onClick={handleExportVisibleGamesCsv} disabled={!cardFiltered.length} aria-label="Export CSV" data-tooltip="Export CSV"><Download size={16} /></CsvExportButton>
        </div>, headerToolsTarget)}

        <div className="mg-mindshare-filter-column" style={{ gridColumn: 1, gridRow: 1 }}>
        <div style={{ gridColumn: 2, gridRow: 1 }}>
        <VirtualizedDropdown
  placeholder="All Genres"
  value={selectedGenres}
  onChange={setSelectedGenres}
  options={filterOptions.genres}
  menuWidth="100%"
  isClearable
  isMulti
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("genre", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("genre", val)}
/>
</div>

        <div style={{ gridColumn: 2, gridRow: 2 }}>
        <VirtualizedDropdown
  placeholder="All Tags"
  value={selectedTags}
  onChange={setSelectedTags}
  options={filterOptions.tags}
  menuWidth="100%"
  isClearable
  isMulti
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("tag", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("tag", val)}
/>
</div>

        <div style={{ gridColumn: 2, gridRow: 3 }}>
        <VirtualizedDropdown
  placeholder="All Publishers"
  value={selectedPublishers}
  onChange={setSelectedPublishers}
  options={filterOptions.publishers}
  menuWidth="100%"
  isClearable
  isMulti
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("publisher", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("publisher", val)}
/>
</div>

        <div style={{ gridColumn: 2, gridRow: 4 }}>
        <VirtualizedDropdown
  placeholder="All Developers"
  value={selectedDevelopers}
  onChange={setSelectedDevelopers}
  options={filterOptions.developers}
  menuWidth="100%"
  isClearable
  isMulti
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("developer", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("developer", val)}
/>
</div>

        <div style={{ gridColumn: 2, gridRow: 5 }}>
       <VirtualizedDropdown
  placeholder="Business Model"
  value={selectedLiveService}
  onChange={setSelectedLiveService}
  options={filterOptions.liveService}
  menuWidth="100%"
  isClearable
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("liveService", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("liveService", val)}
/>
</div>

<div style={{ gridColumn: 2, gridRow: 6 }}>
<VirtualizedDropdown
  placeholder="Pricing"
  value={selectedPricing}
  onChange={setSelectedPricing}
  options={filterOptions.pricing}
  menuWidth="100%"
  isClearable
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("pricing", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("pricing", val)}
/>
</div>

<div style={{ gridColumn: 2, gridRow: 7 }}>
<VirtualizedDropdown
  placeholder="UGC"
  value={selectedUGC}
  onChange={setSelectedUGC}
  options={filterOptions.ugc}
  menuWidth="100%"
  isClearable
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("ugc", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("ugc", val)}
/>
</div>

<div style={{ gridColumn: 2, gridRow: 8 }}>
<VirtualizedDropdown
  placeholder="Play Type"
  value={selectedFocus}
  onChange={setSelectedFocus}
  options={filterOptions.focus}
  menuWidth="100%"
  isClearable
  isMulti
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("focus", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("focus", val)}
/>
</div>

<div style={{ gridColumn: 2, gridRow: 9 }}>
<VirtualizedDropdown
  placeholder="Platform Type"
  value={selectedPlatforms}
  onChange={setSelectedPlatforms}
  options={filterOptions.platforms}
  menuWidth="100%"
  isClearable
  isMulti
  showHighlightToggle={viewMode === "market"}
  keepMenuOpen
  isHighlighted={(val) => isMarketHighlighted("platform", val)}
  onToggleHighlight={(val) => toggleMarketHighlight("platform", val)}
/>
</div>

<div style={{ gridColumn: 2, gridRow: 10, display: "flex", flexDirection: "column", gap: 8 }}>
  <DateSlider
    compact
    headerContent={
  <label
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 14,
      color: "rgba(187,187,187,0.75)",
      cursor: "pointer",
    }}
  >
    <input
      type="checkbox"
      checked={onlyUnreleased}
      onChange={(e) => setOnlyUnreleased(e.target.checked)}
    />
    Show Only Unreleased
  </label>
    }
    range={coerceRangeToDates(releaseDateRange)}
    selected={selectedDateRange}
    onChange={(rng) => setSelectedDateRange(coerceRangeToDates(rng))}
    onClear={() => setSelectedDateRange([null, null])}
    forceClose={isPublicPreview || !filtersOpen}
  />
</div>
        </div>

  <div className="ms-metadata-heading" aria-hidden="true">Filters</div>
  </div>
    </MGFilterControl>

                  {/* Main */}
      <div
        ref={mainRef}
        style={{
          flex: 1,
          padding:
            viewMode === "market"
              ? "20px 20px 18px 20px"
              : "4px 30px 30px 30px",
          position: "relative", // bubbles sit absolute inside this
          minWidth: 0,
          height: viewMode === "market" ? "100%" : "auto",
          minHeight: viewMode === "market" ? 0 : undefined,
          alignSelf: viewMode === "market" ? "stretch" : undefined,
          boxSizing: "border-box",
          overflow: viewMode === "market" ? "hidden" : "visible",
        }}
      >

        {dataError && (
          <div role="alert">
            {dataError.message} <button type="button" onClick={() => setDataRetry(value => value + 1)}>Retry</button>
          </div>
        )}
        <DashboardContent label="mindSHARE" loading={!dataError && (legacyData ? (metaLoading || loading) && !cardFiltered.length : !snapshotRows)}>
        {historyPending && !dataError && viewMode !== "trended" && viewMode !== "launches" && <p role="status">Loading complete mindSHARE history...</p>}
        {viewMode === "trended" && showMindshareTopGrowthSection && (
          <>
            <MGSectionHeader index="01" accent="cyan">Top mindSHARE growth</MGSectionHeader>
            <MindshareTopGrowth
              games={splashGames}
              loading={splashLoading}
              editorial={editorial}
              getVideoEmbed={getVideoEmbed}
              showSpotlight={showTopGrowth}
              onDismiss={() => {
                setShowTopGrowth(false);
                setShowInsights(true);
              }}
              heightOverride={mindshareChartHeightUserAdjustedRef.current ? mindshareChartHeight + CHART_PANEL_NONPLOT_HEIGHT : undefined}
              onSlotHeightChange={handleTopGrowthSlotHeight}
              renderSkeleton={(height) => (
                <div style={{ position: "relative", width: "100%", height }}>
                  <div style={{ position: "absolute", top: 13, left: 19, right: 19 }}>
                    <MindshareChartSkeleton height={height - CHART_PANEL_NONPLOT_HEIGHT} axisWidth={mindshareYAxisWidth} axisData={chartDataWithAverage} axisMax={yUpper} formatXAxis={formatMindshareXAxisTick} message="" />
                  </div>
                  <div className="ms-empty-chart-prompt-row" style={{ top: 26, left: 13 + mindshareYAxisWidth }}>
                    <div className="ms-empty-chart-guide">
                      <ArrowLeft className="ms-empty-chart-guide-arrow" size={18} strokeWidth={2} aria-hidden="true" />
                      <span>Select a chart above or add games below with +</span>
                    </div>
                  </div>
                </div>
              )}
            />
          </>
        )}
        {(viewMode === "trended" || viewMode === "launches") && showMindshareTrendsHeader && (
          <MGSectionHeader index={showMindshareTopGrowthSection ? "02" : "01"} accent="cyan">
            {viewMode === "launches" ? "Launch history" : (
              <>
                Historical trends
                <span className="mg-twitch-ranking-divider" aria-hidden="true">|</span>
                <MGHeadingToggle
                  label="Cards chart preset"
                  value={launchChartPreset}
                  onChange={(preset) => handleLaunchChartPresetChange(preset, "trended")}
                  angledSelection
                  options={[
                    { value: "custom", label: "Custom" },
                    { value: "top100", label: "Current Top 100" },
                    { value: "upcoming", label: "Upcoming Releases" },
                    { value: "recent", label: "Recent Releases" },
                  ]}
                />
              </>
            )}
          </MGSectionHeader>
        )}
                {/* Chart / Market View – Trended only when showInsights, Market always */}
        {viewMode === "publishers" && !historyPending && <PublisherAtlas games={marketFiltered} />}
        {(!historyPending || ["trended", "launches", "business"].includes(viewMode)) && viewMode !== "publishers" && (showInsights || viewMode === "market" || viewMode === "business" || viewMode === "calendar") &&
          !(viewMode === "trended" && !targetsForChart.length && !hasVisibleCardsChart && !showEmptyCustomChartPanel) && (
          <div
            className="mg-analytics-frame"
            style={{
              marginTop: 0,
              marginBottom: viewMode === "market" || viewMode === "trended" || viewMode === "launches" ? 0 : 16,
              padding: viewMode === "market" ? 0 : "12px 18px 18px 18px",
              background: viewMode === "market" ? "transparent" : "#000",
              border: viewMode === "market" ? "none" : "1px solid rgba(255,255,255,0.08)",
              borderRadius: viewMode === "market" ? 0 : 10,
              position: "relative",
              overflow: viewMode === "market" ? undefined : "hidden",
              minHeight: viewMode === "market" ? marketDims.height : undefined,
              height: viewMode === "market" ? marketDims.height : undefined,
            }}
          >
            {mindshareChartBgArt && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${mindshareChartBgArt})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  opacity: 0.35,
                  filter: "blur(3px) saturate(0.85) brightness(0.35)",
                  transform: "scale(1.08)",
                  pointerEvents: "none",
                }}
              />
            )}
            {mindshareChartBgArt && (
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.62) 45%, rgba(0,0,0,0.78) 100%), radial-gradient(120% 120% at 50% 20%, rgba(0,0,0,0.20), rgba(0,0,0,0.72) 82%)",
                  transform: "scale(1.08)",
                  pointerEvents: "none",
                }}
              />
            )}

                                {/* Header row w/ toggles — overlays the chart on trended/launches/business */}
            <div
  ref={headerRef}
  style={
    viewMode === "market"
      ? {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 10,
          gap: 10,
          width: "calc(100% - 24px)",
          flexWrap: "wrap",
          width: "100%",
          position: "relative",
          zIndex: 5, // header sits above bubbles
        }
      : {
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 10,
          flexWrap: "nowrap",
          width: "calc(100% - 24px)",
          position: "absolute",
          top: 12,
          left: 12,
          zIndex: 6, // overlays the chart canvas
          pointerEvents: "none", // children re-enable
        }
  }
>
  {viewMode === "market" ? (
    <>
      {/* Market Mind controls: one left-aligned group so the chart keeps the open space */}
      <div className="mm-controls-row">
        <div
          className="mm-controls-group"
        >
          <div style={{ opacity: marketControlsLocked ? 0.45 : 1, pointerEvents: marketControlsLocked ? "none" : "auto" }}>
          <HeaderMiniDropdown
            prefix="Period:"
            value={marketPeriod}
            options={MARKET_PERIOD_OPTIONS.map(({ value, label }) => ({ value, label }))}
            onChange={setMarketPeriod}
            active
            width={190}
          />
          </div>
          <div style={{ opacity: marketControlsLocked ? 0.45 : 1, pointerEvents: marketControlsLocked ? "none" : "auto" }}>
          <HeaderMiniDropdown
            prefix="Sort By:"
            value={marketSort}
            options={[{ value: "growth", label: "Growth %" }, { value: "mindshare", label: "mindSHARE" }]}
            onChange={setMarketSort}
            active
            width={180}
          />
          </div>
          <HeaderMiniDropdown
            multi
            placeholder="Highlight Favorites"
            value={marketFavorites}
            options={MARKET_FAVORITES}
            onChange={setMarketFavorites}
            active={marketFavorites.length > 0}
            width={190}
          />
        </div>
        <div className="mm-layout-comparison" role="group" aria-label="Market Mind layout">
          {[["bubbles", "Circles"], ["squares", "Squares"]].map(([value, label]) => (
            <button key={value} type="button" aria-pressed={marketViewType === value}
              onClick={() => setMarketViewType(value)}>{label}</button>
          ))}
        </div>
        {hasActiveMarketFilters && (
          <button
            type="button"
            className="mm-split-toggle"
            aria-pressed={marketSplitView}
            onClick={() => setMarketSplitView((v) => !v)}
            title={marketSplitView ? "Return to editable Market Mind controls" : "Show all games on the left and filtered zoom on the right"}
          >
            {marketSplitView ? "Unsplit" : "Split"}
          </button>
        )}
        <span className="mm-window-label" title={`${marketPeriodLabel} ${marketWindowLabel}`}>
          {marketWindowLabel}
        </span>
      </div>
    </>
  ) : viewMode === "calendar" ? null : viewMode === "business" ? (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 10,
          flexWrap: "wrap",
          width: `calc(100% - ${mindshareYAxisWidth + 24}px)`,
          marginLeft: mindshareYAxisWidth,
          paddingTop: 12,
          pointerEvents: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <HeaderTitleSelect
            value={businessDimension}
            options={BUSINESS_DIMENSIONS}
            onChange={setBusinessDimension}
            ariaLabel="Business chart dimension"
          />
          <HeaderTitleSelect
            value={businessWindow}
            options={BUSINESS_WINDOWS}
            onChange={setBusinessWindow}
            ariaLabel="Business chart period"
          />
          <HeaderTitleSelect
            value={chartStyle}
            ariaLabel="Chart style"
            onChange={setChartStyle}
            options={[
              { value: "area", label: "Area", icon: "\u25b0" },
              { value: "line", label: "Line", icon: "\u2301" },
            ]}
          />
          <HeaderTitleSelect
            multiple
            triggerLabel="Benchmarks"
            triggerIcon={<Ruler size={14} strokeWidth={1.9} />}
            ariaLabel="Benchmark overlays"
            value={Object.keys(analyticsSeries).filter((key) => analyticsSeries[key])}
            onChange={(key) => setAnalyticsSeries((previous) => ({ ...previous, [key]: !previous[key] }))}
            options={[{ value: "average", label: "Average" }, { value: "median", label: "Median" }, { value: "top5", label: "Top 5%" }, { value: "top10", label: "Top 10%" }, { value: "top25", label: "Top 25%" }]}
          />
          <button type="button" onClick={() => { setBusinessSeries((businessRollup?.series || []).slice(0, 100).map(getBusinessSeriesName).filter(Boolean)); setLegendHidden(new Set()); setHighlighted(new Set()); setHoverSeriesKey(null); }} style={{ height: 26, borderRadius: 8, border: "1px solid rgba(255,255,255,0.16)", background: "rgba(255,255,255,0.03)", color: "rgba(238,243,247,0.88)", fontFamily: "inherit", fontSize: 15, fontWeight: 450, cursor: "pointer", padding: "0 10px" }}>Top 100</button>
          {businessSeriesPicker}
        </div>
      </div>
    </>
  ) : (
    <>
      <h2
        style={{
          position: "relative",
          height: 58,
          boxSizing: "border-box",
          display: "inline-flex",
          alignItems: "flex-end",
          gap: 10,
          width: `calc(100% - ${mindshareYAxisWidth + 24}px)`,
          margin: `0 0 0 ${mindshareYAxisWidth}px`,
          padding: "0 12px 8px 0",
          color: "#fff",
          fontFamily: "inherit",
          textShadow: "0 1px 8px rgba(0,0,0,0.40)",
          pointerEvents: "auto",
          whiteSpace: "nowrap",
          transform: "translateY(-9px)",
        }}
      >
        <span
          style={{
            position: "relative",
            zIndex: 1,
            paddingBottom: 2,
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          {viewMode === "launches" && (
            <HeaderTitleSelect
              value={launchChartPreset}
              ariaLabel="Launches chart preset"
              onChange={handleLaunchChartPresetChange}
              options={[
                { value: "custom", label: "Custom" },
                { value: "upcoming", label: "Upcoming Releases" },
                { value: "recent", label: "Recent Releases" },
              ]}
              width={200}
            />
          )}
          {showLaunchChartOptions && viewMode !== "launches" && (
            <HeaderTitleSelect
              value={mindshareXAxisMode}
              ariaLabel="Chart horizontal axis"
              onChange={(next) => {
                mindWindowUserAdjustedRef.current = false;
                setMindshareXAxisMode(next);
              }}
              options={[
                { value: "date", label: "Date", icon: <CalendarDays size={14} strokeWidth={1.9} /> },
                { value: "w2l", label: "Weeks to Launch", icon: <CalendarDays size={14} strokeWidth={1.9} /> },
              ]}
              width={mindshareXAxisMode === "w2l" ? 210 : 168}
            />
          )}
          {showLaunchChartOptions && (viewMode === "launches" || mindshareXAxisMode === "w2l") && (
            <>
              {viewMode !== "launches" && (
                <span
                  aria-hidden
                  style={{
                    width: 16,
                    height: 2,
                    borderRadius: 2,
                    marginLeft: -4,
                    marginRight: -4,
                    background: "linear-gradient(90deg, rgba(32,165,187,0.35), #20a5bb, rgba(32,165,187,0.35))",
                    boxShadow: "0 0 8px rgba(32,165,187,0.55)",
                    flexShrink: 0,
                  }}
                />
              )}
              <HeaderTitleSelect
                value={mindshareAgg}
                ariaLabel="Chart aggregation"
                onChange={setMindshareAgg}
                options={[
                  { value: "weekly", label: "Weekly" },
                  { value: "cumulative", label: "Cumulative" },
                ]}
              />
            </>
          )}

          {showLaunchChartOptions && (
            <>
          <span
            aria-hidden
            style={{
              width: 1,
              height: 20,
              background: "rgba(255,255,255,0.22)",
              flexShrink: 0,
              margin: "0 2px",
            }}
          />
          <HeaderTitleSelect
            value={chartStyle}
            ariaLabel="Chart style"
            onChange={setChartStyle}
            options={[
              { value: "area", label: "Area", icon: "\u25b0" },
              { value: "line", label: "Line", icon: "\u2301" },
            ]}
          />
            </>
          )}
          {showLaunchChartOptions && targetsForChart.length >= 3 && (
            <span className="mg-header-fade-in" style={{ display: "inline-flex" }}>
              <HeaderTitleSelect
                multiple
                triggerLabel="Benchmarks"
                triggerIcon={<Ruler size={14} strokeWidth={1.9} />}
                ariaLabel="Benchmark overlays"
                value={Object.keys(analyticsSeries).filter((key) => analyticsSeries[key])}
                onChange={(key) => setAnalyticsSeries((prev) => ({ ...prev, [key]: !prev[key] }))}
                options={[
                  { value: "average", label: "Average" },
                  { value: "median", label: "Median" },
                  { value: "top5", label: "Top 5%" },
                  { value: "top10", label: "Top 10%" },
                  { value: "top25", label: "Top 25%" },
                ]}
              />
            </span>
          )}
          {showLaunchChartOptions && viewMode === "trended" && targetsForChart.length > 0 && (
            <HeaderTitleSelect
              value={chartBgChoice}
              ariaLabel="Chart background"
              onChange={setChartBgChoice}
              fixedLabel="Background"
              options={[
                { value: "none", label: "No Background", icon: <ImageIcon size={14} strokeWidth={1.9} /> },
                ...targetsForChart.map((name) => ({
                  value: name,
                  label: name,
                  icon: <ImageIcon size={14} strokeWidth={1.9} />,
                })),
              ]}
            />
          )}
        </span>
      </h2>

      <div
        className="mg-chart-controls"
        style={{
          display: "flex",
          gap: 10,
          alignItems: "center",
          flexWrap: "nowrap",
          flex: "1 1 auto",
          minWidth: 0,
          background: "transparent",
          border: "none",
          padding: 0,
          pointerEvents: "auto",
        }}
      >

                  </div>
                </>
              )}
            </div>


            {/* MAIN CHART AREA: switch between Trended and Market Mind */}
                     {viewMode === "market" ? (
  <MarketViewContainer
    filtered={splitMarketMainRows}
    loading={!marketReady || ((metaLoading || loading) && !splitMarketMainRows.length)}
    isEmpty={!splitMarketMainRows.length}
    width={marketDims.width}
    height={marketDims.height}
    preview={splitMarketPreview}
    displayMode={marketViewType}
    splitEnabled={splitMarketEnabled}
    splitPreview={favoriteHighlights}
    splitFiltered={marketFiltered}
    splitLabel={appliedMarketFilterLabel}
    periodLabel={`${marketPeriodLabel} ${marketWindowLabel}`}
  />
 ) : viewMode === "calendar" ? (
  <MindshareReleaseCalendar games={games} />
 ) : viewMode === "business" ? (
  businessLoading && !businessChartDataWithAverage.length ? (
    <MindshareChartSkeleton height={Math.min(mindshareChartHeight, Math.max(340, layoutViewportHeight() - 108))} message="Loading business rollups…" />
  ) : (
    <div ref={chartExportRef} style={{ height: `${mindshareChartHeight}px`, minHeight: 420, borderRadius: 8, overflow: "hidden", backgroundColor: "#000", position: "relative", display: "flex", flexDirection: "column" }}>
      {chartStyle === "boxes" ? (
        <BusinessBoxesView />
      ) : (
      <div style={{ flex: "1 1 auto", minHeight: 0, borderRadius: 8, overflow: "hidden" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={businessChartDataWithAverage} margin={{ left: 8, right: 80, top: 64, bottom: 0 }} onMouseMove={(state) => { if (state?.isTooltipActive) setBusinessHistoryRequested(true); }} onMouseLeave={() => setHoverSeriesKey(null)}>
            <CartesianGrid stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="period" stroke="#ddd" tick={{ fontSize: 11, fill: "#cfd8e3", fontWeight: 700 }} tickFormatter={fmtMDY} tickLine={{ stroke: "rgba(255,255,255,0.18)" }} axisLine={{ stroke: "rgba(255,255,255,0.16)" }} />
            <YAxis stroke="#ddd" width={mindshareYAxisWidth} tick={{ fontSize: 12, fill: "#cfd8e3" }} tickLine={{ stroke: "rgba(255,255,255,0.18)" }} axisLine={{ stroke: "rgba(255,255,255,0.16)" }} tickFormatter={(v) => pct3(v)} domain={[0, businessYMax]} />
            <Tooltip content={<BusinessPointTooltip />} isAnimationActive={false} cursor={{ stroke: "rgba(255,255,255,0.34)", strokeDasharray: "3 3" }} />
            {[{ key: "__avg", enabled: analyticsSeries.average, name: "Average", stroke: "rgba(230,230,230,0.92)", dash: "6 6" }, { key: "__median", enabled: analyticsSeries.median, name: "Median", stroke: "rgba(245,230,200,0.90)", dash: "3 5" }, { key: "__top5", enabled: analyticsSeries.top5, name: "Top 5%", stroke: "rgba(168,85,247,0.92)", dash: "8 5" }, { key: "__top10", enabled: analyticsSeries.top10, name: "Top 10%", stroke: "rgba(102,204,255,0.92)", dash: "8 5" }, { key: "__top25", enabled: analyticsSeries.top25, name: "Top 25%", stroke: "rgba(255,155,112,0.90)", dash: "8 5" }].filter((item) => item.enabled).map((item) => (
              <Line key={item.key} type="monotone" dataKey={item.key} stroke={item.stroke} strokeWidth={2.2} strokeDasharray={item.dash} dot={false} activeDot={false} isAnimationActive={false} connectNulls={false} name={item.name} />
            ))}
            {businessVisibleSeries.map((name, idx) => { const isHi = highlighted.size > 0 ? highlighted.has(name) : true; const dim = highlighted.size > 0 && !isHi; const stroke = getChartColor(name, idx); return chartStyle === "area" ? (
              <Area key={name} type="monotone" dataKey={name} stroke={stroke} strokeWidth={dim ? 0.8 : 2.0} strokeOpacity={dim ? 0.12 : 0.95} fill={stroke} fillOpacity={dim ? 0.012 : 0.11} dot={(dotProps) => (<CustomDot {...dotProps} stroke={stroke} seriesName={name} dim={dim} onToggle={() => toggleSeriesHighlight(name)} />)} activeDot={false} isAnimationActive={false} connectNulls={false} name={name} onClick={() => toggleSeriesHighlight(name)} />
            ) : (
              <Line key={name} type="monotone" dataKey={name} stroke={stroke} strokeWidth={dim ? 0.9 : 2.4} strokeOpacity={dim ? 0.14 : 1} dot={(dotProps) => (<CustomDot {...dotProps} stroke={stroke} seriesName={name} dim={dim} onToggle={() => toggleSeriesHighlight(name)} />)} activeDot={false} isAnimationActive={false} connectNulls={false} name={name} onClick={() => toggleSeriesHighlight(name)} />
            ); })}
            {(() => { const dots = []; for (let i = 0; i < businessVisibleSeries.length; i++) { const seriesName = businessVisibleSeries[i]; let best = { y: -Infinity, x: null }; for (const row of businessChartData) { const v = Number(row[seriesName]); if (Number.isFinite(v) && v > best.y) best = { y: v, x: row.period }; } if (best.x == null) continue; const isFocused = highlighted.size === 0 || highlighted.has(seriesName); const shouldLabelSeries = businessVisibleSeries.length <= 100 || highlighted.size > 0 || hoverSeriesKey === seriesName; const labelColor = isFocused ? chartSeriesColor(i) : "#6f7882"; const renderPeakLabel = (props) => { if (!shouldLabelSeries) return null; const viewBox = props?.viewBox || {}; const px = Number(props?.x ?? viewBox.x ?? 0); const py = Number(props?.y ?? viewBox.y ?? 0); const rightEdge = Number(viewBox.x || 0) + Number(viewBox.width || 0); const nearRightEdge = rightEdge > 0 && px > rightEdge - 150; const labelX = nearRightEdge ? px - 8 : px + 8; const labelY = Math.max(14, py - 6); return (<text x={labelX} y={labelY} textAnchor={nearRightEdge ? "end" : "start"} fill={labelColor} fontSize={12} fontWeight={700} opacity={isFocused ? 1 : 0.32} style={{ cursor: "pointer", pointerEvents: "auto", userSelect: "none", paintOrder: "stroke", stroke: "rgba(0,0,0,0.86)", strokeWidth: 3 }} onClick={(e) => { e.stopPropagation(); toggleSeriesHighlight(seriesName); }}>{seriesName}</text>); }; dots.push(<ReferenceDot key={`business-pk-${seriesName}`} x={best.x} y={best.y} r={isFocused ? 5 : 3} isFront ifOverflow="extendDomain" fill={isFocused ? chartSeriesColor(i) : "#1f252b"} stroke="rgba(0,0,0,0.9)" strokeWidth={2} fillOpacity={isFocused ? 1 : 0.22} label={renderPeakLabel} />); } return <>{dots}</>; })()}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      )}
    </div>
  )

) : (
  <div
    ref={chartExportRef}
    className="mg-mindshare-chart-plot"
    style={{
      height: `${mindshareChartHeight}px`,
      minHeight: 340,
      borderRadius: 8,
      overflow: "hidden",
      backgroundColor: "transparent",
      position: "relative",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <div
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "transparent",
      }}
    >
      {showMindshareChartSkeleton ? (
        <MindshareChartSkeleton
          height="100%"
          axisWidth={mindshareYAxisWidth}
          axisData={chartDataWithAverage}
          axisMax={yUpper}
          formatXAxis={formatMindshareXAxisTick}
          message={isEmptyCustomChart ? "Choose a game to start" : "Loading mindSHARE history…"}
        />
      ) : (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={chartDataWithAverage}
          margin={MINDSHARE_CHART_MARGIN}
          onMouseLeave={() => setHoverSeriesKey(null)}
        >
          <CartesianGrid stroke="rgba(255,255,255,0.08)" />
          <XAxis
            dataKey="x"
            stroke="#ddd"
            tick={<MindshareXAxisTick />}
            tickLine={{ stroke: "rgba(255,255,255,0.18)" }}
            axisLine={{ stroke: "rgba(255,255,255,0.16)" }}
            tickFormatter={formatMindshareXAxisTick}
          />
          <YAxis
            stroke="#ddd"
            width={mindshareYAxisWidth}
            tick={{ fontSize: 13, fill: "#cfd8e3" }}
            tickLine={{ stroke: "rgba(255,255,255,0.18)" }}
            axisLine={{ stroke: "rgba(255,255,255,0.16)" }}
            tickFormatter={(v) => pct3(v)}
            domain={[0, yUpper]}
            label={{ content: ({ viewBox }) => (
              <g className="ms-mindshare-axis-title" transform={`translate(${viewBox.x + 18}, ${viewBox.y + viewBox.height / 2}) rotate(-90)`}>
                <text
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="#cfd8e3"
                  fontSize={16}
                  fontWeight={600}
                  style={{ fontFamily: "inherit", letterSpacing: 0, userSelect: "none", textRendering: "geometricPrecision" }}
                >
                  {mindshareAgg === "cumulative" ? "Cumulative mindSHARE" : "mindSHARE"}
                </text>
              </g>
            ) }}
          />
          <Tooltip
            content={<PointTooltip />}
            isAnimationActive={false}
            cursor={{
              stroke: "rgba(255,255,255,0.34)",
              strokeDasharray: "3 3",
            }}
          />
          {[
            { key: "__avg", enabled: analyticsSeries.average, name: "Average", stroke: "rgba(230,230,230,0.92)", dash: "6 6" },
            { key: "__median", enabled: analyticsSeries.median, name: "Median", stroke: "rgba(245,230,200,0.90)", dash: "3 5" },
            { key: "__top5", enabled: analyticsSeries.top5, name: "Top 5%", stroke: "rgba(168,85,247,0.92)", dash: "8 5" },
            { key: "__top10", enabled: analyticsSeries.top10, name: "Top 10%", stroke: "rgba(102,204,255,0.92)", dash: "8 5" },
            { key: "__top25", enabled: analyticsSeries.top25, name: "Top 25%", stroke: "rgba(255,155,112,0.90)", dash: "8 5" },
          ].filter((item) => item.enabled).map((item) => (
            <Line
              key={item.key}
              type="monotone"
              dataKey={item.key}
              stroke={item.stroke}
              strokeWidth={2.2}
              strokeDasharray={item.dash}
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              connectNulls={false}
              name={item.name}
            />
          ))}

          {targetsForChart.map((name, idx) => {
            const isHi = highlighted.size > 0 ? highlighted.has(name) : true;
            const dim = highlighted.size > 0 && !isHi;
            const stroke = getChartColor(name, idx);

            return (viewMode === "launches" || viewMode === "trended") && chartStyle === "area" ? (
              <Area
                key={name}
                type="monotone"
                dataKey={name}
                stroke={stroke}
                strokeWidth={dim ? 1.0 : 2.0}
                strokeOpacity={dim ? 0.30 : 0.95}
                fill={stroke}
                fillOpacity={dim ? 0.035 : 0.11}
                dot={(dotProps) => (
                  <CustomDot
                    {...dotProps}
                    stroke={stroke}
                    seriesName={name}
                    dim={dim}
                    onToggle={() => toggleSeriesHighlight(name)}
                  />
                )}
                activeDot={false}
                isAnimationActive={perGameHistory && !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches}
                animationDuration={350}
                connectNulls={false}
                name={name}
                onClick={() => toggleSeriesHighlight(name)}
              />
            ) : (
              <Line
                key={name}
                type="monotone"
                dataKey={name}
                stroke={stroke}
                strokeWidth={dim ? 1.2 : 2.4}
                strokeOpacity={dim ? 0.35 : 1}
                dot={(dotProps) => (
                  <CustomDot
                    {...dotProps}
                    stroke={stroke}
                    seriesName={name}
                    dim={dim}
                    onToggle={() => toggleSeriesHighlight(name)}
                  />
                )}
                activeDot={false}
                isAnimationActive={perGameHistory && !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches}
                animationDuration={350}
                connectNulls={false}
                name={name}
                onClick={() => toggleSeriesHighlight(name)}
              />
            );
          })}

          {(() => {
            const names = targetsForChart;
            const dots = [];
            const rowsForScan =
              mindshareAgg === "cumulative" ? chartData : mindshareRenderData;

            for (let i = 0; i < names.length; i++) {
              const seriesName = names[i];
              let best = { y: -Infinity, x: null };

              for (const row of rowsForScan) {
                const v = Number(row[seriesName]);
                if (Number.isFinite(v) && v > best.y) {
                  best = { y: v, x: row.x };
                }
              }

              if (best.x != null) {
                const show = highlighted.size === 0 || highlighted.has(seriesName);
                const shouldLabelSeries =
                  viewMode === "launches" ||
                  launchChartPreset === "upcoming" ||
                  launchChartPreset === "recent" ||
                  launchChartPreset === "top100" ||
                  targetsForChart.length <= 30 ||
                  highlighted.has(seriesName) ||
                  hoverSeriesKey === seriesName;
                const labelColor = show ? getChartColor(seriesName, i) : "#777";
                const renderPeakLabel = (props) => {
                  if (!shouldLabelSeries) return null;
                  const viewBox = props?.viewBox || {};
                  const px = Number(props?.x ?? viewBox.x ?? 0);
                  const py = Number(props?.y ?? viewBox.y ?? 0);
                  const rightEdge = Number(viewBox.x || 0) + Number(viewBox.width || 0);
                  const nearRightEdge = rightEdge > 0 && px > rightEdge - 130;
                  const labelX = nearRightEdge ? px - 8 : px + 8;
                  const labelY = Math.max(14, py - 6);

                  return (
                    <text
                      x={labelX}
                      y={labelY}
                      textAnchor={nearRightEdge ? "end" : "start"}
                      fill={labelColor}
                      fontSize={14}
                      fontWeight={700}
                      opacity={show ? 1 : 0.5}
                      style={mindshareGameLabelStyle}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSeriesHighlight(seriesName);
                      }}
                    >
                      {seriesName}
                    </text>
                  );
                };

                dots.push(
                  <ReferenceDot
                    key={`pk-${seriesName}`}
                    x={best.x}
                    y={best.y}
                    r={show ? 5 : 3}
                    isFront
                    ifOverflow="extendDomain"
                    fill={show ? getChartColor(seriesName, i) : "#444"}
                    stroke="rgba(0,0,0,0.85)"
                    strokeWidth={2}
                    fillOpacity={show ? 1 : 0.5}
                    label={renderPeakLabel}
                  />
                );
              }
            }
            return <>{dots}</>;
          })()}
        </ComposedChart>
      </ResponsiveContainer>
      )}
    </div>
  </div>
)}

            {viewMode === "business" && businessChartData.length > 0 && (
              <div style={{ marginTop: 8, marginLeft: mindshareYAxisWidth, marginRight: 80, flex: "0 0 auto" }}>
                <BusinessLegendContent />
              </div>
            )}

            {/* Range slider – Trended view only */}
{(viewMode === "trended" || viewMode === "launches") && (showMindshareChartSkeleton || mindshareRenderData.length > 1) && (
  <div
    style={{
      marginTop: 8,
      marginLeft: mindshareYAxisWidth,
      marginRight: 80,
      flex: "0 0 auto",
    }}
  >
    <style>{`
      .mg-mindshare-range {
        -webkit-appearance: none;
        appearance: none;
      }
      .mg-mindshare-range::-webkit-slider-runnable-track {
        height: 18px;
        background: transparent;
        border: none;
      }
      .mg-mindshare-range::-moz-range-track {
        height: 18px;
        background: transparent;
        border: none;
      }
      .mg-mindshare-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 28px;
        height: 14px;
        border-radius: 7px;
        border: none;
        background: #9aa3ad;
        cursor: grab;
        box-shadow: 0 0 10px rgba(180,190,200,0.28);
        pointer-events: auto;
        margin-top: 2px;
      }
      .mg-mindshare-range:hover::-webkit-slider-thumb {
          box-shadow: 0 0 10px rgba(180,190,200,0.28);
      }
      .mg-mindshare-range:active::-webkit-slider-thumb {
        cursor: grabbing;
      }
      .mg-mindshare-range::-moz-range-thumb {
        width: 28px;
        height: 14px;
        border-radius: 7px;
        border: none;
        background: #9aa3ad;
        cursor: grab;
        box-shadow: 0 0 10px rgba(180,190,200,0.28);
        pointer-events: auto;
      }
    `}</style>

    <div
      style={{
        position: "relative",
        height: 24,
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: 11,
          height: 3,
          background: "rgba(255,255,255,0.09)",
          borderRadius: 999,
          boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.03)",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: `${
            (100 * mindWinStartIdx) /
            Math.max(1, lineChartData.length - 1)
          }%`,
          width: `${
            (100 * (mindWinEndIdx - mindWinStartIdx)) /
            Math.max(1, lineChartData.length - 1)
          }%`,
          top: 11,
          height: 3,
          background: "#9aa3ad",
          borderRadius: 999,
          boxShadow: "0 0 14px rgba(180,190,200,0.22)",
        }}
      />

      <input
        className="mg-mindshare-range"
        type="range"
        disabled={showMindshareChartSkeleton}
        min={0}
        max={Math.max(0, lineChartData.length - 1)}
        value={mindWinStartIdx}
        onChange={(e) => {
          mindWindowUserAdjustedRef.current = true;
          setMindWinStartIdx(
            Math.min(
              parseInt(e.target.value, 10),
              mindWinEndIdx - 1
            )
          );
        }}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: 24,
          background: "transparent",
          pointerEvents: "none",
          margin: 0,
        }}
      />
      <input
        className="mg-mindshare-range"
        type="range"
        disabled={showMindshareChartSkeleton}
        min={0}
        max={Math.max(0, lineChartData.length - 1)}
        value={mindWinEndIdx}
        onChange={(e) => {
          mindWindowUserAdjustedRef.current = true;
          setMindWinEndIdx(
            Math.max(
              parseInt(e.target.value, 10),
              mindWinStartIdx + 1
            )
          );
        }}
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: "100%",
          height: 24,
          background: "transparent",
          pointerEvents: "none",
          margin: 0,
        }}
      />
      <span
        aria-label={viewMode === "launches" || mindshareXAxisMode === "w2l" ? "Weeks to Launch" : "Period"}
        style={{
          position: "absolute",
          zIndex: 3,
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 22,
          boxSizing: "border-box",
          padding: "0 10px",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 999,
          background: "#090b0d",
          color: "#cfd8e3",
          fontSize: 13,
          fontWeight: 700,
          lineHeight: "20px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
        }}
      >
        {viewMode === "launches" || mindshareXAxisMode === "w2l" ? "Weeks to Launch" : "Period"}
      </span>
    </div>
    {LegendContent()}
  </div>
)}

          {/* Shared resize handle, pinned to the true bottom-right of the chart box
              (below the range slider / legend, matching Steam's placement). */}
          {viewMode !== "market" && viewMode !== "calendar" && !showMindshareChartSkeleton && (
            <button
              type="button"
              data-export-hide="true"
              onPointerDown={handleMindshareChartHeightResizeStart}
              title="Drag to resize chart"
              aria-label="Drag to resize chart"
              style={{
                position: "absolute",
                right: 3,
                bottom: 3,
                width: 20,
                height: 20,
                border: "none",
                background: "transparent",
                color: "#9aa3ad",
                cursor: "ns-resize",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 8,
                padding: 0,
                opacity: 0.95,
              }}
            >
              <Grip size={14} aria-hidden="true" />
            </button>
          )}
          </div>
        )}

        {/* Anchor element for grid width calc */}
        <div
          ref={gridHostRef}
          style={{ paddingLeft: gridLeftPad }}
        />

        {/* Cards grid – show under normal Cards + Charts and Launches */}
        {(viewMode === "trended" || viewMode === "launches") && (
          <>
          <MGSectionHeader
            index={showMindshareTopGrowthSection
              ? (showMindshareTrendsHeader ? "03" : "02")
              : "02"}
            accent="paper"
          >
            Ranked games
          </MGSectionHeader>
          {isPublicPreview ? (
            <MindsharePublicPreview
              editorial={editorial}
              games={cardFiltered.slice(0, 120)}
              loading={(legacyData ? metaLoading || loading : !snapshotRows) && !cardFiltered.length}
              onToggleChart={handleToggleChartGame}
              onRemoveGame={handleRemoveGame}
            />
          ) : (
          <MindshareCardGrid
            editorial={editorial}
            games={cardFiltered}
            comparisonGames={perGameHistory ? comparisonGames : targetsForChart}
            onToggleChart={handleToggleChartGame}
            loading={(legacyData ? metaLoading || loading : !snapshotRows) && !cardFiltered.length}
            onRemoveGame={handleRemoveGame}
          />
          )}
          </>
        )}

        {/* Guided overlay removed; presets will move into pre-baked charts. */}
        </DashboardContent>
      </div>
    </div>
  );
}
